import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import NewAssignmentModal from '../components/NewAssignmentModal';
import AssignmentBlock from '../components/AssignmentBlock';
import AssignmentSettingsModal from '../components/AssignmentSettingsModal';
import AssignmentCheckModal from '../components/AssignmentCheckModal';
import ProfileEditModal from '../components/ProfileEditModal';
import ConfirmModal from '../components/ConfirmModal';
import RoutineAssignmentSettingsModal from '../components/RoutineAssignmentSettingsModal';
import RoutineAssignmentBlock from '../components/RoutineAssignmentBlock';
import DateInputModal from '../components/DateInputModal';
import ProjectAssignmentSettingsModal from '../components/ProjectAssignmentSettingsModal';
import ProjectAssignmentBlock from '../components/ProjectAssignmentBlock';
import ItemCardSelectionModal from '../components/ItemCardSelectionModal';
import { Toast } from '../components/Toast';
import CachedProfileImage from '../components/CachedProfileImage';

import { assignmentCategoryService } from '../services/assignmentCategoryService';
import { AssignmentCategory } from '../types/alarm';
import { Assignment, AssignmentStudentStatus, Class, ClassMember, User, RoutineAssignment, RoutineAssignmentRecord, RoutineAssignmentStudentStatus } from '../types/alarm';
import { ProjectAssignment, ProjectAssignmentInfo } from '../types';
import { assignmentService } from '../services/assignmentService';
import { userDataService } from '../services/userDataService';
import { routineAssignmentService } from '../services/routineAssignmentService';
import { projectAssignmentService } from '../services/projectAssignmentService';
import { RoutineAssignmentConsolidatedService } from '../services/routineAssignmentConsolidatedService';
import {
  MemberProfileDisplayMode,
  readMemberProfileDisplayMode,
  resolveMemberProfileSource
} from '../utils/memberProfileDisplay';
import {
  buildGeneralAssignmentsExportHtml,
  buildProjectAssignmentsExportHtml,
  formatAssignmentDueDate,
  openAssignmentPrintWindow,
  sortStudentsForPrint
} from '../utils/assignmentPrintUtils';

interface AssignmentsPageProps {
  user: User;
  onLogout: () => void;
  onUpdateUser: (updatedUser: User) => void;
  onClassManageModalOpen: () => void;
  classes: Class[];
  currentClass: Class | null;
  memberGroups?: string[];
  currentMemberGroup?: string;
  onMemberGroupChange?: (group: string) => void;
  onNavigate: (page: 'home' | 'alarm' | 'admin' | 'assignments' | 'points-table' | 'chat' | 'board' | 'license' | 'terms-of-service' | 'privacy-policy') => void;
  onMembersChange?: () => void; // 멤버 정보 변경 시 호출될 콜백
  onRoutineDetail: (routineId: string) => void; // 루틴 과제 상세 페이지로 이동
  onNavigateToTable: () => void; // 과제 관리 테이블 페이지로 이동
  onNavigateToCard: () => void; // 과제 관리 카드 페이지로 이동
  // 포인트 메뉴 핸들러들
  onSeatAssignmentClick?: () => void;
  onLevelSettingsClick?: () => void;
  onPointManagementClick?: () => void;
  onItemCardSettingsClick?: () => void;
  onAssignmentSettingsClick?: () => void;
  themeMode: 'dark' | 'light';
  onToggleTheme: () => void;
  onLicenseClick?: () => void; // 이용권 구매 모달 열기
  onUpgradeModalOpen?: () => void; // 플랜 업그레이드 모달 열기
}

interface StudentOverviewItem {
  studentId: string;
  studentName: string;
  studentNumber: number;
  profileImage?: string;
  pendingCount: number;
  confirmedCount: number;
  seatRow: number;
  seatCol: number;
}

// 탭 스타일 컴포넌트들
const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid var(--color-border);
  margin-bottom: 24px;
  background: var(--color-surface);
  border-radius: 8px 8px 0 0;
  overflow: hidden;
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 16px 24px;
  border: none;
  background: ${props => props.$active ? 'var(--color-accent-strong)' : 'var(--color-panel)'};
  color: ${props => props.$active ? 'white' : 'var(--color-text-secondary)'};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border-right: 1px solid var(--color-border);
  
  &:last-child {
    border-right: none;
  }
  
  &:hover {
    background: ${props => props.$active ? 'var(--color-accent-strong)' : 'var(--color-border)'};
    transform: ${props => props.$active ? 'none' : 'translateY(-1px)'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(71, 112, 246, 0.3);
  }
  
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(71, 112, 246, 0.3);
  }
  
  @media (max-width: 600px) {
    font-size: 0.9rem;
    padding: 14px 20px;
  }
  
  @media (max-width: 500px) {
    font-size: 0.85rem;
    padding: 12px 16px;
  }
  
  @media (max-width: 420px) {
    font-size: 0.8rem;
    padding: 10px 12px;
  }
  
  .desktop-text {
    @media (max-width: 420px) {
      display: none;
    }
  }
  
  .mobile-text {
    display: none;
    
    @media (max-width: 420px) {
      display: inline;
    }
  }
`;

// 루틴 과제 필터링 관련 스타일
const RoutineFiltersContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 0;
  margin-bottom: 0;
  justify-content: space-between;
  
  @media (max-width: 416px) {
    gap: 6px;
    margin-top: 0;
    margin-bottom: 0;
  }
  
  @media (max-width: 380px) {
    gap: 4px;
    margin-top: 0;
    margin-bottom: 0;
  }
`;

/** 멤버별 과제 현황 모달 — AssignmentsPage보다 먼저 선언 (HMR/번들에서 export 아래 전용 스타일 누락 방지) */
const StudentOverviewModalHeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-width: 0;

  @media (min-width: 520px) {
    flex: 1;
    position: relative;
    z-index: 1;
  }
`;

const StudentOverviewModalToggleRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  @media (min-width: 520px) {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    width: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;

    & > * {
      pointer-events: auto;
    }
  }
`;

const AssignmentsPage: React.FC<AssignmentsPageProps> = ({ user, onLogout, onUpdateUser, onClassManageModalOpen, classes, currentClass, memberGroups, currentMemberGroup, onMemberGroupChange, onNavigate, onMembersChange, onRoutineDetail, onNavigateToTable, onNavigateToCard, onSeatAssignmentClick, onLevelSettingsClick, onPointManagementClick, onItemCardSettingsClick, onAssignmentSettingsClick, themeMode, onToggleTheme, onLicenseClick, onUpgradeModalOpen }) => {
  // 로컬 스토리지에서 필터 상태 복원
  const getStoredFilterState = () => {
    try {
      const stored = localStorage.getItem('assignmentsPageFilters');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('필터 상태 복원 실패:', error);
    }
    return null;
  };

  const storedState = getStoredFilterState();

  // 탭 상태 추가 (로컬 스토리지에서 복원)
  const [activeTab, setActiveTab] = useState<'assignments' | 'routine' | 'project'>(
    (storedState?.activeTab as 'assignments' | 'routine' | 'project') || 'assignments'
  );
  
  // 기존 상태들 (로컬 스토리지에서 복원)
  const [members, setMembers] = useState<ClassMember[]>([]);
  const [memberProfileDisplayMode, setMemberProfileDisplayMode] = useState<MemberProfileDisplayMode>('svg');
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [filterCategoryId, setFilterCategoryId] = useState<string>(storedState?.filterCategoryId || '');
  const [filterType, setFilterType] = useState<'all' | 'incomplete'>(storedState?.filterType || 'all'); // 전체 과제 / 미완료 과제 필터
  const [generalSortBy, setGeneralSortBy] = useState<'createdAt' | 'dueDate' | 'title'>(storedState?.generalSortBy || 'createdAt'); // 일반 과제 정렬
  const [projectFilterType, setProjectFilterType] = useState<'all' | 'incomplete'>(storedState?.projectFilterType || 'all'); // 프로젝트 과제용 필터
  const [routineFilterType, setRoutineFilterType] = useState<'all' | 'incomplete'>(storedState?.routineFilterType || 'all'); // 루틴 과제용 필터 (전체/미완료)
  const [routineFilterStatus] = useState<'all' | 'active' | 'completed' | 'expired'>(storedState?.routineFilterStatus || 'all');
  const [routineSortBy, setRoutineSortBy] = useState<'createdAt' | 'dueDate' | 'title'>(storedState?.routineSortBy || 'createdAt');
  const [routineSortOrder] = useState<'asc' | 'desc'>(storedState?.routineSortOrder || 'desc');
  
  // 필터 상태를 로컬 스토리지에 저장
  useEffect(() => {
    try {
      const filterState = {
        activeTab,
        filterCategoryId,
        filterType,
        generalSortBy,
        projectFilterType,
        routineFilterType,
        routineFilterStatus,
        routineSortBy,
        routineSortOrder
      };
      localStorage.setItem('assignmentsPageFilters', JSON.stringify(filterState));
    } catch (error) {
      console.error('필터 상태 저장 실패:', error);
    }
  }, [activeTab, filterCategoryId, filterType, generalSortBy, projectFilterType, routineFilterType, routineFilterStatus, routineSortBy, routineSortOrder]);

  useEffect(() => {
    if (!currentClass) return;
    setMemberProfileDisplayMode(readMemberProfileDisplayMode(user.id, currentClass.id));
  }, [user.id, currentClass]);

  useEffect(() => {
    if (!currentClass) return;
    const handler = () => {
      setMemberProfileDisplayMode(readMemberProfileDisplayMode(user.id, currentClass.id));
    };
    window.addEventListener('memberProfileDisplayModeChanged', handler as EventListener);
    return () => {
      window.removeEventListener('memberProfileDisplayModeChanged', handler as EventListener);
    };
  }, [user.id, currentClass]);

  // 생성일 정렬용: 날짜+시·분·초 전체 타임스탬프(ms). 같은 날이어도 최근 생성이 위로
  const createdAtToMs = useCallback((v: any): number => {
    if (v == null) return 0;
    if (v instanceof Date) return v.getTime();
    if (typeof (v as any)?.toDate === 'function') return (v as any).toDate().getTime();
    const d = new Date(typeof v === 'string' ? v : v);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  }, []);

  // 필터/정렬 변경 시 페이지 리셋
  useEffect(() => {
    setGeneralAssignmentCurrentPage(1);
  }, [filterType, filterCategoryId, generalSortBy]);

  // 탭 전환 시 선택 모드 해제 (다른 탭 갔다 오면 체크 상태 초기화)
  useEffect(() => {
    if (activeTab !== 'assignments') {
      setIsGeneralSelectionMode(false);
      setSelectedGeneralAssignmentIds(new Set());
    }
    if (activeTab !== 'project') {
      setIsProjectSelectionMode(false);
      setSelectedProjectAssignmentIds(new Set());
    }
  }, [activeTab]);
  
  const [isRoutineFilterDropdownOpen, setIsRoutineFilterDropdownOpen] = useState(false);
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'new' | 'edit' | 'copy'>('new');
  
  const [initialAssignment, setInitialAssignment] = useState<Partial<Assignment> | undefined>(undefined);
  const [initialRoutineAssignment, setInitialRoutineAssignment] = useState<Partial<RoutineAssignment> | undefined>(undefined);
  const [initialSelectedIds, setInitialSelectedIds] = useState<string[] | undefined>(undefined);
  const [isCatModalOpen, setIsCatModalOpen] = useState(false);
  const [categories, setCategories] = useState<AssignmentCategory[]>([]);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [defaultPoints, setDefaultPoints] = useState<number>(10); // 기본 단위 포인트
  const [statusDefaultPoints, setStatusDefaultPoints] = useState({
    pending: 0,      // 미확인
    confirmed: 0,    // 확인
    delayed: 0,      // 지연확인
    exempted: 0      // 예외
  });
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [isProjectFilterDropdownOpen, setIsProjectFilterDropdownOpen] = useState(false);
  
  // 과제 체크 모달 상태 추가
  const [isCheckModalOpen, setIsCheckModalOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [studentStatuses, setStudentStatuses] = useState<AssignmentStudentStatus[]>([]);
  
  // 일반 과제 선택 모드 (다운로드/프린트용)
  const [isGeneralSelectionMode, setIsGeneralSelectionMode] = useState(false);
  const [selectedGeneralAssignmentIds, setSelectedGeneralAssignmentIds] = useState<Set<string>>(new Set());
  const [isExportingGeneralAssignments, setIsExportingGeneralAssignments] = useState(false);

  // 프로젝트 과제 선택 모드 (프린트용)
  const [isProjectSelectionMode, setIsProjectSelectionMode] = useState(false);
  const [selectedProjectAssignmentIds, setSelectedProjectAssignmentIds] = useState<Set<string>>(new Set());
  const [isExportingProjectAssignments, setIsExportingProjectAssignments] = useState(false);
  const [isStudentOverviewOpen, setIsStudentOverviewOpen] = useState(false);
  const [studentOverviewMode, setStudentOverviewMode] = useState<'number' | 'seat'>(() => {
    const saved = localStorage.getItem('assignmentsStudentOverviewMode');
    return saved === 'seat' ? 'seat' : 'number';
  });
  const [isStudentOverviewSeatRotated, setIsStudentOverviewSeatRotated] = useState(false);
  const [studentOverviewLoading, setStudentOverviewLoading] = useState(false);
  const [studentOverviewData, setStudentOverviewData] = useState<StudentOverviewItem[]>([]);
  /** 멤버별 과제 현황 번호 모드 그리드 정렬 — PointsTablePage와 동일 */
  const [studentOverviewGridAlignment, setStudentOverviewGridAlignment] = useState<'left' | 'center'>(() => {
    const saved = localStorage.getItem('assignmentsStudentOverviewGridAlignment');
    return saved === 'left' || saved === 'center' ? saved : 'center';
  });

  useEffect(() => {
    localStorage.setItem('assignmentsStudentOverviewMode', studentOverviewMode);
  }, [studentOverviewMode]);

  useEffect(() => {
    localStorage.setItem('assignmentsStudentOverviewGridAlignment', studentOverviewGridAlignment);
  }, [studentOverviewGridAlignment]);

  // pendingCount 업데이트 추적을 위한 ref
  const pendingCountUpdateRef = useRef<Record<string, number>>({});

  // 과제 삭제 관련 상태
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<Assignment | null>(null);
  
  // 루틴 과제 삭제 관련 상태
  const [isRoutineDeleteConfirmModalOpen, setIsRoutineDeleteConfirmModalOpen] = useState(false);
  const [routineToDelete, setRoutineToDelete] = useState<RoutineAssignment | null>(null);
  
  // 프로젝트 과제 삭제 관련 상태
  const [isProjectDeleteConfirmModalOpen, setIsProjectDeleteConfirmModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<ProjectAssignment | null>(null);
  
  // 루틴 과제 관련 상태 추가
  const [routineAssignments, setRoutineAssignments] = useState<RoutineAssignment[]>([]);
  const [isRoutineSettingsModalOpen, setIsRoutineSettingsModalOpen] = useState(false);
  const [isRoutineCheckModalOpen, setIsRoutineCheckModalOpen] = useState(false);
  const [selectedRoutineAssignment, setSelectedRoutineAssignment] = useState<RoutineAssignmentRecord | null>(null);
  
  // 루틴 과제 상세 보기 상태 추가
  const [selectedRoutineForDetail, setSelectedRoutineForDetail] = useState<RoutineAssignment | null>(null);
  const [routineDateSummaries, setRoutineDateSummaries] = useState<any[]>([]);
  const [isRoutineMonitoringModalOpen, setIsRoutineMonitoringModalOpen] = useState(false);
  const [routineMonitoringLoading, setRoutineMonitoringLoading] = useState(false);
  const [routineMonitoringData, setRoutineMonitoringData] = useState<Array<{
    studentId: string;
    studentName: string;
    studentNumber: number;
    completionRate: number;
    confirmedCount: number;
    totalDates: number;
    todayCount: number;
  }>>([]);
  const [routineMonitoringSortOrder, setRoutineMonitoringSortOrder] = useState<'number' | 'rate'>('number');
  const [isRoutineRewardProcessing, setIsRoutineRewardProcessing] = useState(false);
  const routineMonitoringCacheRef = useRef<{ routineId: string; data: Array<{ studentId: string; studentName: string; studentNumber: number; completionRate: number; confirmedCount: number; totalDates: number; todayCount: number }>; consolidatedDocs?: import('../types/index').RoutineAssignmentConsolidated[]; timestamp: number } | null>(null);
  const ROUTINE_MONITORING_CACHE_TTL_MS = 60000; // 1분
  const [isProjectMonitoringModalOpen, setIsProjectMonitoringModalOpen] = useState(false);
  const [projectMonitoringLoading, setProjectMonitoringLoading] = useState(false);
  const [projectMonitoringData, setProjectMonitoringData] = useState<Array<{
    studentId: string;
    studentName: string;
    studentNumber: number;
    completionRate: number;
    completedCount: number;
    totalSubTasks: number;
    segmentStates: boolean[];
  }>>([]);
  const [projectMonitoringSortOrder, setProjectMonitoringSortOrder] = useState<'number' | 'rate'>('number');
  
  // 루틴 과제 날짜 페이지네이션 상태
  const [routineDateCurrentPage, setRoutineDateCurrentPage] = useState(1);
  const routineDateItemsPerPage = 10;

  // 일반 과제 페이지네이션 상태
  const [generalAssignmentCurrentPage, setGeneralAssignmentCurrentPage] = useState(1);
  const generalAssignmentItemsPerPage = 10;

  // 프로젝트 과제 페이지네이션 상태
  const [projectAssignmentCurrentPage, setProjectAssignmentCurrentPage] = useState(1);
  const projectAssignmentItemsPerPage = 10;

  // 프로젝트 과제 관련 상태 추가
  const [projectAssignments, setProjectAssignments] = useState<any[]>([]);
  const [isProjectSettingsModalOpen, setIsProjectSettingsModalOpen] = useState(false);
  const [isProjectCheckModalOpen, setIsProjectCheckModalOpen] = useState(false);
  const [selectedProjectAssignment, setSelectedProjectAssignment] = useState<any | null>(null);
  
  // 프로젝트 과제 상세 보기 상태 추가 (보상 지급 시 캐시로 재사용)
  const [selectedProjectForDetail, setSelectedProjectForDetail] = useState<any | null>(null);
  const [projectAssignmentInfoForReward, setProjectAssignmentInfoForReward] = useState<ProjectAssignmentInfo | null>(null);
  const [projectSubTaskSummaries, setProjectSubTaskSummaries] = useState<any[]>([]);
  const [editingProject, setEditingProject] = useState<ProjectAssignment | null>(null);
  
  // 프로젝트 하위 과제 네비게이션 상태
  const [selectedSubTaskId, setSelectedSubTaskId] = useState<string | null>(null);
  const [currentSubTaskIndex, setCurrentSubTaskIndex] = useState<number>(0);
  
  // 프로젝트 과제 멤버 상태
  const [projectStudentStatuses, setProjectStudentStatuses] = useState<AssignmentStudentStatus[]>([]);
  
  // 🎯 날짜 필터 상태 추가
  const [dateFilterType, setDateFilterType] = useState<'desc' | 'asc' | 'hasData' | 'customOnly'>('desc');
  const [isDateFilterDropdownOpen, setIsDateFilterDropdownOpen] = useState(false);
  
  // 🎯 DateInputModal 상태 추가
  const [isDateInputModalOpen, setIsDateInputModalOpen] = useState(false);
  const [customDateForRoutine, setCustomDateForRoutine] = useState<Date | null>(null);
  
  // 🎯 날짜 삭제 상태 추가
  const [dateToDelete, setDateToDelete] = useState<{routineId: string, dateId: string, date: Date} | null>(null);
  
  // 토스트 메시지 상태
  const [toastMessage, setToastMessage] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [toastRequiresConfirm, setToastRequiresConfirm] = useState(false);
  const [isDateDeleteConfirmModalOpen, setIsDateDeleteConfirmModalOpen] = useState(false);
  
  // 전역 햄버거 메뉴 상태 관리
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  
  // 루틴 과제 메뉴 상태 관리
  const [openRoutineMenuId, setOpenRoutineMenuId] = useState<string | null>(null);
  const [routineMenuPosition, setRoutineMenuPosition] = useState({ top: 0, left: 0 });
  
  // 프로젝트 과제 메뉴 상태 관리
  const [openProjectMenuId, setOpenProjectMenuId] = useState<string | null>(null);
  const [projectMenuPosition, setProjectMenuPosition] = useState({ top: 0, left: 0 });
  
  // 프로젝트 과제 필터/정렬 상태 관리
  const [projectSortBy, setProjectSortBy] = useState<'createdAt' | 'dueDate' | 'title'>('createdAt');

  // 프로젝트 필터/정렬 변경 시 페이지 리셋
  useEffect(() => {
    setProjectAssignmentCurrentPage(1);
  }, [projectFilterType, projectSortBy]);
  
  // 프로젝트 하위과제 정렬 상태 관리
  const [projectSubTaskSortOrder, setProjectSubTaskSortOrder] = useState<'desc' | 'asc'>('desc');

  // 프로젝트 하위과제 정렬 함수
  const getSortedProjectSubTasks = useCallback((subTasks: any[]) => {
    return [...subTasks].sort((a, b) => {
      // 하위과제는 order 기준으로 정렬
      const orderA = a.subTask?.order || 0;
      const orderB = b.subTask?.order || 0;
      
      if (projectSubTaskSortOrder === 'desc') {
        return orderB - orderA;
      } else {
        return orderA - orderB;
      }
    });
  }, [projectSubTaskSortOrder]);

  // 일반 과제 보상 아이템 카드 선택 모달 상태
  const [isAssignmentRewardCardModalOpen, setIsAssignmentRewardCardModalOpen] = useState(false);
  const [assignmentRewardCardIds, setAssignmentRewardCardIds] = useState<string[]>([]);
  const [assignmentRewardCardCallback, setAssignmentRewardCardCallback] = useState<((ids: string[]) => void) | null>(null);

  // 미완료 과제 모달 상태
  const [isIncompleteByStudentModalOpen, setIsIncompleteByStudentModalOpen] = useState(false);
  const [isIncompleteByAssignmentModalOpen, setIsIncompleteByAssignmentModalOpen] = useState(false);
  const [isStudentIncompleteModalOpen, setIsStudentIncompleteModalOpen] = useState(false);
  const [selectedOverviewStudent, setSelectedOverviewStudent] = useState<StudentOverviewItem | null>(null);
  const [selectedStudentIncompleteAssignments, setSelectedStudentIncompleteAssignments] = useState<IncompleteAssignmentItem[]>([]);
  const incompleteModalContentRef = useRef<HTMLDivElement>(null);

  // 과제 삭제 실행 함수
  const executeDeleteAssignment = async () => {
    if (!assignmentToDelete || !user?.id || !currentClass?.id) return;
    
    try {
      await assignmentService.deleteAssignment(
        user.id,
        currentClass.id,
        assignmentToDelete.id,
        currentMemberGroup
      );
      // 삭제 후 전체 데이터를 다시 로드하여 pendingCount 포함된 과제 목록 가져오기
      await loadData();
      setIsDeleteConfirmModalOpen(false);
      setAssignmentToDelete(null);
    } catch (error) {
      console.error('과제 삭제 실패:', error);
      alert('과제 삭제에 실패했습니다.');
    }
  };

  // 루틴 과제 생성/수정 처리
  const handleRoutineAssignmentSave = async (routineAssignment: any) => {
    try {
      // 데이터 새로고침
      await loadData();
      // 상세 보기 중인 루틴이면 최신 설정(도달률 등) 반영
      if (selectedRoutineForDetail && routineAssignment.id && selectedRoutineForDetail.id === routineAssignment.id) {
        setSelectedRoutineForDetail(prev => prev ? { ...prev, ...routineAssignment } : routineAssignment);
      }
      invalidateRoutineMonitoringCache(routineAssignment.id);
      // 모달 닫기
      setIsRoutineSettingsModalOpen(false);
      // 초기 데이터 초기화
      setInitialRoutineAssignment(undefined);
      setInitialSelectedIds(undefined);
    } catch (error) {
      console.error('루틴 과제 데이터 새로고침 실패:', error);
    }
  };

  // 새 루틴 과제 생성
  const handleCreateRoutineAssignment = () => {
    console.log('➕ 새 루틴 과제 생성 시작:', {
      membersCount: members.length,
      defaultSelectedIds: members.map(m => m.id)
    });
    
    setModalMode('new');
    setInitialRoutineAssignment(undefined);
    setInitialSelectedIds(undefined);
    
    // 즉시 모달 열기 (일반 과제와 동일한 방식)
    setIsRoutineSettingsModalOpen(true);
  };

  // 루틴 과제 편집
  const handleRoutineEdit = (routine: RoutineAssignment) => {
    console.log('🔧 루틴 과제 편집 시작:', {
      routineId: routine.id,
      title: routine.title,
      assignedStudentIds: routine.assignedStudentIds,
      membersCount: members.length
    });
    
    const selectedIds = routine.assignedStudentIds || members.map(m => m.id);
    console.log('👥 설정할 멤버 ID들:', selectedIds);
    
    // 상태를 먼저 설정
    setInitialRoutineAssignment({
      id: routine.id,
      title: routine.title,
      startDate: new Date(routine.startDate), // 시작일 추가
      routineDays: [...routine.routineDays], // 배열 복사
      dueDate: new Date(routine.dueDate), // 날짜 객체 복사
      basePoints: routine.basePoints,
      assignedStudentIds: [...routine.assignedStudentIds], // 배열 복사
      categoryId: routine.categoryId,
      notes: routine.notes, // 노트 추가
      rewardEnabled: routine.rewardEnabled ?? false,
      rewardMessage: routine.rewardMessage ?? '',
      rewardCoins: routine.rewardCoins ?? 0,
      rewardItemCardIds: routine.rewardItemCardIds ?? [],
      rewardCompletionRate: routine.rewardCompletionRate ?? 100,
      includeInIncompleteCheck: routine.includeInIncompleteCheck ?? false,
      // customDates 제거
      isActive: routine.isActive, // 활성 상태도 복사
    });
    setModalMode('edit');
    
    // initialSelectedIds를 설정한 후 모달 열기
    setInitialSelectedIds(selectedIds);
    console.log('✅ initialSelectedIds 설정됨:', selectedIds);
    
    // 다음 렌더링 사이클에서 모달 열기
    setTimeout(() => {
      setIsRoutineSettingsModalOpen(true);
    }, 0);
  };

  // 루틴 과제 삭제
  const handleRoutineDelete = async (routine: RoutineAssignment) => {
    setRoutineToDelete(routine);
    setIsRoutineDeleteConfirmModalOpen(true);
  };

  // 루틴 과제 삭제 실행 함수
  const executeDeleteRoutineAssignment = async () => {
    if (!routineToDelete || !user?.id || !currentClass?.id) return;
    
    try {
      await routineAssignmentService.deleteRoutineAssignment(user.id, currentClass.id, routineToDelete.id, currentMemberGroup);
      await loadData();
      setIsRoutineDeleteConfirmModalOpen(false);
      setRoutineToDelete(null);
    } catch (error) {
      console.error('루틴 과제 삭제 실패:', error);
      alert('루틴 과제 삭제에 실패했습니다.');
    }
  };

  // 프로젝트 과제 삭제 실행 함수
  const executeDeleteProjectAssignment = async () => {
    if (!projectToDelete || !user?.id || !currentClass?.id) return;
    
    try {
      await projectAssignmentService.deleteProjectAssignment(user.id, currentClass.id, projectToDelete.id, currentMemberGroup);
      await loadData();
      setIsProjectDeleteConfirmModalOpen(false);
      setProjectToDelete(null);
    } catch (error) {
      console.error('프로젝트 과제 삭제 실패:', error);
      alert('프로젝트 과제 삭제에 실패했습니다.');
    }
  };

  // 루틴 과제 날짜 배열 생성 (시작날짜~마감일 중 루틴 요일만)
  const generateRoutineDates = (routine: RoutineAssignment) => {
    const dates: Array<{
      dateId: string;
      date: Date;
      dayOfWeek: string;
      isRoutineDay: boolean;
      isCustomDate: boolean;
    }> = [];
    
    if (!routine.startDate || !routine.routineDays || !routine.dueDate) return dates;
    
    const startDate = new Date(routine.startDate);
    const endDate = new Date(routine.dueDate); // 마감일로 설정
    endDate.setHours(23, 59, 59, 999); // 🎯 마감일의 마지막 시간으로 설정
    const currentDate = new Date(startDate);
    
    
    // 🎯 startDate와 endDate 비교 로그 추가
    console.log('🔍 루틴 과제 날짜 범위 분석:', {
      startDate: startDate.toString(),
      startDateLocal: startDate.toLocaleDateString('ko-KR'),
      endDate: endDate.toString(),
      endDateLocal: endDate.toLocaleDateString('ko-KR'),
      startDateTime: startDate.getTime(),
      endDateTime: endDate.getTime(),
      startDateLessThanEndDate: startDate <= endDate,
      differenceMs: endDate.getTime() - startDate.getTime(),
      differenceDays: Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    });
    
    while (currentDate <= endDate) {
      const dayOfWeek = currentDate.getDay();
      const isRoutineDay = routine.routineDays.includes(dayOfWeek);
      
      // 🎯 9월 1일 특별 추적
      const currentDateString = currentDate.toLocaleDateString('ko-KR');
      if (currentDateString.includes('2025. 9. 1.') || currentDateString.includes('2025-09-01')) {
        console.log(`🎯 9월 1일 특별 추적:`, {
          currentDate: currentDateString,
          dayOfWeek,
          dayOfWeekName: ['일', '월', '화', '수', '목', '금', '토'][dayOfWeek],
          isRoutineDay,
          routineDays: routine.routineDays,
          includesResult: routine.routineDays.includes(dayOfWeek)
        });
      }
      
      if (isRoutineDay) {
        // 🎯 로컬 시간대 기준으로 dateId 생성 (UTC 변환 방지)
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const dateId = `${year}-${month}-${day}`;
        
        dates.push({
          dateId: dateId, // 로컬 시간대 기준 YYYY-MM-DD 형식
          date: new Date(currentDate),
          dayOfWeek: ['일', '월', '화', '수', '목', '금', '토'][dayOfWeek],
          isRoutineDay: true,
          isCustomDate: false
        });
        console.log(`✅ AssignmentsPage - 루틴 날짜 추가: ${dateId} (dayOfWeek: ${dayOfWeek}, 요일: ${['일', '월', '화', '수', '목', '금', '토'][dayOfWeek]})`);
      } else {
        // 🎯 로컬 시간대 기준으로 dateId 생성 (UTC 변환 방지)
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const dateId = `${year}-${month}-${day}`;
        
        console.log(`❌ AssignmentsPage - 루틴 요일에 포함되지 않음: ${dateId} (dayOfWeek: ${dayOfWeek}, 요일: ${['일', '월', '화', '수', '목', '금', '토'][dayOfWeek]})`);
      }
      
      // 다음 날짜로 이동
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    console.log('🔍 AssignmentsPage - generateRoutineDates 완료:', {
      totalDates: dates.length,
      dates: dates.map(d => d.dateId)
    });
    
    // 내림차순으로 정렬 (최신 날짜가 위에 오도록)
    return dates.sort((a, b) => b.date.getTime() - a.date.getTime());
  };

  // 루틴 과제 상세 보기 뒤로가기
  const handleRoutineDetailBack = () => {
    setSelectedRoutineForDetail(null);
    setRoutineDateSummaries([]);
    setRoutineDateCurrentPage(1); // 뒤로가기 시 첫 페이지로 리셋
  };

  // 프로젝트 과제 상세 보기에서 뒤로가기
  const handleProjectDetailBack = () => {
    setSelectedProjectForDetail(null);
    setProjectAssignmentInfoForReward(null);
    setProjectSubTaskSummaries([]);
  };

  // 날짜별 블럭 클릭 시 과제 체크 모달 열기
  const handleDateBlockClick = (dateId: string, date: Date) => {
    if (selectedRoutineForDetail) {
      // 선택된 날짜로 과제 체크 모달 열기
      setSelectedRoutineAssignment({
        id: dateId,
        routineAssignmentId: selectedRoutineForDetail.id,
        date: date,
        studentId: '', // 임시값
        status: 'pending',
        earnedPoints: 0,
        updatedAt: new Date(),
        isCustomDate: false
      });
      setIsRoutineCheckModalOpen(true);
    }
  };

  // 루틴 과제 체크 - 상세 보기 토글
  const handleRoutineCheck = async (routine: RoutineAssignment) => {
    try {
      // 이미 선택된 루틴과 같은 경우 토글 (닫기)
      if (selectedRoutineForDetail && selectedRoutineForDetail.id === routine.id) {
        setSelectedRoutineForDetail(null);
        setRoutineDateSummaries([]);
        return;
      }
      
      // 기존 선택 해제
      setSelectedRoutineForDetail(null);
      setRoutineDateSummaries([]);
      
      // 새로운 루틴 과제 선택
      setSelectedRoutineForDetail(routine);
      
      // 날짜별 상태 요약 로드 시도
      if (user?.id && currentClass?.id) {
        try {
          console.log('🔍 서버 데이터 로드 시작:', { userId: user.id, classId: currentClass.id, routineId: routine.id });
          
          const consolidatedService = new RoutineAssignmentConsolidatedService(user.id, currentClass.id, currentMemberGroup);
          const result = await consolidatedService.loadRoutineAssignmentWithStatuses(routine.id);
          
          // RoutineAssignmentDateStatus 형식으로 변환
          const allDateStatuses = {
            routine: result.routine,
            dateStatuses: result.dateStatuses.map(dateAssignment => {
              const students: Record<string, RoutineAssignmentStudentStatus> = {};
              let totalStudents = 0;
              let completedStudents = 0;
              let pendingStudents = 0;
              
              const studentStatuses = dateAssignment.studentStatuses || [];
              studentStatuses.forEach((studentStatus: any) => {
                students[studentStatus.studentId] = {
                  id: studentStatus.studentId,
                  studentId: studentStatus.studentId,
                  assignmentId: routine.id,
                  status: studentStatus.status,
                  earnedPoints: studentStatus.points,
                  memo: studentStatus.memo,
                  updatedAt: studentStatus.updatedAt
                };
                
                totalStudents++;
                if (studentStatus.status === 'confirmed') {
                  completedStudents++;
                } else if (studentStatus.status === 'pending') {
                  pendingStudents++;
                }
              });
              
              return {
                dateId: dateAssignment.date,
                routineAssignmentId: routine.id,
                date: new Date(dateAssignment.date),
                isCustomDate: dateAssignment.isCustomDate,
                students,
                totalStudents,
                completedStudents,
                pendingStudents,
                updatedAt: new Date()
              };
            })
          };
          
          console.log('🔍 서버 데이터 로드 결과:', allDateStatuses);
          
          // 날짜별 요약 정보 생성
          const summaries = processRoutineDateStatuses(allDateStatuses.dateStatuses, routine);
          setRoutineDateSummaries(summaries);
          setRoutineDateCurrentPage(1); // 새 루틴 선택 시 첫 페이지로 리셋
        } catch (error) {
          console.log('❌ 서버 데이터 로드 실패, 날짜 배열로 블럭 생성:', error);
          // 서버 데이터 로드 실패 시 날짜 배열로 블럭 생성
          const generatedDates = generateRoutineDates(routine);
          const summaries = generatedDates.map(dateInfo => ({
            dateId: dateInfo.dateId,
            date: dateInfo.date,
            dayOfWeek: dateInfo.dayOfWeek,
            isRoutineDay: dateInfo.isRoutineDay,
            status: {
              pendingCount: 0,
              hasCompletedStudents: false,
              totalStudents: 0
            }
          }));
          setRoutineDateSummaries(summaries);
        }
      } else {
        // 사용자 정보가 없을 때도 날짜 배열로 블럭 생성
        const generatedDates = generateRoutineDates(routine);
        const summaries = generatedDates.map(dateInfo => ({
          dateId: dateInfo.dateId,
          date: dateInfo.date,
          dayOfWeek: dateInfo.dayOfWeek,
          isRoutineDay: dateInfo.isRoutineDay,
          status: {
            pendingCount: 0,
            hasCompletedStudents: false,
            totalStudents: 0
          }
        }));
        setRoutineDateSummaries(summaries);
      }
    } catch (error) {
      console.error('루틴 과제 상세 보기 전환 실패:', error);
      // 에러 발생 시에도 기본 날짜 블럭 생성
      const generatedDates = generateRoutineDates(routine);
      const summaries = generatedDates.map(dateInfo => ({
        dateId: dateInfo.dateId,
        date: dateInfo.date,
        dayOfWeek: dateInfo.dayOfWeek,
        isRoutineDay: dateInfo.isRoutineDay,
        status: {
          pendingCount: 0,
          hasCompletedStudents: false,
          totalStudents: 0
        }
      }));
      setRoutineDateSummaries(summaries);
    }
  };

  const invalidateRoutineMonitoringCache = useCallback((routineId?: string) => {
    if (!routineId || routineMonitoringCacheRef.current?.routineId === routineId) {
      routineMonitoringCacheRef.current = null;
    }
  }, []);

  const loadRoutineMonitoringData = async (): Promise<{
    data: Array<{ studentId: string; studentName: string; studentNumber: number; completionRate: number; confirmedCount: number; totalDates: number; todayCount: number }>;
    consolidatedDocs?: import('../types/index').RoutineAssignmentConsolidated[];
  }> => {
    if (!user?.id || !currentClass?.id || !selectedRoutineForDetail) return { data: [] };
    const routineId = selectedRoutineForDetail.id;
    const now = Date.now();
    const cached = routineMonitoringCacheRef.current;
    if (cached && cached.routineId === routineId && (now - cached.timestamp) < ROUTINE_MONITORING_CACHE_TTL_MS) {
      return { data: cached.data, consolidatedDocs: cached.consolidatedDocs };
    }
    const consolidatedService = new RoutineAssignmentConsolidatedService(
      user.id,
      currentClass.id,
      currentMemberGroup
    );
    const result = await consolidatedService.loadRoutineAssignmentWithStatuses(routineId);
    const dateAssignments = result.dateStatuses || [];
    const routineDates = generateRoutineDates(selectedRoutineForDetail);
    const totalDates = routineDates.length;
    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const todayCount = routineDates.filter((d) => {
      const dateOnly = new Date(d.date.getFullYear(), d.date.getMonth(), d.date.getDate());
      return dateOnly <= todayOnly;
    }).length;
    const confirmedCounts: Record<string, number> = {};

    dateAssignments.forEach((assignment: any) => {
      const statuses = assignment.studentStatuses || [];
      statuses.forEach((status: any) => {
        if (status.status === 'confirmed') {
          confirmedCounts[status.studentId] = (confirmedCounts[status.studentId] || 0) + 1;
        }
      });
    });

    const memberMap = new Map(members.map(m => [m.id, m]));
    const assignedIds = selectedRoutineForDetail.assignedStudentIds || [];
    const data = assignedIds.map(studentId => {
      const member = memberMap.get(studentId);
      const confirmedCount = confirmedCounts[studentId] || 0;
      const completionRate = totalDates > 0 ? Number(((confirmedCount / totalDates) * 100).toFixed(2)) : 0;
      return {
        studentId,
        studentName: member?.name || '알 수 없음',
        studentNumber: Number(member?.number || 0),
        completionRate,
        confirmedCount,
        totalDates,
        todayCount
      };
    });

    data.sort((a, b) => a.studentNumber - b.studentNumber);

    routineMonitoringCacheRef.current = { routineId, data, consolidatedDocs: result.consolidatedDocs, timestamp: Date.now() };
    return { data, consolidatedDocs: result.consolidatedDocs };
  };

  const handleOpenRoutineMonitoring = async () => {
    if (!user?.id || !currentClass?.id || !selectedRoutineForDetail) return;
    setRoutineMonitoringLoading(true);
    try {
      const { data } = await loadRoutineMonitoringData();
      setRoutineMonitoringData(data);
      setRoutineMonitoringLoading(false);
      setIsRoutineMonitoringModalOpen(true);
    } catch (error) {
      console.error('루틴 과제 도달 모니터링 로드 실패:', error);
      setRoutineMonitoringData([]);
      setRoutineMonitoringLoading(false);
    }
  };

  const handleRoutineRewardGrant = async () => {
    if (!user?.id || !currentClass?.id || !selectedRoutineForDetail) return;
    try {
      setIsRoutineRewardProcessing(true);
      const consolidatedService = new RoutineAssignmentConsolidatedService(
        user.id,
        currentClass.id,
        currentMemberGroup
      );
      let monitoringData = routineMonitoringData;
      let consolidatedDocs: import('../types/index').RoutineAssignmentConsolidated[] | undefined;
      if (monitoringData.length === 0) {
        const loaded = await loadRoutineMonitoringData();
        monitoringData = loaded.data;
        consolidatedDocs = loaded.consolidatedDocs;
        setRoutineMonitoringData(loaded.data);
      } else {
        const cached = routineMonitoringCacheRef.current;
        if (cached?.routineId === selectedRoutineForDetail.id && cached.consolidatedDocs) {
          consolidatedDocs = cached.consolidatedDocs;
        }
      }
      const completionRatesByStudentId = monitoringData.reduce<Record<string, number>>((acc, item) => {
        acc[item.studentId] = item.completionRate;
        return acc;
      }, {});
      const result = await consolidatedService.processRoutineRewardsManualWithRates(
        selectedRoutineForDetail.id,
        completionRatesByStudentId,
        consolidatedDocs
      );
      invalidateRoutineMonitoringCache(selectedRoutineForDetail.id);
      setToastMessage(result.message);
      setToastRequiresConfirm(true);
      setShowToast(true);
      const { data: freshData } = await loadRoutineMonitoringData();
      setRoutineMonitoringData(freshData);
    } catch (error) {
      console.error('루틴 과제 보상 지급 실패:', error);
      setToastMessage('보상 처리 중 오류가 발생했습니다.');
      setToastRequiresConfirm(true);
      setShowToast(true);
    } finally {
      setIsRoutineRewardProcessing(false);
    }
  };

  const handleOpenProjectMonitoring = async () => {
    if (!user?.id || !currentClass?.id || !selectedProjectForDetail) return;
    setProjectMonitoringLoading(true);
    try {
      const result = await projectAssignmentService.loadProjectAssignmentWithStatuses(
        user.id,
        currentClass.id,
        selectedProjectForDetail.id,
        currentMemberGroup
      );

      const projectAssignment = result.projectAssignment;
      const subTasks = (projectAssignment.subTasks || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
      const totalSubTasks = subTasks.length;
      const memberMap = new Map(members.map(m => [m.id, m]));
      const assignedIds = projectAssignment.assignedStudentIds || [];
      const completedStatuses = new Set(['confirmed', 'delayed', 'exempted']);

      type SubTaskWithStatus = { studentStatuses?: Array<{ studentId: string; status: string }> };
      const data = assignedIds.map(studentId => {
        const member = memberMap.get(studentId);
        const segmentStates = subTasks.map(subTask => {
          const task = subTask as SubTaskWithStatus;
          const status = task.studentStatuses?.find((s: { studentId: string; status: string }) => s.studentId === studentId);
          return status ? completedStatuses.has(status.status) : false;
        });
        const completedCount = segmentStates.filter(Boolean).length;
        const completionRate = totalSubTasks > 0
          ? Number(((completedCount / totalSubTasks) * 100).toFixed(2))
          : 0;

        return {
          studentId,
          studentName: member?.name || '알 수 없음',
          studentNumber: Number(member?.number || 0),
          completionRate,
          completedCount,
          totalSubTasks,
          segmentStates
        };
      });

      data.sort((a, b) => a.studentNumber - b.studentNumber);

      setProjectMonitoringData(data);
      setProjectMonitoringLoading(false);
      setIsProjectMonitoringModalOpen(true);
    } catch (error) {
      console.error('프로젝트 과제 도달 모니터링 로드 실패:', error);
      setProjectMonitoringData([]);
      setProjectMonitoringLoading(false);
    }
  };

  // 루틴 과제 날짜별 상태 요약 정보 생성 (루틴 날짜 + 커스텀 날짜)
  const processRoutineDateStatuses = (dateStatuses: any[], routine: RoutineAssignment) => {
    // 1. 루틴 날짜 계산 (서버 데이터 유무와 관계없이)
    const routineDates = generateRoutineDates(routine);
    
    // 2. 루틴 날짜에 없는 서버 날짜들 찾기 (커스텀 날짜)
    console.log('🔍 커스텀 날짜 찾기 시작:', {
      dateStatusesCount: dateStatuses?.length || 0,
      routineDatesCount: routineDates.length,
      routineDateIds: routineDates.map(rd => rd.dateId)
    });
    
    const customDates = (dateStatuses || [])
      .filter(ds => {
        const isNotRoutineDate = !routineDates.some(rd => rd.dateId === ds.dateId);
        console.log(`🔍 커스텀 날짜 필터링: ${ds.dateId}`, {
          isNotRoutineDate,
          routineDateIds: routineDates.map(rd => rd.dateId)
        });
        return isNotRoutineDate;
      })
      .map(ds => ({
        dateId: ds.dateId,
        date: new Date(ds.dateId),
        dayOfWeek: ['일', '월', '화', '수', '목', '금', '토'][new Date(ds.dateId).getDay()],
        isRoutineDay: false, // 커스텀 날짜 표시
        isCustomDate: true   // 커스텀 날짜 플래그 추가
      }));
    
    // 4. 루틴 날짜와 커스텀 날짜 합치기
    const allDates = [...routineDates, ...customDates];
    
    console.log('🔍 processRoutineDateStatuses 디버깅:', {
      routineTitle: routine.title,
      routineStartDate: routine.startDate,
      routineDates: routineDates.map(d => ({ dateId: d.dateId, date: d.date.toISOString().split('T')[0] })),
      customDates: customDates.map(d => ({ dateId: d.dateId, date: d.date.toISOString().split('T')[0] })),
      allDates: allDates.map(d => ({ dateId: d.dateId, date: d.date.toISOString().split('T')[0], isRoutineDay: d.isRoutineDay, isCustomDate: d.isCustomDate })),
      serverDateStatuses: dateStatuses?.map(ds => ({ dateId: ds.dateId, studentsCount: Object.keys(ds.students || {}).length }))
    });
    
    // 5. 모든 날짜에 대해 상태 정보 생성
    console.log('🔍 최종 날짜 처리 시작:', {
      totalDates: allDates.length,
      routineDates: allDates.filter(d => d.isRoutineDay).length,
      customDates: allDates.filter(d => d.isCustomDate).length
    });
    
    const result = allDates.map(dateInfo => {
      const dateId = dateInfo.dateId;
      const serverDateStatus = dateStatuses?.find(ds => ds.dateId === dateId);
      
      console.log(`🔍 날짜 처리: ${dateId}`, {
        found: !!serverDateStatus,
        serverData: serverDateStatus ? '있음' : '없음',
        isRoutineDay: dateInfo.isRoutineDay,
        isCustomDate: dateInfo.isCustomDate,
        studentsCount: serverDateStatus ? Object.keys(serverDateStatus.students || {}).length : 0
      });
      
      if (serverDateStatus) {
        // 🎯 서버에 데이터가 있는 날짜 (루틴이든 커스텀이든)
        const students = serverDateStatus.students || {};
        const totalAssignedStudents = routine.assignedStudentIds?.length || 0;
        
        // 🎯 서버에 문서가 없는 멤버들은 'pending' 상태로 계산
        const serverStudentsCount = Object.keys(students).length;
        const missingStudentsCount = totalAssignedStudents - serverStudentsCount;
        
        // 🎯 서버에 있는 멤버 중 pending 상태인 멤버 수
        const serverPendingCount = Object.values(students).filter(
          (s: any) => s.status === 'pending'
        ).length;
        
        // 🎯 전체 pending 멤버 수 = 서버 pending + 문서가 없는 멤버들
        const totalPendingCount = serverPendingCount + missingStudentsCount;
        
        // 🎯 디버깅을 위한 상세 로그
        console.log(`🔍 ${dateId} 날짜 상태 계산:`, {
          totalAssignedStudents,
          serverStudentsCount,
          missingStudentsCount,
          serverPendingCount,
          totalPendingCount,
          confirmedCount: totalAssignedStudents - totalPendingCount,
          studentStatuses: Object.values(students).map((s: any) => ({
            studentId: s.studentId,
            status: s.status
          }))
        });
        
        return {
          dateId: dateInfo.dateId,
          date: dateInfo.date,
          dayOfWeek: dateInfo.dayOfWeek,
          isRoutineDay: dateInfo.isRoutineDay,
          isCustomDate: dateInfo.isCustomDate,
          status: {
            pendingCount: totalPendingCount, // 🎯 전체 pending 멤버 수
            hasCompletedStudents: totalPendingCount < totalAssignedStudents,
            totalStudents: totalAssignedStudents,
            hasServerData: true
          }
        };
      } else {
        // 🎯 서버에 데이터가 없는 날짜 (루틴 날짜만)
        if (dateInfo.isCustomDate) {
          // 커스텀 날짜인데 서버 데이터가 없으면 제외
          console.log(`🔍 커스텀 날짜 제외: ${dateId} - 서버 데이터 없음`);
          return null;
        }
        
        return {
          dateId: dateInfo.dateId,
          date: dateInfo.date,
          dayOfWeek: dateInfo.dayOfWeek,
          isRoutineDay: dateInfo.isRoutineDay,
          isCustomDate: dateInfo.isCustomDate,
          status: {
            pendingCount: 0,
            hasCompletedStudents: false,
            totalStudents: routine.assignedStudentIds?.length || 0, // 🎯 실제 과제를 부여받은 멤버 수
            hasServerData: false
          }
        };
      }
    })
    .filter(Boolean) // null 값 제거
    .sort((a, b) => (b?.date.getTime() || 0) - (a?.date.getTime() || 0)); // 🎯 내림차순 정렬 (최신 날짜가 위로)
    
    console.log('🔍 최종 결과:', {
      totalResultDates: result.length,
      routineDates: result.filter(d => d?.isRoutineDay).length,
      customDates: result.filter(d => d?.isCustomDate).length,
      datesWithData: result.filter(d => d?.status.hasServerData).length
    });
    
    return result;
  };

  // 🎯 날짜 필터링 및 정렬 함수
  const getFilteredAndSortedDates = useCallback((dates: any[]) => {
    let filtered = [...dates];
    
    // 미래 날짜 필터링 (오늘 이후 날짜는 제외)
    const today = new Date();
    today.setHours(0, 0, 0, 0); // 오늘 00:00:00으로 설정
    filtered = filtered.filter(date => {
      const dateOnly = new Date(date.date);
      dateOnly.setHours(0, 0, 0, 0); // 날짜만 비교하기 위해 시간 제거
      return dateOnly <= today; // 오늘 이전 날짜만 포함
    });
    
    // 필터링
    switch (dateFilterType) {
      case 'hasData':
        // 서버에 기록이 있는 날짜만
        filtered = filtered.filter(date => date.status.hasServerData);
        break;
      case 'customOnly':
        // 커스텀 날짜만 (루틴에 포함되지 않은 날짜)
        filtered = filtered.filter(date => date.isCustomDate);
        break;
      default:
        // 'desc'와 'asc'는 모든 날짜 포함
        break;
    }
    
    // 정렬
    switch (dateFilterType) {
      case 'desc':
        // 내림차순 (최신 날짜가 위로)
        filtered.sort((a, b) => (b?.date.getTime() || 0) - (a?.date.getTime() || 0));
        break;
      case 'asc':
        // 오름차순 (과거 날짜가 위로)
        filtered.sort((a, b) => (a?.date.getTime() || 0) - (b?.date.getTime() || 0));
        break;
      case 'hasData':
      case 'customOnly':
        // 필터링된 날짜들은 내림차순 정렬
        filtered.sort((a, b) => (b?.date.getTime() || 0) - (a?.date.getTime() || 0));
        break;
    }
    
    return filtered;
  }, [dateFilterType]);

  // 🎯 루틴 날짜 페이지네이션 계산
  const getRoutineDatePagination = useCallback(() => {
    const filteredDates = getFilteredAndSortedDates(routineDateSummaries);
    const totalPages = Math.ceil(filteredDates.length / routineDateItemsPerPage);
    const startIndex = (routineDateCurrentPage - 1) * routineDateItemsPerPage;
    const endIndex = startIndex + routineDateItemsPerPage;
    const currentPageDates = filteredDates.slice(startIndex, endIndex);
    
    return {
      totalPages,
      currentPageDates,
      totalCount: filteredDates.length
    };
  }, [routineDateSummaries, routineDateCurrentPage, routineDateItemsPerPage, getFilteredAndSortedDates]);

  // 🎯 루틴 날짜 페이지 이동 핸들러
  const handleRoutineDatePageChange = useCallback((newPage: number) => {
    const { totalPages } = getRoutineDatePagination();
    if (newPage >= 1 && newPage <= totalPages) {
      setRoutineDateCurrentPage(newPage);
    }
  }, [getRoutineDatePagination]);

  // 🎯 일반 과제 페이지네이션 계산
  const getGeneralAssignmentPagination = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const filteredAndSorted = assignments
      .filter(a => {
        // 카테고리 필터
        if (filterCategoryId === '__default__') {
          if (a.categoryId && a.categoryId.trim() !== '') return false;
        } else if (filterCategoryId) {
          if (a.categoryId !== filterCategoryId) return false;
        }
        // 완료 상태 필터
        if (filterType === 'incomplete') {
          return (a.pendingCount && a.pendingCount > 0) || (a.submittedCount && a.submittedCount > 0);
        }
        return true;
      })
      .sort((a, b) => {
        switch (generalSortBy) {
          case 'createdAt': {
            const diff = createdAtToMs(b.createdAt) - createdAtToMs(a.createdAt);
            if (diff !== 0) return diff;
            return (a.id || '').localeCompare(b.id || '');
          }
          case 'dueDate':
            return new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime();
          case 'title':
            return a.title.localeCompare(b.title);
          default:
            return 0;
        }
      });

    const totalCount = filteredAndSorted.length;
    const totalPages = Math.ceil(totalCount / generalAssignmentItemsPerPage);
    const startIndex = (generalAssignmentCurrentPage - 1) * generalAssignmentItemsPerPage;
    const endIndex = startIndex + generalAssignmentItemsPerPage;
    const currentPageAssignments = filteredAndSorted.slice(startIndex, endIndex);

    return {
      totalPages,
      totalCount,
      currentPageAssignments
    };
  }, [assignments, filterCategoryId, filterType, generalSortBy, generalAssignmentCurrentPage, generalAssignmentItemsPerPage, createdAtToMs]);

  // 🎯 프로젝트 과제 페이지네이션 계산
  const getProjectAssignmentPagination = useCallback(() => {
    const filteredAndSorted = projectAssignments
      .filter(project => {
        if (projectFilterType === 'incomplete') {
          return (project.pendingCount && project.pendingCount > 0) || (project.submittedCount && project.submittedCount > 0);
        }
        return true;
      })
      .sort((a, b) => {
        switch (projectSortBy) {
          case 'createdAt': {
            const diff = createdAtToMs(b.createdAt) - createdAtToMs(a.createdAt);
            if (diff !== 0) return diff;
            return (a.id || '').localeCompare(b.id || '');
          }
          case 'dueDate':
            return new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime();
          case 'title':
            return a.title.localeCompare(b.title);
          default:
            return 0;
        }
      });

    const totalCount = filteredAndSorted.length;
    const totalPages = Math.ceil(totalCount / projectAssignmentItemsPerPage);
    const startIndex = (projectAssignmentCurrentPage - 1) * projectAssignmentItemsPerPage;
    const endIndex = startIndex + projectAssignmentItemsPerPage;
    const currentPageProjects = filteredAndSorted.slice(startIndex, endIndex);

    return {
      totalPages,
      totalCount,
      currentPageProjects
    };
  }, [projectAssignments, projectFilterType, projectSortBy, projectAssignmentCurrentPage, projectAssignmentItemsPerPage, createdAtToMs]);

  // 🎯 일반 과제 페이지 변경 핸들러
  const handleGeneralAssignmentPageChange = useCallback((newPage: number) => {
    const { totalPages } = getGeneralAssignmentPagination();
    if (newPage >= 1 && newPage <= totalPages) {
      setGeneralAssignmentCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [getGeneralAssignmentPagination]);

  // 🎯 프로젝트 과제 페이지 변경 핸들러
  const handleProjectAssignmentPageChange = useCallback((newPage: number) => {
    const { totalPages } = getProjectAssignmentPagination();
    if (newPage >= 1 && newPage <= totalPages) {
      setProjectAssignmentCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [getProjectAssignmentPagination]);

  // 🎯 날짜 삭제 핸들러
  const handleDateDelete = useCallback((routineId: string, dateId: string, date: Date) => {
    console.log('🗑️ 날짜 삭제 요청:', { routineId, dateId, date });
    setDateToDelete({ routineId, dateId, date });
    setIsDateDeleteConfirmModalOpen(true);
  }, []);

  // 🎯 날짜 삭제 실행 함수
  const executeDateDelete = async () => {
    if (!dateToDelete || !user?.id || !currentClass?.id) return;
    
    try {
      console.log('🗑️ 날짜 삭제 실행 시작:', {
        dateToDelete,
        userId: user.id,
        classId: currentClass.id
      });
      
      // 해당 날짜의 전체 문서와 하위 컬렉션 삭제 (현재 그룹 경로 사용)
      await routineAssignmentService.deleteRoutineAssignmentDate(
        user.id,
        currentClass.id,
        dateToDelete.routineId,
        dateToDelete.dateId,
        currentMemberGroup
      );
      
      console.log('✅ 날짜 삭제 완료:', dateToDelete.dateId);
      
      // 모달 닫기
      setIsDateDeleteConfirmModalOpen(false);
      setDateToDelete(null);
      
      // 데이터 새로고침
      await loadData();
      
      // 현재 선택된 루틴의 날짜별 현황도 다시 로드
      if (selectedRoutineForDetail) {
        try {
          const consolidatedService = new RoutineAssignmentConsolidatedService(user.id, currentClass.id, currentMemberGroup);
          const result = await consolidatedService.loadRoutineAssignmentWithStatuses(selectedRoutineForDetail.id);
          
          // RoutineAssignmentDateStatus 형식으로 변환
          const dateStatuses = result.dateStatuses.map(dateAssignment => {
            const students: Record<string, RoutineAssignmentStudentStatus> = {};
            let totalStudents = 0;
            let completedStudents = 0;
            let pendingStudents = 0;
            
            const studentStatuses = dateAssignment.studentStatuses || [];
            studentStatuses.forEach((studentStatus: any) => {
              students[studentStatus.studentId] = {
                id: studentStatus.studentId,
                studentId: studentStatus.studentId,
                assignmentId: selectedRoutineForDetail.id,
                status: studentStatus.status,
                earnedPoints: studentStatus.points,
                memo: studentStatus.memo,
                updatedAt: studentStatus.updatedAt
              };
              
              totalStudents++;
              if (studentStatus.status === 'confirmed') {
                completedStudents++;
              } else if (studentStatus.status === 'pending') {
                pendingStudents++;
              }
            });
            
            return {
              dateId: dateAssignment.date,
              routineAssignmentId: selectedRoutineForDetail.id,
              date: new Date(dateAssignment.date),
              isCustomDate: dateAssignment.isCustomDate,
              students,
              totalStudents,
              completedStudents,
              pendingStudents,
              updatedAt: new Date()
            };
          });
          
          const summaries = processRoutineDateStatuses(dateStatuses, selectedRoutineForDetail);
          setRoutineDateSummaries(summaries);
          console.log('🔄 날짜 삭제 후 날짜별 과제 현황 리로드 완료');
        } catch (error) {
          console.error('날짜별 과제 현황 리로드 실패:', error);
        }
      }
      
    } catch (error) {
      console.error('❌ 날짜 삭제 실패:', error);
      alert(`날짜 삭제에 실패했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
    } finally {
      // 모달 닫기 및 상태 초기화 (성공/실패 관계없이 실행)
      setIsDateDeleteConfirmModalOpen(false);
      setDateToDelete(null);
    }
  };

  // 🎯 DateInputModal 확인 핸들러
  const handleDateInputConfirm = useCallback((date: Date) => {
    if (!selectedRoutineForDetail) return;
    
    console.log('🎯 커스텀 날짜 선택됨:', date);
    
    setIsDateInputModalOpen(false);
    
    // 커스텀 날짜 설정
    setCustomDateForRoutine(date);
    
    // 루틴 과제 체크 모달 열기
    setSelectedRoutineAssignment({
      id: selectedRoutineForDetail.id,
      routineAssignmentId: selectedRoutineForDetail.id,
      date: date,
      studentId: '', // 빈 값으로 설정 (전체 멤버 대상)
      status: 'pending',
      earnedPoints: 0,
      updatedAt: new Date(),
      isCustomDate: true
    });
    
    setIsRoutineCheckModalOpen(true);
    

  }, [selectedRoutineForDetail]);

  // 루틴 과제 복제
  const handleCopyRoutineAssignment = (routine: RoutineAssignment) => {
    console.log('📋 루틴 과제 복제 시작:', {
      routineId: routine.id,
      title: routine.title,
      assignedStudentIds: routine.assignedStudentIds,
      membersCount: members.length
    });
    
    const selectedIds = routine.assignedStudentIds || members.map(m => m.id);
    console.log('👥 복사할 멤버 ID들:', selectedIds);
    
    // 상태를 먼저 설정
    setInitialRoutineAssignment({
      // id는 복사할 때 제거 (새로 생성될 것이므로)
      title: `${routine.title} (복사)`,
      startDate: new Date(routine.startDate), // 시작일 추가
      routineDays: [...routine.routineDays], // 배열 복사
      dueDate: new Date(routine.dueDate), // 날짜 객체 복사
      basePoints: routine.basePoints,
      assignedStudentIds: [...routine.assignedStudentIds], // 배열 복사
      categoryId: routine.categoryId,
      notes: routine.notes, // 노트 추가
      rewardEnabled: routine.rewardEnabled ?? false,
      rewardMessage: routine.rewardMessage ?? '',
      rewardCoins: routine.rewardCoins ?? 0,
      rewardItemCardIds: routine.rewardItemCardIds ?? [],
      rewardCompletionRate: routine.rewardCompletionRate ?? 100,
      includeInIncompleteCheck: routine.includeInIncompleteCheck ?? false,
      // customDates 제거
      isActive: routine.isActive, // 활성 상태도 복사
    });
    setModalMode('copy');
    
    // initialSelectedIds를 설정한 후 모달 열기
    setInitialSelectedIds(selectedIds);
    console.log('✅ initialSelectedIds 설정됨 (복사):', selectedIds);
    
    // 다음 렌더링 사이클에서 모달 열기
    setTimeout(() => {
      setIsRoutineSettingsModalOpen(true);
    }, 0);
  };

  // ===== 프로젝트 과제 핸들러 함수들 =====

  // 프로젝트 과제 편집
  const handleProjectEdit = (project: ProjectAssignment) => {
    console.log('🔧 프로젝트 과제 편집 시작:', {
      projectId: project.id,
      title: project.title,
      assignedStudentIds: project.assignedStudentIds,
      membersCount: members.length
    });
    
    // 편집할 프로젝트 설정
    setEditingProject(project);
    setIsProjectSettingsModalOpen(true);
  };

  // 프로젝트 과제 삭제
  const handleProjectDelete = async (project: ProjectAssignment) => {
    setProjectToDelete(project);
    setIsProjectDeleteConfirmModalOpen(true);
  };

  // 프로젝트 과제 복제
  const handleProjectCopy = (project: ProjectAssignment) => {
    console.log('📋 프로젝트 과제 복제 시작:', {
      projectId: project.id,
      title: project.title,
      assignedStudentIds: project.assignedStudentIds,
      membersCount: members.length,
      subTasks: project.subTasks
    });
    
    // 복제할 프로젝트 정보 설정 (하위과제 포함)
    setEditingProject({
      ...project,
      title: `${project.title} (복사)`,
      id: '', // 새 ID를 위해 빈 문자열로 설정
    });
    setIsProjectSettingsModalOpen(true);
  };

  // 프로젝트 하위 과제 체크 모달 열기
  const handleProjectSubTaskCheck = async (subTaskSummary: any) => {
    console.log('🔍 프로젝트 하위과제 체크 모달 열기:', subTaskSummary);
    
    // 선택된 하위과제와 프로젝트 정보 설정
    setSelectedProjectAssignment(selectedProjectForDetail);
    setSelectedSubTaskId(subTaskSummary.subTask.id);
    
    // 현재 선택된 하위과제의 인덱스 찾기
    const subTaskIndex = projectSubTaskSummaries.findIndex(
      summary => summary.subTask.id === subTaskSummary.subTask.id
    );
    setCurrentSubTaskIndex(subTaskIndex);
    
    // 기존 멤버 상태 로드
    if (user?.id && currentClass?.id && selectedProjectForDetail) {
      try {
        console.log('🔍 프로젝트 하위과제 멤버 상태 로드 시작:', {
          projectId: selectedProjectForDetail.id,
          subTaskId: subTaskSummary.subTask.id
        });
        
        const existingStatuses = await projectAssignmentService.loadProjectAssignmentStudentStatuses(
          user.id,
          currentClass.id,
          selectedProjectForDetail.id,
          subTaskSummary.subTask.id,
          currentMemberGroup
        );
        
        console.log('✅ 프로젝트 하위과제 멤버 상태 로드 완료:', existingStatuses);
        setProjectStudentStatuses(existingStatuses);
      } catch (error) {
        console.error('프로젝트 하위과제 멤버 상태 로드 실패:', error);
        setProjectStudentStatuses([]);
      }
    }
    
    // 프로젝트 체크 모달 열기
    setIsProjectCheckModalOpen(true);
  };

  // 프로젝트 과제 보상 지급 핸들러 (상세 로드 시 캐시된 projectAssignmentInfo 재사용)
  const handleProjectRewardGrant = async (projectAssignmentId: string) => {
    if (!user?.id || !currentClass?.id) return;
    const cached =
      selectedProjectForDetail?.id === projectAssignmentId ? (projectAssignmentInfoForReward ?? undefined) : undefined;
    try {
      const { ProjectAssignmentRewardService } = await import('../services/projectAssignmentRewardService');
      const rewardService = new ProjectAssignmentRewardService(user.id, currentClass.id, currentMemberGroup);
      const result = await rewardService.processProjectAssignmentRewardsManual(projectAssignmentId, cached);

      if (result.success) {
        setToastMessage(result.message);
        setToastRequiresConfirm(true);
        setShowToast(true);
        await loadData();
        if (selectedProjectForDetail && selectedProjectForDetail.id === projectAssignmentId) {
          const refreshedResult = await projectAssignmentService.loadProjectAssignmentWithStatuses(
            user.id,
            currentClass.id,
            projectAssignmentId,
            currentMemberGroup
          );
          setProjectSubTaskSummaries(refreshedResult.subTaskSummaries);
          setSelectedProjectForDetail(refreshedResult.projectAssignment);
          setProjectAssignmentInfoForReward(refreshedResult.projectAssignmentInfo);
        }
      } else {
        // 에러 메시지도 토스트로 표시
        setToastMessage(result.message);
        setToastRequiresConfirm(true);
        setShowToast(true);
      }
    } catch (error) {
      console.error('❌ 프로젝트 보상 지급 실패:', error);
      setToastMessage('보상 지급 중 오류가 발생했습니다.');
      setToastRequiresConfirm(true);
      setShowToast(true);
    }
  };

  // 프로젝트 과제 체크 - 상세 보기 토글
  const handleProjectCheck = async (project: ProjectAssignment) => {
    try {
      if (selectedProjectForDetail && selectedProjectForDetail.id === project.id) {
        setSelectedProjectForDetail(null);
        setProjectAssignmentInfoForReward(null);
        setProjectSubTaskSummaries([]);
        return;
      }
      setSelectedProjectForDetail(null);
      setProjectAssignmentInfoForReward(null);
      setProjectSubTaskSummaries([]);
      setSelectedProjectForDetail(project);

      if (user?.id && currentClass?.id) {
        try {
          const result = await projectAssignmentService.loadProjectAssignmentWithStatuses(
            user.id,
            currentClass.id,
            project.id,
            currentMemberGroup
          );
          setProjectSubTaskSummaries(result.subTaskSummaries);
          setProjectAssignmentInfoForReward(result.projectAssignmentInfo);
        } catch (error) {
          console.error('프로젝트 하위과제 데이터 로드 실패:', error);
          setProjectSubTaskSummaries([]);
          setProjectAssignmentInfoForReward(null);
        }
      }
    } catch (error) {
      console.error('프로젝트 과제 체크 실패:', error);
      alert('프로젝트 과제를 불러오는데 실패했습니다.');
    }
  };

  // 멤버 정보와 과제 정보를 로드하는 함수
  const loadData = useCallback(async () => {
    if (!user?.id || !currentClass?.id) return;
    
    console.log('🔄 loadData 시작:', { userId: user.id, classId: currentClass.id });
    
    const [loadedMembers, loadedAssignments, loadedRoutineAssignments, loadedProjectAssignments] = await Promise.all([
      userDataService.loadClassMembers(user.id, currentClass.id, currentMemberGroup),
      assignmentService.loadAssignments(user.id, currentClass.id, currentMemberGroup),
      routineAssignmentService.loadRoutineAssignments(user.id, currentClass.id, currentMemberGroup),
      projectAssignmentService.getProjectAssignments(user.id, currentClass.id, currentMemberGroup)
    ]);
    
    console.log('📊 로드된 데이터:', {
      members: loadedMembers.length,
      assignments: loadedAssignments.length,
      routineAssignments: loadedRoutineAssignments.length,
      projectAssignments: loadedProjectAssignments.length
    });
    
    setMembers(loadedMembers);
    setRoutineAssignments(loadedRoutineAssignments);
    
    // loadedProjectAssignments에 이미 submittedCount가 포함되어 있음
    setProjectAssignments(loadedProjectAssignments);
    
    // 과제 설정 로드
    try {
      const assignmentSettings = await userDataService.loadAssignmentSettings(user.id, currentClass.id);
      if (assignmentSettings) {
        setDefaultPoints(assignmentSettings.defaultPoints);
        setStatusDefaultPoints(assignmentSettings.statusDefaultPoints);
      } else {
        // 설정이 없으면 기본값으로 초기화
        await userDataService.initializeAssignmentSettings(user.id, currentClass.id);
        const defaultSettings = await userDataService.loadAssignmentSettings(user.id, currentClass.id);
        if (defaultSettings) {
          setDefaultPoints(defaultSettings.defaultPoints);
          setStatusDefaultPoints(defaultSettings.statusDefaultPoints);
        }
      }
    } catch (error) {
      console.error('과제 설정 로드 실패:', error);
      // 에러가 발생해도 기본값 사용
    }
    
    // loadedAssignments에 이미 pendingCount와 submittedCount가 포함되어 있음
    // 정렬: 미래/최신 날짜가 위로, 과거는 아래로, 같은 마감일이면 생성일 최신순
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const sorted = [...loadedAssignments].sort((a, b) => {
      const aDueDate = new Date(a.dueDate);
      aDueDate.setHours(0, 0, 0, 0);
      const bDueDate = new Date(b.dueDate);
      bDueDate.setHours(0, 0, 0, 0);
      
      const aIsFuture = aDueDate >= today;
      const bIsFuture = bDueDate >= today;
      
      // 1순위: 미래 날짜가 과거 날짜보다 위로
      if (aIsFuture !== bIsFuture) {
        return aIsFuture ? -1 : 1; // 미래가 위로 (음수 반환)
      }
      
      // 2순위: 같은 그룹(미래/과거) 내에서 마감일 비교
      const dueDateComparison = bDueDate.getTime() - aDueDate.getTime();
      
      // 마감일이 다르면 마감일 순으로 정렬 (최신이 위)
      if (dueDateComparison !== 0) {
        return dueDateComparison;
      }
      
      // 3순위: 마감일이 같으면 생성일·시·분·초 비교 (내림차순 - 나중에 생성된 것이 위)
      const aCreatedAt = a.createdAt instanceof Date ? a.createdAt : 
                        (a.createdAt as any)?.toDate?.() || 
                        (typeof a.createdAt === 'string' ? new Date(a.createdAt) : 
                        (a.createdAt ? new Date(a.createdAt) : new Date(0)));
      const bCreatedAt = b.createdAt instanceof Date ? b.createdAt : 
                        (b.createdAt as any)?.toDate?.() || 
                        (typeof b.createdAt === 'string' ? new Date(b.createdAt) : 
                        (b.createdAt ? new Date(b.createdAt) : new Date(0)));
      const createdDiff = bCreatedAt.getTime() - aCreatedAt.getTime();
      if (createdDiff !== 0) return createdDiff;
      return (a.id || '').localeCompare(b.id || '');
    });
    setAssignments(sorted);
    const cats = await assignmentCategoryService.loadCategories(user.id, currentClass.id);
    setCategories(cats);
  }, [user?.id, currentClass?.id, currentMemberGroup]);

  // 과제 설정 변경 시 서버에 저장하는 함수
  const handleAssignmentSettingsChange = useCallback(async (newDefaultPoints: number, newStatusDefaultPoints: any) => {
    if (!user?.id || !currentClass?.id) return;
    
    try {
      const newSettings = {
        defaultPoints: newDefaultPoints,
        statusDefaultPoints: newStatusDefaultPoints,
        updatedAt: new Date()
      };
      
      await userDataService.saveAssignmentSettings(user.id, currentClass.id, newSettings);
      
      // 로컬 상태 업데이트
      setDefaultPoints(newDefaultPoints);
      setStatusDefaultPoints(newStatusDefaultPoints);
      
      console.log('✅ 과제 설정이 서버에 저장되었습니다.');
    } catch (error) {
      console.error('❌ 과제 설정 저장 실패:', error);
      alert('과제 설정 저장에 실패했습니다.');
    }
  }, [user?.id, currentClass?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // onMembersChange는 이제 홈 페이지로 이동하므로 과제 페이지에서는 처리하지 않음

  const handleCreateAssignment = async (assignment: Assignment, selectedMemberIdsByGroup: Record<string, string[]>, mode: 'new' | 'copy' | 'edit') => {
    if (!user?.id || !currentClass?.id) return;
    const primaryGroup = (currentMemberGroup || 'A').toString().trim().toUpperCase();
    const targetGroups = Object.entries(selectedMemberIdsByGroup || {})
      .filter(([_, ids]) => Array.isArray(ids) && ids.length > 0)
      .map(([g]) => (g || 'A').toString().trim().toUpperCase());
    if (targetGroups.length === 0) {
      // 어떤 그룹도 학생 선택이 없으면 저장/전파하지 않음
      return;
    }
    
    const getGroupSelectedIds = (group: string) => {
      const g = (group || 'A').toString().trim().toUpperCase();
      return selectedMemberIdsByGroup?.[g] || [];
    };
    
    // 새로 생성된 과제의 ID를 저장하기 위해 생성 전 과제 목록 가져오기
    let beforeAssignments: Assignment[] = [];
    if (mode !== 'edit') {
      beforeAssignments = await assignmentService.loadAssignments(user.id, currentClass.id, primaryGroup);
    }
    
    // 그룹별로 저장/전파
    for (const group of targetGroups) {
      const g = (group || 'A').toString().trim().toUpperCase();
      const groupMembers = await userDataService.loadClassMembers(user.id, currentClass.id, g);
      const selectedIds = getGroupSelectedIds(g);
      const selectedMembers = groupMembers.filter(m => selectedIds.includes(m.id));
      if (selectedMembers.length === 0) continue;

      const assignmentForGroup: Assignment = {
        ...assignment,
        rewardGrantedStudentIds: [], // 보상 지급 완료 멤버 ID 초기화 (먼저 설정)
        assignedStudentIds: selectedMembers.map(m => m.id),
        pendingCount: selectedMembers.length,
        lastPendingUpdate: new Date()
      };

      console.log(`🎯 과제 생성/수정 - rewardGrantedStudentIds 초기화:`, {
        mode,
        assignmentId: assignment.id,
        originalRewardGrantedIds: assignment.rewardGrantedStudentIds,
        finalRewardGrantedIds: assignmentForGroup.rewardGrantedStudentIds
      });

      if (mode === 'edit') {
        await assignmentService.upsertAssignment(user.id, currentClass.id, assignmentForGroup, selectedMembers, g);
      } else {
        await assignmentService.createAssignment(user.id, currentClass.id, assignmentForGroup, selectedMembers, g);
      }
    }
    
    // 새로 생성/수정된 과제 목록을 다시 로드하여 pendingCount 포함
    await loadData();
    
    // 새로 생성된 과제 찾기
    let createdAssignmentId: string | null = null;
    if (mode !== 'edit') {
      const afterAssignments = await assignmentService.loadAssignments(user.id, currentClass.id, primaryGroup);
      
      // 새로 추가된 과제 찾기
      const newAssignment = afterAssignments.find(afterAss => 
        !beforeAssignments.some(beforeAss => beforeAss.id === afterAss.id)
      );
      
      if (newAssignment) {
        createdAssignmentId = newAssignment.id;
      }
    }
    
    setIsNewModalOpen(false);
    
    // 새로 생성된 과제인 경우에만 체크 모달 열기
    if (mode !== 'edit' && createdAssignmentId) {
      // afterAssignments에서 새로 생성된 과제 찾기
      const afterAssignments = await assignmentService.loadAssignments(user.id, currentClass.id, primaryGroup);
      const newlyCreatedAssignment = afterAssignments.find(ass => ass.id === createdAssignmentId);
      
      if (newlyCreatedAssignment) {
        const primarySelected = getGroupSelectedIds(primaryGroup);
        const primaryMembers = await userDataService.loadClassMembers(user.id, currentClass.id, primaryGroup);
        const primarySelectedMembers = (primarySelected.length > 0)
          ? primaryMembers.filter(m => primarySelected.includes(m.id))
          : primaryMembers;

        // pendingCount 포함된 과제 객체 생성
        const assignmentWithPendingCount = {
          ...newlyCreatedAssignment,
          pendingCount: primarySelectedMembers.length,
          lastPendingUpdate: new Date()
        };
        
        // 새로 생성된 과제의 체크 모달 열기
        setSelectedAssignment(assignmentWithPendingCount);
        // pendingCount 추적 초기화
        pendingCountUpdateRef.current[newlyCreatedAssignment.id] = primarySelectedMembers.length;
        // 기존 멤버 상태 로드 (새로 생성된 과제이므로 빈 배열)
        setStudentStatuses([]);
        // 과제 체크 모달 열기
        setIsCheckModalOpen(true);
      }
    }
  };

  // 새 과제 모달 열기 전에 최신 멤버 정보 확인
  const handleOpenNewAssignmentModal = (mode: 'new' | 'copy' | 'edit', assignment?: Assignment) => {
    // 모달을 열기 전에 최신 멤버 정보 로드
    loadData();
    
    if (mode === 'copy' && assignment) {
      handleCopyAssignment(assignment);
    } else if (mode === 'edit' && assignment) {
      handleEditAssignment(assignment);
    } else {
      setModalMode('new');
      setInitialAssignment(undefined);
      setInitialSelectedIds(undefined);
      setIsNewModalOpen(true);
    }
  };

  const handleCopyAssignment = async (assignment: Assignment) => {
    if (!user?.id || !currentClass?.id) return;
    console.log('🔍 AssignmentsPage - 복사 모드, assignment:', assignment);
    console.log('🔍 AssignmentsPage - 복사할 icon 값:', assignment.icon);
    console.log('🔍 AssignmentsPage - 복사할 notes 값:', assignment.notes);
    console.log('🔍 AssignmentsPage - 복사할 보상 정보:', {
      rewardEnabled: assignment.rewardEnabled,
      rewardCode: assignment.rewardCode,
      rewardMessage: assignment.rewardMessage,
      rewardCoins: assignment.rewardCoins,
      rewardItemCardIds: assignment.rewardItemCardIds
    });
    
    const initialData: any = {
      title: `${assignment.title} (복사)`,
      basePoints: assignment.basePoints,
      categoryId: assignment.categoryId,
      icon: assignment.icon,
      notes: assignment.notes
    };
    
    // 과제 보상 필드 추가 (rewardEnabled가 true일 때만)
    if (assignment.rewardEnabled === true) {
      initialData.rewardEnabled = true;
      if (assignment.rewardCode !== undefined) {
        initialData.rewardCode = assignment.rewardCode;
      }
      if (assignment.rewardMessage !== undefined) {
        initialData.rewardMessage = assignment.rewardMessage;
      }
      if (assignment.rewardCoins !== undefined) {
        initialData.rewardCoins = assignment.rewardCoins;
      }
      if (assignment.rewardItemCardIds !== undefined) {
        initialData.rewardItemCardIds = assignment.rewardItemCardIds;
      }
    }
    
    setModalMode('copy');
    setInitialAssignment(initialData);
    setInitialSelectedIds(assignment.assignedStudentIds || members.map(m => m.id));
    setIsNewModalOpen(true);
  };

  const handleEditAssignment = (assignment: Assignment) => {
    console.log('🔍 AssignmentsPage - 편집 모드, assignment:', assignment);
    console.log('🔍 AssignmentsPage - 편집할 icon 값:', assignment.icon);
    console.log('🔍 AssignmentsPage - 편집할 notes 값:', assignment.notes);
    console.log('🔍 AssignmentsPage - 편집할 보상 정보:', {
      rewardEnabled: assignment.rewardEnabled,
      rewardCode: assignment.rewardCode,
      rewardMessage: assignment.rewardMessage,
      rewardCoins: assignment.rewardCoins,
      rewardItemCardIds: assignment.rewardItemCardIds
    });
    
    const initialData: any = {
      id: assignment.id,
      title: assignment.title,
      basePoints: assignment.basePoints,
      dueDate: assignment.dueDate,
      categoryId: assignment.categoryId,
      icon: assignment.icon,
      notes: assignment.notes
    };
    
    // 과제 보상 필드 추가 (rewardEnabled가 true일 때만)
    if (assignment.rewardEnabled === true) {
      initialData.rewardEnabled = true;
      if (assignment.rewardCode !== undefined) {
        initialData.rewardCode = assignment.rewardCode;
      }
      if (assignment.rewardMessage !== undefined) {
        initialData.rewardMessage = assignment.rewardMessage;
      }
      if (assignment.rewardCoins !== undefined) {
        initialData.rewardCoins = assignment.rewardCoins;
      }
      if (assignment.rewardItemCardIds !== undefined) {
        initialData.rewardItemCardIds = assignment.rewardItemCardIds;
      }
    }
    
    setModalMode('edit');
    setInitialAssignment(initialData);
    setInitialSelectedIds(assignment.assignedStudentIds || members.map(m => m.id));
    setIsNewModalOpen(true);
  };

  // 과제 체크 모달 열기
  const handleOpenCheckModal = async (assignment: Assignment) => {
    if (!user?.id || !currentClass?.id) return;
    
    // 최신 과제 정보 다시 로드 (rewardGrantedStudentIds 포함)
    try {
      const result = await assignmentService.loadAssignmentWithStatuses(user.id, currentClass.id, assignment.id);
      if (result.assignment) {
        setSelectedAssignment(result.assignment);
      } else {
        setSelectedAssignment(assignment);
      }
    } catch (error) {
      console.error('최신 과제 정보 로드 실패:', error);
      setSelectedAssignment(assignment);
    }
    
    // pendingCount 추적 초기화
    pendingCountUpdateRef.current[assignment.id] = assignment.pendingCount || 0;
    
    // 기존 멤버 상태 로드
    try {
      const existingStatuses = await assignmentService.loadStudentStatuses(user.id, currentClass.id, assignment.id);
      setStudentStatuses(existingStatuses);
      
      // 새로운 통합 구조에서는 항상 pendingCount와 submittedCount를 다시 계산
      // 제출 확인 상태도 미확인으로 취급
      const pendingCount = existingStatuses.filter(status => status.status === 'pending' || status.status === 'submitted').length;
      const submittedCount = existingStatuses.filter(status => status.status === 'submitted').length;
      const totalAssignedStudents = assignment.assignedStudentIds?.length || 0;
      
      // pendingCount와 submittedCount 업데이트 (서버에서 로드된 데이터 기반)
      setAssignments(prev => prev.map(ass => 
        ass.id === assignment.id 
          ? { ...ass, pendingCount, submittedCount, lastPendingUpdate: new Date(), lastSubmittedUpdate: new Date() }
          : ass
      ));
      
      // pendingCount 추적도 업데이트
      pendingCountUpdateRef.current[assignment.id] = pendingCount;
      
      console.log('🔍 과제 체크 모달 열기 - pendingCount 계산:', {
        assignmentId: assignment.id,
        totalAssignedStudents,
        existingStatusesCount: existingStatuses.length,
        pendingCount,
        studentStatuses: existingStatuses.map(s => ({ studentId: s.studentId, status: s.status }))
      });
    } catch (error) {
      console.error('멤버 상태 로드 실패:', error);
      // 기존 상태가 없으면 빈 배열로 시작
      setStudentStatuses([]);
      
      // pendingCount와 submittedCount 초기화
      const totalAssignedStudents = assignment.assignedStudentIds?.length || 0;
      setAssignments(prev => prev.map(ass => 
        ass.id === assignment.id 
          ? { ...ass, pendingCount: totalAssignedStudents, submittedCount: 0, lastPendingUpdate: new Date(), lastSubmittedUpdate: new Date() }
          : ass
      ));
      
      // pendingCount 추적도 업데이트
      pendingCountUpdateRef.current[assignment.id] = totalAssignedStudents;
    }
    
    setIsCheckModalOpen(true);
  };

  const toggleGeneralSelectionMode = () => {
    setIsGeneralSelectionMode(prev => {
      const next = !prev;
      if (!next) {
        setSelectedGeneralAssignmentIds(new Set());
      }
      return next;
    });
  };

  const toggleGeneralAssignmentSelected = (assignmentId: string) => {
    setSelectedGeneralAssignmentIds(prev => {
      const next = new Set(prev);
      if (next.has(assignmentId)) next.delete(assignmentId);
      else next.add(assignmentId);
      return next;
    });
  };

  const toggleProjectSelectionMode = () => {
    setIsProjectSelectionMode(prev => {
      const next = !prev;
      if (!next) {
        setSelectedProjectAssignmentIds(new Set());
      }
      return next;
    });
  };

  const toggleProjectAssignmentSelected = (projectId: string) => {
    setSelectedProjectAssignmentIds(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };


  const exportSelectedGeneralAssignments = async () => {
    if (!user?.id || !currentClass?.id) return;
    if (selectedGeneralAssignmentIds.size === 0) return;
    if (isExportingGeneralAssignments) return;

    setIsExportingGeneralAssignments(true);
    try {
      const selectedAssignments = assignments.filter(a => selectedGeneralAssignmentIds.has(a.id));
      const exportData = await Promise.all(selectedAssignments.map(async (ass) => {
        const statuses = await assignmentService.loadStudentStatuses(user.id, currentClass.id, ass.id);
        const category = categories.find(c => c.id === ass.categoryId);

        const students = sortStudentsForPrint(statuses.map(s => {
          const member = members.find(m => m.id === s.studentId);
          return {
            studentNumber: member?.number,
            studentName: member?.name || s.studentId,
            status: s.status,
            memo: s.memo || ''
          };
        }));

        const due = ass.dueDate ? new Date(ass.dueDate) : undefined;
        const dueText = due ? formatAssignmentDueDate(due) : '마감일 없음';
        const rewardCoins = ass.rewardEnabled ? (ass.rewardCoins || 0) : 0;
        const rewardItemCardCount = ass.rewardEnabled ? (ass.rewardItemCardIds?.length || 0) : 0;

        return {
          assignment: ass,
          categoryName: category?.name || '전체 카테고리',
          dueDateText: dueText,
          basePoints: ass.basePoints || 0,
          rewardCoins,
          rewardItemCardCount,
          students
        };
      }));

      const html = buildGeneralAssignmentsExportHtml(exportData);
      openAssignmentPrintWindow(html);
    } catch (error) {
      console.error('선택 과제 내보내기 실패:', error);
      alert('선택 과제 내보내기에 실패했습니다.');
    } finally {
      setIsExportingGeneralAssignments(false);
    }
  };

  const exportSelectedProjectAssignments = async () => {
    if (!user?.id || !currentClass?.id) return;
    if (selectedProjectAssignmentIds.size === 0) return;
    if (isExportingProjectAssignments) return;

    setIsExportingProjectAssignments(true);
    try {
      const selectedProjects = projectAssignments.filter(p => selectedProjectAssignmentIds.has(p.id));
      const exportData = await Promise.all(selectedProjects.map(async (project) => {
        // 각 하위 과제별로 학생 상태 로드
        const subTasksData = await Promise.all(project.subTasks.map(async (subTask: any) => {
          const statuses = await projectAssignmentService.loadProjectAssignmentStudentStatuses(
            user.id,
            currentClass.id,
            project.id,
            subTask.id,
            currentMemberGroup
          );

          const students = sortStudentsForPrint(statuses.map(s => {
            const member = members.find(m => m.id === s.studentId);
            return {
              studentNumber: member?.number,
              studentName: member?.name || s.studentId,
              status: s.status,
              memo: s.memo || ''
            };
          }));

          return {
            subTaskTitle: subTask.title,
            subTaskPoints: subTask.points,
            students
          };
        }));

        const due = project.dueDate ? new Date(project.dueDate) : undefined;
        const dueText = due ? formatAssignmentDueDate(due) : '마감일 없음';
        const totalPoints = project.subTasks.reduce((sum: number, st: any) => sum + st.points, 0);
        const rewardCoins = project.rewardEnabled ? (project.rewardCoins || 0) : 0;
        const rewardItemCardCount = project.rewardEnabled ? (project.rewardItemCardIds?.length || 0) : 0;

        return {
          project,
          dueDateText: dueText,
          basePoints: totalPoints,
          rewardCoins,
          rewardItemCardCount,
          subTasks: subTasksData
        };
      }));

      const html = buildProjectAssignmentsExportHtml(exportData);
      openAssignmentPrintWindow(html);
    } catch (error) {
      console.error('선택 프로젝트 과제 내보내기 실패:', error);
      alert('선택 프로젝트 과제 내보내기에 실패했습니다.');
    } finally {
      setIsExportingProjectAssignments(false);
    }
  };

  // 미완료 과제 데이터 타입
  interface IncompleteAssignmentItem {
    assignment: Assignment | ProjectAssignment | RoutineAssignment;
    type: 'general' | 'project' | 'routine';
    routineDateId?: string;
    routineDate?: Date;
  }

  interface IncompleteByStudent {
    student: ClassMember;
    incompleteAssignments: IncompleteAssignmentItem[];
  }

  interface IncompleteByAssignment {
    assignment: Assignment | ProjectAssignment | RoutineAssignment;
    type: 'general' | 'project' | 'routine';
    routineDateId?: string;
    routineDate?: Date;
    incompleteStudents: ClassMember[];
  }

  // 미완료 과제 데이터 상태
  const [incompleteByStudentData, setIncompleteByStudentData] = useState<IncompleteByStudent[]>([]);
  const [incompleteByAssignmentData, setIncompleteByAssignmentData] = useState<IncompleteByAssignment[]>([]);
  const [isLoadingIncompleteData, setIsLoadingIncompleteData] = useState(false);
  const [modalOpenTime, setModalOpenTime] = useState<Date | null>(null);
  const [processingToast, setProcessingToast] = useState<string | null>(null);

  // 날짜 포맷 함수
  const formatDueDate = (date: Date | undefined) => {
    if (!date) return '';
    const d = new Date(date);
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    const dayOfWeek = dayNames[d.getDay()];
    return `${month}/${day}(${dayOfWeek})`;
  };

  const formatModalOpenTime = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hour = date.getHours();
    const ampm = hour < 12 ? '오전' : '오후';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${year}년 ${month}월 ${day}일 ${ampm} ${displayHour}시`;
  };

  const buildStudentIncompleteMap = useCallback(async () => {
    if (!user?.id || !currentClass?.id) return new Map<string, IncompleteAssignmentItem[]>();

      const incompleteGeneralAssignments = assignments.filter(a => 
        (a.pendingCount && a.pendingCount > 0) || (a.submittedCount && a.submittedCount > 0)
      );
      
      const incompleteProjectAssignments = projectAssignments.filter(p => 
        p.pendingCount && p.pendingCount > 0
      );

    const studentIncompleteMap: Map<string, IncompleteAssignmentItem[]> = new Map();

      for (const assignment of incompleteGeneralAssignments) {
        const statuses = await assignmentService.loadStudentStatuses(user.id, currentClass.id, assignment.id);
        const incompleteStudentIds = statuses
          .filter(s => s.status === 'pending' || s.status === 'submitted')
          .map(s => s.studentId);
        
        const statusStudentIds = new Set(statuses.map(s => s.studentId));
        const noStatusStudentIds = (assignment.assignedStudentIds || []).filter(id => !statusStudentIds.has(id));
        const allIncompleteIds = [...incompleteStudentIds, ...noStatusStudentIds];
        
        allIncompleteIds.forEach(studentId => {
          if (!studentIncompleteMap.has(studentId)) {
            studentIncompleteMap.set(studentId, []);
          }
          studentIncompleteMap.get(studentId)!.push({ assignment, type: 'general' });
        });
      }

      for (const project of incompleteProjectAssignments) {
        const incompleteStudentIds = (project.assignedStudentIds || []).filter((studentId: string) => {
        return true;
        });
        
        incompleteStudentIds.forEach((studentId: string) => {
          if (!studentIncompleteMap.has(studentId)) {
            studentIncompleteMap.set(studentId, []);
          }
          studentIncompleteMap.get(studentId)!.push({ assignment: project, type: 'project' });
        });
      }

    const routinesIncludedInIncomplete = routineAssignments.filter(r => r.includeInIncompleteCheck === true);
    if (routinesIncludedInIncomplete.length > 0) {
      const consolidatedService = new RoutineAssignmentConsolidatedService(user.id, currentClass.id, currentMemberGroup);
      const routineResults = await Promise.all(
        routinesIncludedInIncomplete.map(async (routine) => {
          const result = await consolidatedService.loadRoutineAssignmentWithStatuses(routine.id);
          return { routine, dateStatuses: result.dateStatuses || [] };
        })
      );

      routineResults.forEach(({ routine, dateStatuses }) => {
        dateStatuses.forEach((dateAssignment: any) => {
          const dateId = dateAssignment?.date;
          if (!dateId) return;

          const statuses = Array.isArray(dateAssignment?.studentStatuses) ? dateAssignment.studentStatuses : [];
          const statusStudentIds = new Set(statuses.map((s: any) => s.studentId));
          const incompleteByStatus = statuses
            .filter((s: any) => s.status === 'pending' || s.status === 'submitted')
            .map((s: any) => s.studentId);
          const missingStatusIds = (routine.assignedStudentIds || []).filter(id => !statusStudentIds.has(id));
          const allIncompleteIds = Array.from(new Set([...incompleteByStatus, ...missingStatusIds]));
          if (allIncompleteIds.length === 0) return;

          const routineDate = new Date(dateId);
          allIncompleteIds.forEach((studentId) => {
            if (!studentIncompleteMap.has(studentId)) {
              studentIncompleteMap.set(studentId, []);
            }
            studentIncompleteMap.get(studentId)!.push({
              assignment: routine,
              type: 'routine',
              routineDateId: dateId,
              routineDate
            });
          });
        });
      });
    }

    return studentIncompleteMap;
  }, [user?.id, currentClass?.id, assignments, projectAssignments, routineAssignments, currentMemberGroup]);

  // 멤버별 미완료 과제 모달 열기
  const handleOpenIncompleteByStudentModal = async () => {
    if (!user?.id || !currentClass?.id) return;
    
    setIsLoadingIncompleteData(true);
    setIsIncompleteByStudentModalOpen(true);
    setModalOpenTime(new Date());
    
    try {
      const studentIncompleteMap = await buildStudentIncompleteMap();

      // 멤버별로 정리
      const result: IncompleteByStudent[] = [];
      members.forEach(member => {
        const incomplete = studentIncompleteMap.get(member.id);
        if (incomplete && incomplete.length > 0) {
          result.push({
            student: member,
            incompleteAssignments: incomplete
          });
        }
      });

      // 번호순 정렬
      result.sort((a, b) => {
        const numA = parseInt(a.student.number) || 0;
        const numB = parseInt(b.student.number) || 0;
        return numA - numB;
      });

      setIncompleteByStudentData(result);
    } catch (error) {
      console.error('미완료 과제 데이터 로드 실패:', error);
    } finally {
      setIsLoadingIncompleteData(false);
    }
  };

  const handleOpenStudentIncompleteModal = useCallback(async (student: StudentOverviewItem) => {
    if (student.pendingCount <= 0) return;
    if (!user?.id || !currentClass?.id) return;

    setSelectedOverviewStudent(student);
    setSelectedStudentIncompleteAssignments([]);
    setIsStudentIncompleteModalOpen(true);
    setIsLoadingIncompleteData(true);

    try {
      const studentIncompleteMap = await buildStudentIncompleteMap();
      const studentItems = studentIncompleteMap.get(student.studentId) || [];
      setSelectedStudentIncompleteAssignments(studentItems);
    } catch (error) {
      console.error('학생별 미완료 과제 로드 실패:', error);
      setSelectedStudentIncompleteAssignments([]);
    } finally {
      setIsLoadingIncompleteData(false);
    }
  }, [user?.id, currentClass?.id, buildStudentIncompleteMap]);

  // 과제별 미완료 멤버 모달 열기
  const handleOpenIncompleteByAssignmentModal = async () => {
    if (!user?.id || !currentClass?.id) return;
    
    setIsLoadingIncompleteData(true);
    setIsIncompleteByAssignmentModalOpen(true);
    setModalOpenTime(new Date());
    
    try {
      const result: IncompleteByAssignment[] = [];

      // 미완료 과제가 있는 일반 과제 처리
      const incompleteGeneralAssignments = assignments.filter(a => 
        (a.pendingCount && a.pendingCount > 0) || (a.submittedCount && a.submittedCount > 0)
      );

      for (const assignment of incompleteGeneralAssignments) {
        const statuses = await assignmentService.loadStudentStatuses(user.id, currentClass.id, assignment.id);
        const incompleteStudentIds = statuses
          .filter(s => s.status === 'pending' || s.status === 'submitted')
          .map(s => s.studentId);
        
        // 배정된 멤버 중 상태가 없는 멤버도 미완료로 처리
        const statusStudentIds = new Set(statuses.map(s => s.studentId));
        const noStatusStudentIds = (assignment.assignedStudentIds || []).filter(id => !statusStudentIds.has(id));
        
        const allIncompleteIds = Array.from(new Set([...incompleteStudentIds, ...noStatusStudentIds]));
        
        const incompleteStudents = members.filter(m => allIncompleteIds.includes(m.id));
        
        if (incompleteStudents.length > 0) {
          result.push({
            assignment,
            type: 'general',
            incompleteStudents: incompleteStudents.sort((a, b) => {
              const numA = parseInt(a.number) || 0;
              const numB = parseInt(b.number) || 0;
              return numA - numB;
            })
          });
        }
      }

      // 미완료 과제가 있는 프로젝트 과제 처리
      const incompleteProjectAssignments = projectAssignments.filter(p => 
        p.pendingCount && p.pendingCount > 0
      );

      for (const project of incompleteProjectAssignments) {
        const incompleteStudents = members.filter(m => 
          (project.assignedStudentIds || []).includes(m.id)
        );
        
        if (incompleteStudents.length > 0) {
          result.push({
            assignment: project,
            type: 'project',
            incompleteStudents: incompleteStudents.sort((a, b) => {
              const numA = parseInt(a.number) || 0;
              const numB = parseInt(b.number) || 0;
              return numA - numB;
            })
          });
        }
      }

      // 미완료 과제가 있는 루틴 날짜 과제 처리 (옵션이 켜진 루틴만 포함)
      const routinesIncludedInIncomplete = routineAssignments.filter(r => r.includeInIncompleteCheck === true);
      if (routinesIncludedInIncomplete.length > 0) {
        const consolidatedService = new RoutineAssignmentConsolidatedService(user.id, currentClass.id, currentMemberGroup);
        const routineResults = await Promise.all(
          routinesIncludedInIncomplete.map(async (routine) => {
            const loaded = await consolidatedService.loadRoutineAssignmentWithStatuses(routine.id);
            return { routine, dateStatuses: loaded.dateStatuses || [] };
          })
        );

        routineResults.forEach(({ routine, dateStatuses }) => {
          dateStatuses.forEach((dateAssignment: any) => {
            const dateId = dateAssignment?.date;
            if (!dateId) return;

            const statuses = Array.isArray(dateAssignment?.studentStatuses) ? dateAssignment.studentStatuses : [];
            const statusStudentIds = new Set(statuses.map((s: any) => s.studentId));
            const incompleteByStatus = statuses
              .filter((s: any) => s.status === 'pending' || s.status === 'submitted')
              .map((s: any) => s.studentId);
            const missingStatusIds = (routine.assignedStudentIds || []).filter(id => !statusStudentIds.has(id));
            const allIncompleteIds = Array.from(new Set([...incompleteByStatus, ...missingStatusIds]));
            if (allIncompleteIds.length === 0) return;

            const incompleteStudents = members.filter(m => allIncompleteIds.includes(m.id));
            if (incompleteStudents.length === 0) return;

            result.push({
              assignment: routine,
              type: 'routine',
              routineDateId: dateId,
              routineDate: new Date(dateId),
              incompleteStudents: incompleteStudents.sort((a, b) => {
                const numA = parseInt(a.number) || 0;
                const numB = parseInt(b.number) || 0;
                return numA - numB;
              })
            });
          });
        });
      }

      setIncompleteByAssignmentData(result);
    } catch (error) {
      console.error('미완료 과제 데이터 로드 실패:', error);
    } finally {
      setIsLoadingIncompleteData(false);
    }
  };

  // 미완료 과제에서 과제 체크 모달 열기
  const handleOpenCheckFromIncomplete = async (
    assignment: Assignment | ProjectAssignment | RoutineAssignment,
    type: 'general' | 'project' | 'routine',
    routineDateId?: string,
    routineDate?: Date
  ) => {
    if (type === 'general') {
      // 기존 모달은 유지하고 과제 체크 모달만 열기
      await handleOpenCheckModal(assignment as Assignment);
    } else if (type === 'project') {
      // 프로젝트 과제 처리
      setSelectedProjectForDetail(assignment);
      // 프로젝트 과제 체크 모달 열기 로직 필요 시 추가
    } else {
      // 루틴 날짜 과제 처리
      const routine = assignment as RoutineAssignment;
      const targetDate = routineDate || (routineDateId ? new Date(routineDateId) : null);
      if (!targetDate) return;
      setActiveTab('routine');
      setSelectedRoutineForDetail(routine);
      setSelectedRoutineAssignment({
        id: routineDateId || `${routine.id}-${targetDate.toISOString().slice(0, 10)}`,
        routineAssignmentId: routine.id,
        date: targetDate,
        studentId: '',
        status: 'pending',
        earnedPoints: 0,
        updatedAt: new Date(),
        isCustomDate: false
      });
      setIsRoutineCheckModalOpen(true);
    }
  };

  const handleOpenCheckFromStudentIncomplete = async (
    assignment: Assignment | ProjectAssignment | RoutineAssignment,
    type: 'general' | 'project' | 'routine',
    routineDateId?: string,
    routineDate?: Date
  ) => {
    // 기존 모달은 유지하고 선택한 과제의 체크 모달만 추가로 연다.
    await handleOpenCheckFromIncomplete(assignment, type, routineDateId, routineDate);
  };

  // PDF 저장 함수
  const handleSaveAsPdf = async (modalType: 'byStudent' | 'byAssignment') => {
    const content = incompleteModalContentRef.current;
    if (!content) return;

    setProcessingToast('다운로드 중...');

    try {
      // @ts-ignore
      const html2canvasModule = await import('html2canvas');
      const html2canvas = html2canvasModule.default;
      // @ts-ignore
      const jsPDFModule = await import('jspdf');
      const jsPDF = jsPDFModule.default;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const usableWidth = pdfWidth - (margin * 2);
      // 날짜 헤더와 각 행을 개별적으로 캡처
      const dateHeader = content.querySelector('[data-pdf-element="header"]') as HTMLElement;
      const rows = content.querySelectorAll('[data-pdf-element="row"]');
      
      // 라이트 모드 컨테이너 생성 (화면 밖에 배치)
      const lightModeContainer = document.createElement('div');
      lightModeContainer.setAttribute('data-theme', 'light');
      lightModeContainer.style.position = 'absolute';
      lightModeContainer.style.left = '-9999px';
      lightModeContainer.style.top = '0';
      lightModeContainer.style.width = content.offsetWidth + 'px';
      document.body.appendChild(lightModeContainer);
      
      let currentY = margin;
      
      try {
      // 날짜 헤더 캡처
      if (dateHeader) {
          const clonedHeader = dateHeader.cloneNode(true) as HTMLElement;
          lightModeContainer.appendChild(clonedHeader);
          
          await new Promise(resolve => setTimeout(resolve, 100));
          
          const headerCanvas = await html2canvas(clonedHeader, {
          scale: 2,
          useCORS: true,
          logging: false,
        } as any);
        
        const headerWidth = headerCanvas.width / 2;
        const headerHeight = headerCanvas.height / 2;
        const headerRatio = usableWidth / headerWidth;
        const scaledHeaderHeight = headerHeight * headerRatio;
        
        const headerImgData = headerCanvas.toDataURL('image/png');
        pdf.addImage(headerImgData, 'PNG', margin, currentY, usableWidth, scaledHeaderHeight);
        currentY += scaledHeaderHeight + 5;
      }
      
      // 각 행 캡처
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i] as HTMLElement;
          const clonedRow = row.cloneNode(true) as HTMLElement;
          lightModeContainer.appendChild(clonedRow);
        
          await new Promise(resolve => setTimeout(resolve, 50));
          
          const rowCanvas = await html2canvas(clonedRow, {
          scale: 2,
          useCORS: true,
          logging: false,
        } as any);
        
        const rowWidth = rowCanvas.width / 2;
        const rowHeight = rowCanvas.height / 2;
        const rowRatio = usableWidth / rowWidth;
        const scaledRowHeight = rowHeight * rowRatio;
        
        // 현재 페이지에 공간이 부족하면 새 페이지 추가
        if (currentY + scaledRowHeight > pdfHeight - margin) {
          pdf.addPage();
          currentY = margin;
        }
        
        const rowImgData = rowCanvas.toDataURL('image/png');
        pdf.addImage(rowImgData, 'PNG', margin, currentY, usableWidth, scaledRowHeight);
        currentY += scaledRowHeight + 3;
      }
      
      const fileName = modalType === 'byStudent' 
        ? `멤버별_미완료_과제_${new Date().toLocaleDateString('ko-KR')}.pdf`
        : `과제별_미완료_멤버_${new Date().toLocaleDateString('ko-KR')}.pdf`;
      
      pdf.save(fileName);
      } finally {
        // 복제본 제거
        if (lightModeContainer.parentNode) {
          document.body.removeChild(lightModeContainer);
        }
      }
      
      setProcessingToast(null);
    } catch (error) {
      console.error('PDF 저장 실패:', error);
      setProcessingToast(null);
      alert('PDF 저장에 실패했습니다.');
    }
  };

  // 프린트 함수
  const handlePrint = async () => {
    const content = incompleteModalContentRef.current;
    if (!content) return;

    setProcessingToast('프린트 페이지 생성 중...');

    try {
      // @ts-ignore
      const html2canvasModule = await import('html2canvas');
      const html2canvas = html2canvasModule.default;
      
      // 날짜 헤더와 각 행을 개별적으로 캡처
      const dateHeader = content.querySelector('[data-pdf-element="header"]') as HTMLElement;
      const rows = content.querySelectorAll('[data-pdf-element="row"]');
      
      const images: string[] = [];
      const clonedElements: HTMLElement[] = [];
      
      // 라이트 모드 컨테이너 생성 (화면 밖에 배치)
      const lightModeContainer = document.createElement('div');
      lightModeContainer.setAttribute('data-theme', 'light');
      lightModeContainer.style.position = 'absolute';
      lightModeContainer.style.left = '-9999px';
      lightModeContainer.style.top = '0';
      lightModeContainer.style.width = content.offsetWidth + 'px';
      document.body.appendChild(lightModeContainer);
      
      try {
      // 날짜 헤더 캡처
      if (dateHeader) {
          const clonedHeader = dateHeader.cloneNode(true) as HTMLElement;
          lightModeContainer.appendChild(clonedHeader);
          clonedElements.push(clonedHeader);
          
          await new Promise(resolve => setTimeout(resolve, 100));
          
          const headerCanvas = await html2canvas(clonedHeader, {
          scale: 2,
          useCORS: true,
          logging: false,
        } as any);
        images.push(headerCanvas.toDataURL('image/png'));
      }
      
      // 각 행 캡처
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i] as HTMLElement;
          const clonedRow = row.cloneNode(true) as HTMLElement;
          lightModeContainer.appendChild(clonedRow);
          clonedElements.push(clonedRow);
          
          await new Promise(resolve => setTimeout(resolve, 50));
          
          const rowCanvas = await html2canvas(clonedRow, {
          scale: 2,
          useCORS: true,
          logging: false,
        } as any);
        images.push(rowCanvas.toDataURL('image/png'));
        }
      } finally {
        // 복제본 제거
        document.body.removeChild(lightModeContainer);
      }
      
      setProcessingToast(null);

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('팝업 차단을 해제해주세요.');
        return;
      }

      const imagesHtml = images.map(img => `<img src="${img}" />`).join('');

      printWindow.document.write(`
        <html>
          <head>
            <title>미완료 과제 목록</title>
            <style>
              @media print {
                body { margin: 0; padding: 10mm; background: white; }
                img { 
                  max-width: 100%; 
                  height: auto; 
                  page-break-inside: avoid;
                  margin-bottom: 3mm;
                }
              }
              body { 
                margin: 0; 
                padding: 20px; 
                background: white;
              }
              img { 
                max-width: 100%; 
                height: auto; 
                display: block;
                margin-bottom: 8px;
              }
            </style>
          </head>
          <body>
            ${imagesHtml}
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    } catch (error) {
      console.error('프린트 실패:', error);
      setProcessingToast(null);
      alert('프린트에 실패했습니다.');
      
      // 에러 발생 시에도 원래 테마로 복원
      const htmlElement = document.documentElement;
      const originalTheme = htmlElement.getAttribute('data-theme');
      if (originalTheme !== 'light') {
        if (originalTheme) {
          htmlElement.setAttribute('data-theme', originalTheme);
        } else {
          htmlElement.removeAttribute('data-theme');
        }
      }
    }
  };

  // 과제 체크 저장
  const handleSaveCheckResults = async (newStudentStatuses: AssignmentStudentStatus[]) => {
    if (!user?.id || !currentClass?.id || !selectedAssignment) return;
    
    try {
      // 새로운 최적화된 서비스 사용
      const studentUpdates = newStudentStatuses.map(status => {
        const update: any = {
          studentId: status.studentId,
          status: status.status,
          earnedPoints: status.earnedPoints,
          memo: status.memo || ''
        };
        
        // 제출 확인 데이터 포함 (있는 경우)
        if (status.hasOwnProperty('submissionLink')) {
          update.submissionLink = status.submissionLink;
        }
        if (status.hasOwnProperty('submissionMessage')) {
          update.submissionMessage = status.submissionMessage;
        }
        if (status.hasOwnProperty('submittedAt')) {
          update.submittedAt = status.submittedAt;
        }
        if (status.hasOwnProperty('submissionImagePath')) {
          update.submissionImagePath = (status as any).submissionImagePath;
        }
        if (status.hasOwnProperty('submissionImageUpdatedAt')) {
          update.submissionImageUpdatedAt = (status as any).submissionImageUpdatedAt;
        }
        if (status.hasOwnProperty('submissionImageSizeBytes')) {
          update.submissionImageSizeBytes = (status as any).submissionImageSizeBytes;
        }
        
        return update;
      });
      
      await assignmentService.batchUpdateStudentStatusesOptimized(
        user.id, 
        currentClass.id, 
        selectedAssignment.id, 
        studentUpdates
      );
      
      // 전체 studentStatuses를 업데이트 (기존 상태와 새로운 상태를 병합)
      let mergedAfterSave: AssignmentStudentStatus[] = [];
      setStudentStatuses(prevStatuses => {
        mergedAfterSave = [...prevStatuses];
        newStudentStatuses.forEach(newStatus => {
          const existingIndex = mergedAfterSave.findIndex(s => s.studentId === newStatus.studentId);
          if (existingIndex >= 0) {
            mergedAfterSave[existingIndex] = newStatus;
          } else {
            mergedAfterSave.push(newStatus);
          }
        });
        return mergedAfterSave;
      });

      // 변경분만 넘어오므로, 병합된 전체 기준으로 카운트 (일부만 저장 시에도 블럭 표시가 맞음)
      const pendingCount = mergedAfterSave.filter(
        status => status.status === 'pending' || status.status === 'submitted'
      ).length;
      const submittedCount = mergedAfterSave.filter(status => status.status === 'submitted').length;
      pendingCountUpdateRef.current[selectedAssignment.id] = pendingCount;
      setAssignments(prev => prev.map(ass =>
        ass.id === selectedAssignment.id
          ? { ...ass, pendingCount, submittedCount, lastPendingUpdate: new Date(), lastSubmittedUpdate: new Date() }
          : ass
      ));
      
      // loadData() 호출 제거 - 이미 pendingCount가 업데이트되어 있음
      
      // 성공 메시지는 AssignmentCheckModal에서 팝업으로 표시됨
    } catch (error) {
      console.error('과제 체크 결과 저장 실패:', error);
      // 저장 실패 시 에러 로그만 남기고 사용자에게는 팝업으로 표시하지 않음
    }
  };

  const loadStudentOverviewData = useCallback(async () => {
    if (!user?.id || !currentClass?.id || members.length === 0) {
      setStudentOverviewData([]);
      return;
    }

    setStudentOverviewLoading(true);
    try {
      const { PointsQueryService } = await import('../services/pointsQueryService');
      const pointsQueryService = new PointsQueryService(user.id, currentClass.id, currentMemberGroup);
      const pointsTableData = await pointsQueryService.getPointsTableData(members);
      const historyMap = new Map(pointsTableData.students.map((s) => [s.studentId, s]));

      // 학생 블럭의 "미완료" 카운트는 멤버별 미완료 모달과 동일하게
      // 루틴과제는 `includeInIncompleteCheck` 옵션이 ON일 때만 날짜과제의 미확인(미완료) 상태를 카운팅합니다.
      const routinesIncludedInIncomplete = routineAssignments.filter(r => r.includeInIncompleteCheck === true);
      const studentRoutineIncompleteCountMap: Record<string, number> = {};

      if (routinesIncludedInIncomplete.length > 0) {
        const consolidatedService = new RoutineAssignmentConsolidatedService(user.id, currentClass.id, currentMemberGroup);
        const routineResults = await Promise.all(
          routinesIncludedInIncomplete.map(async (routine) => {
            const result = await consolidatedService.loadRoutineAssignmentWithStatuses(routine.id);
            return { routine, dateStatuses: result.dateStatuses || [] };
          })
        );

        routineResults.forEach(({ routine, dateStatuses }) => {
          dateStatuses.forEach((dateAssignment: any) => {
            const dateId = dateAssignment?.date;
            if (!dateId) return;

            const statuses = Array.isArray(dateAssignment?.studentStatuses) ? dateAssignment.studentStatuses : [];
            const statusStudentIds = new Set(statuses.map((s: any) => s.studentId));
            const incompleteByStatus = statuses
              .filter((s: any) => s.status === 'pending' || s.status === 'submitted')
              .map((s: any) => s.studentId);
            const missingStatusIds = (routine.assignedStudentIds || []).filter((id: string) => !statusStudentIds.has(id));
            const allIncompleteIds = Array.from(new Set([...incompleteByStatus, ...missingStatusIds]));

            if (allIncompleteIds.length === 0) return;

            // 모달과 동일: 날짜과제(1개)에 대해 미완료 학생이면 각각 카운트 +1
            allIncompleteIds.forEach((studentId: string) => {
              studentRoutineIncompleteCountMap[studentId] = (studentRoutineIncompleteCountMap[studentId] || 0) + 1;
            });
          });
        });
      }

      const currentDate = new Date();
      const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      const availableMonths = new Set<string>();
      members.forEach((member: any) => {
        if (member.seatPositions && typeof member.seatPositions === 'object') {
          Object.keys(member.seatPositions).forEach((month: string) => availableMonths.add(month));
        }
      });
      let selectedMonth = currentMonth;
      if (!availableMonths.has(currentMonth)) {
        const sortedMonths = Array.from(availableMonths).sort().reverse();
        if (sortedMonths.length > 0) selectedMonth = sortedMonths[0];
      }

      const data: StudentOverviewItem[] = members
        .map((member) => {
          const history = historyMap.get(member.id);
          const assignmentRecords = history?.assignments || [];
          const pendingCountNonRoutine = assignmentRecords.filter((a: any) =>
            a.type !== 'routine' && (a.status === 'pending' || a.status === 'submitted')
          ).length;

          const pendingCountRoutine = studentRoutineIncompleteCountMap[member.id] || 0;

          const pendingCount = pendingCountNonRoutine + pendingCountRoutine;
          const confirmedCount = assignmentRecords.filter((a: any) => a.status === 'confirmed').length;
          const seatPosition = (member as any)?.seatPositions?.[selectedMonth];
          return {
            studentId: member.id,
            studentName: member.name,
            studentNumber: Number(member.number) || 0,
            profileImage: history?.profileImage || (member as any).profileImage || 'basic-0',
            pendingCount,
            confirmedCount,
            seatRow: typeof seatPosition?.row === 'number' ? seatPosition.row : -1,
            seatCol: typeof seatPosition?.col === 'number' ? seatPosition.col : -1
          };
        })
        .sort((a, b) => a.studentNumber - b.studentNumber);

      setStudentOverviewData(data);
    } catch (error) {
      console.error('학생 블럭 조회 데이터 로드 실패:', error);
      setStudentOverviewData([]);
    } finally {
      setStudentOverviewLoading(false);
    }
  }, [user?.id, currentClass?.id, currentMemberGroup, members, routineAssignments]);

  const handleOpenStudentOverview = useCallback(async () => {
    setIsStudentOverviewOpen(true);
    await loadStudentOverviewData();
  }, [loadStudentOverviewData]);

  const handleCloseStudentOverview = useCallback(() => {
    setIsStudentOverviewOpen(false);
  }, []);

  const resolveOverviewProfileByStudentId = useCallback((studentId: string, fallbackProfileImage?: string) => {
    const member = members.find((m) => m.id === studentId);
    if (member) {
      return resolveMemberProfileSource(member, memberProfileDisplayMode);
    }
    return fallbackProfileImage || 'basic-0';
  }, [members, memberProfileDisplayMode]);

  const sortedStudentOverviewData = [...studentOverviewData].sort((a, b) => {
    if (studentOverviewMode === 'seat') {
      const aAssigned = a.seatRow >= 0 && a.seatCol >= 0;
      const bAssigned = b.seatRow >= 0 && b.seatCol >= 0;

      if (aAssigned !== bAssigned) {
        return aAssigned ? -1 : 1;
      }
      if (aAssigned && bAssigned) {
        if (a.seatRow !== b.seatRow) return a.seatRow - b.seatRow;
        if (a.seatCol !== b.seatCol) return a.seatCol - b.seatCol;
      }
    }

    if (a.studentNumber !== b.studentNumber) {
      return a.studentNumber - b.studentNumber;
    }
    return a.studentName.localeCompare(b.studentName, 'ko');
  });
  const seatAssignedStudents = sortedStudentOverviewData.filter((student) => student.seatRow >= 0 && student.seatCol >= 0);
  const seatUnassignedStudents = sortedStudentOverviewData.filter((student) => student.seatRow < 0 || student.seatCol < 0);
  /** AssignmentCardPage 자리모드 seatModeData 와 동일한 그리드 계산 */
  const STUDENT_OVERVIEW_SEAT_ROTATE_MAX = 15;
  const studentOverviewSeatLayout = useMemo(() => {
    const assigned = seatAssignedStudents.map((s) => ({
      student: s,
      seatRow: isStudentOverviewSeatRotated ? STUDENT_OVERVIEW_SEAT_ROTATE_MAX - s.seatRow : s.seatRow,
      seatCol: isStudentOverviewSeatRotated ? STUDENT_OVERVIEW_SEAT_ROTATE_MAX - s.seatCol : s.seatCol
    }));

    let minRow = 0;
    let maxRow = STUDENT_OVERVIEW_SEAT_ROTATE_MAX;
    let minCol = 0;
    let maxCol = STUDENT_OVERVIEW_SEAT_ROTATE_MAX;
    if (assigned.length > 0) {
      const rows = assigned.map((a) => a.seatRow);
      const cols = assigned.map((a) => a.seatCol);
      minRow = Math.max(0, Math.min(...rows));
      maxRow = Math.min(STUDENT_OVERVIEW_SEAT_ROTATE_MAX, Math.max(...rows));
      minCol = Math.max(0, Math.min(...cols));
      maxCol = Math.min(STUDENT_OVERVIEW_SEAT_ROTATE_MAX, Math.max(...cols));
    }

    const rowCount = Math.max(2, maxRow - minRow + 2);
    const colCount = Math.max(2, maxCol - minCol + 2);
    const usedGridRows = new Set<number>();
    const usedGridCols = new Set<number>();
    assigned.forEach(({ seatRow, seatCol }) => {
      const rowIndex = seatRow - minRow;
      const colIndex = seatCol - minCol;
      [rowIndex, rowIndex + 1].forEach((idx) => {
        if (idx >= 0 && idx < rowCount) usedGridRows.add(idx);
      });
      [colIndex, colIndex + 1].forEach((idx) => {
        if (idx >= 0 && idx < colCount) usedGridCols.add(idx);
    });
  });

    const rowTemplate = Array.from(
      { length: rowCount },
      (_, idx) => (usedGridRows.has(idx) ? '43px' : '21px')
  ).join(' ');
    const colTemplate = Array.from(
      { length: colCount },
      (_, idx) => (usedGridCols.has(idx) ? '92px' : '46px')
  ).join(' ');

    return { minRow, minCol, rowTemplate, colTemplate, assigned };
  }, [seatAssignedStudents, isStudentOverviewSeatRotated]);

  return (
    <PageContainer>
      <Header
        user={user}
        onLogout={onLogout}
        onNavigate={onNavigate}
        currentPage="assignments"
        onUpdateUser={onUpdateUser}
        onProfileModalOpen={() => setIsProfileModalOpen(true)}
        onClassManageModalOpen={onClassManageModalOpen}
        classes={classes}
        currentClass={currentClass}
        memberGroups={memberGroups}
        currentMemberGroup={currentMemberGroup}
        onMemberGroupChange={onMemberGroupChange}
        themeMode={themeMode}
        onToggleTheme={onToggleTheme}
        onSeatAssignmentClick={onSeatAssignmentClick}
        onLevelSettingsClick={onLevelSettingsClick}
        onPointManagementClick={onPointManagementClick}
        onItemCardSettingsClick={onItemCardSettingsClick}
        onAssignmentSettingsClick={onAssignmentSettingsClick}
      />

      <Content>
        {/* 원형 버튼 영역: 왼쪽(테이블/카드) | 오른쪽(미완료) */}
        <TopButtonRow>
          {/* 과제 관리 테이블/카드 — 카테고리 왼쪽 상단, 왼쪽 정렬 */}
          <TableButtonContainer>
            <AssignmentsTooltipWrapper data-tooltip="과제 관리 테이블">
              <TableCardMainButton onClick={onNavigateToTable} aria-label="과제 관리 테이블">
                <img src="/tablecheck.svg" alt="" />
              </TableCardMainButton>
            </AssignmentsTooltipWrapper>
            <AssignmentsTooltipWrapper data-tooltip="과제 관리 카드">
              <TableCardMainButton onClick={onNavigateToCard} aria-label="과제 관리 카드">
                <img src="/cardcheck.svg" alt="" />
              </TableCardMainButton>
            </AssignmentsTooltipWrapper>
          </TableButtonContainer>

          {/* 중앙 버튼: 학생 블럭 조회 화면 진입 */}
          <CenterButtonContainer>
            <AssignmentsTooltipWrapper data-tooltip="멤버별 과제 현황">
              <TableCardMainButton onClick={handleOpenStudentOverview} aria-label="멤버별 과제 현황">
                <img src="/ProfileCard.svg" alt="" />
              </TableCardMainButton>
            </AssignmentsTooltipWrapper>
          </CenterButtonContainer>

          {/* 미완료 과제 확인 — 카테고리 오른쪽 상단, 오른쪽 정렬 */}
          <IncompleteButtonsContainer>
            <AssignmentsTooltipWrapper data-tooltip="멤버별 미완료 과제">
              <IncompleteCircularButton
                onClick={handleOpenIncompleteByStudentModal}
                aria-label="멤버별 미완료 과제"
              >
                <img src="/membersprint.svg" alt="" />
              </IncompleteCircularButton>
            </AssignmentsTooltipWrapper>
            <AssignmentsTooltipWrapper data-tooltip="과제별 미완료 멤버">
              <IncompleteCircularButton
                onClick={handleOpenIncompleteByAssignmentModal}
                aria-label="과제별 미완료 멤버"
              >
                <img src="/tasksprint.svg" alt="" />
              </IncompleteCircularButton>
            </AssignmentsTooltipWrapper>
          </IncompleteButtonsContainer>
        </TopButtonRow>

        {/* 탭 구조 추가 */}
        <TabContainer>
          <Tab 
            $active={activeTab === 'assignments'} 
            onClick={() => {
              console.log('📋 과제 관리 탭 클릭 - activeTab을 assignments로 설정');
              setActiveTab('assignments');
            }}
          >
            <span className="desktop-text">일반 과제 관리</span>
            <span className="mobile-text">일반</span>
          </Tab>
          <Tab 
            $active={activeTab === 'project'} 
            onClick={() => {
              console.log('📂 프로젝트 과제 관리 탭 클릭 - activeTab을 project로 설정');
              setActiveTab('project');
            }}
          >
            <span className="desktop-text">프로젝트 과제 관리</span>
            <span className="mobile-text">프로젝트</span>
          </Tab>
          <Tab 
            $active={activeTab === 'routine'} 
            onClick={() => {
              console.log('🔄 루틴 과제 관리 탭 클릭 - activeTab을 routine으로 설정');
              setActiveTab('routine');
            }}
          >
            <span className="desktop-text">루틴 과제 관리</span>
            <span className="mobile-text">루틴</span>
          </Tab>
        </TabContainer>

        {/* 일반 과제 탭 */}
        {activeTab === 'assignments' && (
          <>
            <TopBar>
              <Title>과제 관리</Title>
              <TopBarRightActions>
                <CircleToggleButton
                  type="button"
                  onClick={toggleGeneralSelectionMode}
                  title={isGeneralSelectionMode ? '선택모드 취소' : '선택모드'}
                  aria-pressed={isGeneralSelectionMode}
                  $active={isGeneralSelectionMode}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </CircleToggleButton>
                <IconActionButton
                  type="button"
                  onClick={() => exportSelectedGeneralAssignments()}
                  disabled={!isGeneralSelectionMode || selectedGeneralAssignmentIds.size === 0 || isExportingGeneralAssignments}
                  title="프린트"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" stroke="currentColor" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
                  </svg>
                </IconActionButton>
              </TopBarRightActions>
            </TopBar>

            {/* 2행: 필터/정렬/카테고리 드롭다운 (3영역: 전체·미완료 | 생성일·마감일·제목 | 카테고리) */}
            <FilterRow>
              <FilterSelectContainer>
                <FilterSelectButton 
                  onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                  type="button"
                >
                  <FilterOption>
                    <span>
                      {filterType === 'all' ? '전체 과제' : '미완료 과제'}
                      {' · '}
                      {generalSortBy === 'createdAt' ? '생성일' : generalSortBy === 'dueDate' ? '마감일' : '제목'}
                      {' · '}
                      {filterCategoryId === '__default__' ? '기본 카테고리' :
                        filterCategoryId ? (categories.find(c => c.id === filterCategoryId)?.name || '전체 카테고리') : '전체 카테고리'}
                    </span>
                  </FilterOption>
                </FilterSelectButton>
                
                {isFilterDropdownOpen && (
                  <>
                    <FilterDropdownOverlay onClick={() => setIsFilterDropdownOpen(false)} />
                    <FilterDropdown>
                      {/* 1: 전체과제 / 미완료과제 */}
                      <FilterOption 
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilterType('all');
                          setIsFilterDropdownOpen(false);
                        }}
                      >
                        <span>전체과제</span>
                      </FilterOption>
                      <FilterOption 
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilterType('incomplete');
                          setIsFilterDropdownOpen(false);
                        }}
                      >
                        <span>미완료과제</span>
                      </FilterOption>
                      <FilterDivider />
                      {/* 2: 생성일 / 마감일 / 제목 */}
                      <FilterOption 
                        onClick={(e) => {
                          e.stopPropagation();
                          setGeneralSortBy('createdAt');
                          setIsFilterDropdownOpen(false);
                        }}
                      >
                        <span>생성일</span>
                      </FilterOption>
                      <FilterOption 
                        onClick={(e) => {
                          e.stopPropagation();
                          setGeneralSortBy('dueDate');
                          setIsFilterDropdownOpen(false);
                        }}
                      >
                        <span>마감일</span>
                      </FilterOption>
                      <FilterOption 
                        onClick={(e) => {
                          e.stopPropagation();
                          setGeneralSortBy('title');
                          setIsFilterDropdownOpen(false);
                        }}
                      >
                        <span>제목</span>
                      </FilterOption>
                      <FilterDivider />
                      {/* 3: 전체 카테고리 / 기본 카테고리 / 카테고리 목록 */}
                      <FilterOption 
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilterCategoryId('');
                          setIsFilterDropdownOpen(false);
                        }}
                      >
                        <ColorDot color="var(--color-border)" />
                        <span>전체 카테고리</span>
                      </FilterOption>
                      <FilterOption 
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilterCategoryId('__default__');
                          setIsFilterDropdownOpen(false);
                        }}
                      >
                        <ColorDot color="var(--color-border)" />
                        <span>기본 카테고리</span>
                      </FilterOption>
                      {categories.map(cat => (
                        <FilterOption 
                          key={cat.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilterCategoryId(cat.id);
                            setIsFilterDropdownOpen(false);
                          }}
                        >
                          <ColorDot color={cat.color} />
                          <span>{cat.name}</span>
                        </FilterOption>
                      ))}
                    </FilterDropdown>
                  </>
                )}
              </FilterSelectContainer>
              
            </FilterRow>

        <BlocksContainer>
          {/* 🎯 페이지네이션 버튼 (첫번째 블럭 위) */}
          {(() => {
            const { totalPages } = getGeneralAssignmentPagination();
            return totalPages > 1 && (
              <PaginationContainer>
                <PaginationButton 
                  onClick={() => handleGeneralAssignmentPageChange(generalAssignmentCurrentPage - 1)} 
                  disabled={generalAssignmentCurrentPage === 1}
                >
                  &lt;
                </PaginationButton>
                <PageInfo>
                  {generalAssignmentCurrentPage}/{totalPages}
                </PageInfo>
                <PaginationButton 
                  onClick={() => handleGeneralAssignmentPageChange(generalAssignmentCurrentPage + 1)} 
                  disabled={generalAssignmentCurrentPage === totalPages}
                >
                  &gt;
                </PaginationButton>
              </PaginationContainer>
            );
          })()}

          {(() => {
            const { currentPageAssignments } = getGeneralAssignmentPagination();
            
            return currentPageAssignments.length > 0 ? (
              currentPageAssignments.map(ass => {
              const category = categories.find(c => c.id === ass.categoryId);
              const isSelected = selectedGeneralAssignmentIds.has(ass.id);
              return (
              <AssignmentBlock
                key={ass.id}
                assignment={ass}
                onCopy={() => handleOpenNewAssignmentModal('copy', ass)}
                onUpdated={() => handleOpenNewAssignmentModal('edit', ass)}
                onDelete={async () => {
                  setAssignmentToDelete(ass);
                  setIsDeleteConfirmModalOpen(true);
                }}
                onCheck={() => handleOpenCheckModal(ass)}
                categoryName={category?.name}
                categoryColor={category?.color}
                onMenuToggle={(id) => {
                  if (openMenuId === id) {
                    setOpenMenuId(null);
                  } else {
                    // 햄버거 버튼의 위치를 계산하여 메뉴 위치 설정
                    const button = document.querySelector(`[data-assignment-id="${id}"] .hamburger-button`) as HTMLElement;
                    if (button) {
                      const rect = button.getBoundingClientRect();
                      setMenuPosition({
                        top: rect.bottom + 4, // viewport 기준
                        left: rect.right - 160 // 메뉴 너비만큼 왼쪽으로 이동
                      });
                    }
                    setOpenMenuId(id);
                  }
                }}
                isMenuOpen={openMenuId === ass.id}
                selectionMode={isGeneralSelectionMode}
                isSelected={isSelected}
                onSelectToggle={() => toggleGeneralAssignmentSelected(ass.id)}
              />
              );
            })
          ) : (
            <EmptyState>
              <EmptyStateTitle>과제가 없습니다</EmptyStateTitle>
              <EmptyStateText>
                아직 생성된 과제가 없습니다.
                <br />
                첫 번째 일반 과제를 생성해보세요!
              </EmptyStateText>
            </EmptyState>
            );
          })()}
        </BlocksContainer>
        
        {/* 햄버거 메뉴 드롭다운 */}
        {openMenuId && (
          <>
            <MenuDropdownOverlay onClick={() => setOpenMenuId(null)} />
            <MenuDropdown style={{
              top: menuPosition.top,
              left: menuPosition.left
            }}>
              <MenuDropdownOption onClick={() => {
                const assignment = assignments.find(a => a.id === openMenuId);
                if (assignment) {
                  handleOpenNewAssignmentModal('edit', assignment);
                  setOpenMenuId(null);
                }
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09A1.65 1.65 0 0 0 19.4 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                과제 설정
              </MenuDropdownOption>
              <MenuDropdownOption onClick={() => {
                const assignment = assignments.find(a => a.id === openMenuId);
                if (assignment) {
                  handleOpenNewAssignmentModal('copy', assignment);
                  setOpenMenuId(null);
                }
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 4v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7.242a2 2 0 0 0-.602-1.43L16.083 2.57A2 2 0 0 0 14.685 2H10a2 2 0 0 0-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 18v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                과제 복제
              </MenuDropdownOption>
              <MenuDropdownOption onClick={() => {
                const assignment = assignments.find(a => a.id === openMenuId);
                if (assignment) {
                  setAssignmentToDelete(assignment);
                  setIsDeleteConfirmModalOpen(true);
                  setOpenMenuId(null);
                }
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                과제 삭제
              </MenuDropdownOption>
            </MenuDropdown>
          </>
        )}
        
        {/* 플로팅 액션 버튼 */}
        <FloatingActionButton onClick={() => handleOpenNewAssignmentModal('new')}>
          <PlusIcon>+</PlusIcon>
        </FloatingActionButton>
        
          </>
        )}

        {/* 루틴 과제 탭 내용 */}
        {activeTab === 'routine' && (
          <>
            <TopBar>
              <Title>루틴 과제 관리</Title>
            </TopBar>
            
            {/* 2행: 루틴 과제 필터 */}
            <RoutineFiltersContainer>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                {/* 필터 타입 (전체/미완료) */}
                <FilterSelectContainer>
                  <FilterSelectButton 
                    onClick={() => setIsRoutineFilterDropdownOpen(!isRoutineFilterDropdownOpen)}
                    type="button"
                  >
                    <FilterOption>
                      <ColorDot color="var(--color-border)" />
                      <span>
                        {routineFilterType === 'all' ? '전체 과제' : '미완료 과제'}
                        {routineSortBy === 'createdAt' ? ' · 생성일' : 
                         routineSortBy === 'dueDate' ? ' · 마감일' : 
                         routineSortBy === 'title' ? ' · 제목' : ''}
                      </span>
                    </FilterOption>
                  </FilterSelectButton>
                  
                  {isRoutineFilterDropdownOpen && (
                    <>
                      <FilterDropdownOverlay onClick={() => setIsRoutineFilterDropdownOpen(false)} />
                      <FilterDropdown>
                        <FilterOption
                          onClick={(e) => {
                            e.stopPropagation();
                            setRoutineFilterType('all');
                            setIsRoutineFilterDropdownOpen(false);
                          }}
                        >
                          <span>전체 과제</span>
                        </FilterOption>
                        <FilterOption 
                          onClick={(e) => {
                            e.stopPropagation();
                            setRoutineFilterType('incomplete');
                            setIsRoutineFilterDropdownOpen(false);
                          }}
                        >
                          <span>미완료 과제</span>
                        </FilterOption>
                        <FilterDivider />
                        <FilterOption 
                          onClick={() => {
                            setRoutineSortBy('createdAt');
                            setIsRoutineFilterDropdownOpen(false);
                          }}
                        >
                          <ColorDot color="var(--color-border)" />
                          <span>생성일</span>
                        </FilterOption>
                        <FilterOption 
                          onClick={() => {
                            setRoutineSortBy('dueDate');
                            setIsRoutineFilterDropdownOpen(false);
                          }}
                        >
                          <ColorDot color="var(--color-border)" />
                          <span>마감일</span>
                        </FilterOption>
                        <FilterOption 
                          onClick={() => {
                            setRoutineSortBy('title');
                            setIsRoutineFilterDropdownOpen(false);
                          }}
                        >
                          <ColorDot color="var(--color-border)" />
                          <span>제목</span>
                        </FilterOption>
                      </FilterDropdown>
                    </>
                  )}
                </FilterSelectContainer>
                
              </div>
            </RoutineFiltersContainer>

            <BlocksContainer>
              {/* 모든 루틴 과제 표시 */}
              {(() => {
                const toMs = (v: any) => {
                  const d = v?.toDate ? v.toDate() : new Date(v);
                  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
                };
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                return [...routineAssignments]
                  .filter(routine => {
                    if (routineFilterType === 'incomplete') {
                      const dueDate = new Date(routine.dueDate);
                      dueDate.setHours(0, 0, 0, 0);
                      return dueDate >= today;
                    }
                    return true;
                  })
                  .sort((a, b) => {
                    switch (routineSortBy) {
                      case 'createdAt':
                        // 생성일·시·분·초 전체 타임스탬프 기준, 최근 생성이 위로
                        const createdDiff = createdAtToMs(b.createdAt) - createdAtToMs(a.createdAt);
                        if (createdDiff !== 0) return createdDiff;
                        return (a.id || '').localeCompare(b.id || '');
                      case 'dueDate':
                        return toMs(b.dueDate) - toMs(a.dueDate);
                      case 'title':
                        return a.title.localeCompare(b.title);
                      default:
                        return 0;
                    }
                  })
                  .map(routine => {
                  return (
                    <React.Fragment key={routine.id}>
                      <RoutineAssignmentBlock
                        routine={routine}
                        onDelete={handleRoutineDelete}
                        onEdit={handleRoutineEdit}
                        onCheck={handleRoutineCheck}
                        onCopy={handleCopyRoutineAssignment}
                        onMenuToggle={(routineId) => {
                          if (openRoutineMenuId === routineId) {
                            setOpenRoutineMenuId(null);
                          } else {
                            // 햄버거 버튼의 위치를 계산하여 메뉴 위치 설정
                            const button = document.querySelector(`[data-routine-id="${routineId}"] .hamburger-button`) as HTMLElement;
                            if (button) {
                              const rect = button.getBoundingClientRect();
                              setRoutineMenuPosition({
                                top: rect.bottom + 4, // viewport 기준
                                left: rect.right - 160 // 메뉴 너비만큼 왼쪽으로 이동
                              });
                            }
                            setOpenRoutineMenuId(routineId);
                          }
                        }}
                        isMenuOpen={openRoutineMenuId === routine.id}
                        members={members}
                        currentClassId={currentClass?.id || ''}
                      />
                      {/* 선택된 루틴 과제의 하위 섹션 */}
                      {selectedRoutineForDetail && selectedRoutineForDetail.id === routine.id && (
                        <RoutineDetailSection>
                          {/* 뒤로가기 버튼 - 상단 중앙 */}
                          <BackButtonContainer>
                            <BackButton onClick={handleRoutineDetailBack}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              뒤로가기
                            </BackButton>
                          </BackButtonContainer>
                          
                          <MonitoringHeader>
                            <MonitoringTitle>루틴 과제 도달 모니터링</MonitoringTitle>
                            <MonitoringButton onClick={handleOpenRoutineMonitoring} disabled={routineMonitoringLoading}>
                              {routineMonitoringLoading ? '도달률 계산 중...' : '멤버별 도달률 확인하기'}
                            </MonitoringButton>
                          </MonitoringHeader>

                          <DateBlocksSection>
                            <SectionTitle>날짜별 과제 현황</SectionTitle>
                            
                            {/* 🎯 페이지네이션 */}
                            {(() => {
                              const { totalPages } = getRoutineDatePagination();
                              return totalPages > 1 && (
                                <PaginationContainer>
                                  <PaginationButton 
                                    onClick={() => handleRoutineDatePageChange(routineDateCurrentPage - 1)} 
                                    disabled={routineDateCurrentPage === 1}
                                  >
                                    &lt;
                                  </PaginationButton>
                                  <PageInfo>
                                    {routineDateCurrentPage}/{totalPages}
                                  </PageInfo>
                                  <PaginationButton 
                                    onClick={() => handleRoutineDatePageChange(routineDateCurrentPage + 1)} 
                                    disabled={routineDateCurrentPage === totalPages}
                                  >
                                    &gt;
                                  </PaginationButton>
                                </PaginationContainer>
                              );
                            })()}
                            
                            {/* 🎯 날짜 필터 드롭다운 및 + 버튼 */}
                            <FilterRow>
                              {/* 🎯 드롭다운 외부 클릭 시 닫기 */}
                              {isDateFilterDropdownOpen && (
                                <FilterDropdownOverlay onClick={() => setIsDateFilterDropdownOpen(false)} />
                              )}
                              <FilterContainer>
                                <FilterDropdownItem>
                                  <FilterDropdownButton 
                                    onClick={() => setIsDateFilterDropdownOpen(!isDateFilterDropdownOpen)}
                                    $active={false}
                                  >
                                    <FilterDot />
                                    {(() => {
                                      switch (dateFilterType) {
                                        case 'desc': return '내림차순 날짜';
                                        case 'asc': return '오름차순 날짜';
                                        case 'hasData': return '기록된 날짜';
                                        case 'customOnly': return '커스텀 날짜';
                                        default: return '내림차순 날짜';
                                      }
                                    })()}
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </FilterDropdownButton>
                                  
                                  {isDateFilterDropdownOpen && (
                                    <FilterDropdown>
                                      <FilterDropdownOption 
                                        onClick={() => {
                                          setDateFilterType('desc');
                                          setIsDateFilterDropdownOpen(false);
                                          setRoutineDateCurrentPage(1);
                                        }}
                                      >
                                        <FilterDot />
                                        내림차순 날짜
                                      </FilterDropdownOption>
                                      <FilterDropdownOption 
                                        onClick={() => {
                                          setDateFilterType('asc');
                                          setIsDateFilterDropdownOpen(false);
                                          setRoutineDateCurrentPage(1);
                                        }}
                                      >
                                        <FilterDot />
                                        오름차순 날짜
                                      </FilterDropdownOption>
                                      <FilterDropdownOption 
                                        onClick={() => {
                                          setDateFilterType('hasData');
                                          setIsDateFilterDropdownOpen(false);
                                          setRoutineDateCurrentPage(1);
                                        }}
                                      >
                                        <FilterDot />
                                        기록된 날짜
                                      </FilterDropdownOption>
                                      <FilterDropdownOption 
                                        onClick={() => {
                                          setDateFilterType('customOnly');
                                          setIsDateFilterDropdownOpen(false);
                                          setRoutineDateCurrentPage(1);
                                        }}
                                      >
                                        <FilterDot />
                                        커스텀 날짜
                                      </FilterDropdownOption>
                                    </FilterDropdown>
                                  )}
                                </FilterDropdownItem>
                              </FilterContainer>
                              
                              {/* 🎯 + 버튼 (더 작게) */}
                              <AssignmentsTooltipWrapper data-tooltip="추가 날짜 과제 생성">
                              <AddButton onClick={() => setIsDateInputModalOpen(true)}>
                                <PlusIcon>+</PlusIcon>
                              </AddButton>
                              </AssignmentsTooltipWrapper>
                            </FilterRow>
                            
                            <DateBlocksGrid>
                              {(() => {
                                const { currentPageDates } = getRoutineDatePagination();
                                return currentPageDates.map((dateSummary) => (
                                <DateBlock 
                                  key={dateSummary.dateId}
                                  onClick={() => handleDateBlockClick(dateSummary.dateId, dateSummary.date)}
                                >
                                  <DateInfo>
                                    <DateText>{dateSummary.date.toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' })}</DateText>
                                                            <DayText>
                                    ({dateSummary.dayOfWeek})
                                    {dateSummary.isRoutineDay && <RoutineDayIcon>🌟</RoutineDayIcon>}
                                    {dateSummary.isCustomDate && <CustomDayIcon>📅</CustomDayIcon>}
                                  </DayText>
                                  </DateInfo>
                                  <StatusText>
                                    {(() => {
                                      if (dateSummary.status.hasServerData) {
                                        // 서버에 데이터가 있는 경우: 실제 확인/전체 수 표시
                                        const totalStudents = dateSummary.status.totalStudents || 0;
                                        const confirmedCount = totalStudents - (dateSummary.status.pendingCount || 0);
                                        return `확인: ${confirmedCount}/${totalStudents}명`;
                                      } else {
                                        // 서버에 데이터가 없는 경우: 기록 없음 표시
                                        return `기록 없음`;
                                      }
                                    })()}
                                  </StatusText>
                                  {/* 🗑️ 휴지통 아이콘 또는 빈 점선 정사각형 */}
                                  {dateSummary.status.hasServerData ? (
                                    <DeleteIcon
                                      onClick={(e) => {
                                        e.stopPropagation(); // 클릭 이벤트 전파 방지
                                        handleDateDelete(selectedRoutineForDetail?.id || '', dateSummary.dateId, dateSummary.date);
                                      }}
                                      viewBox="0 0 24 24"
                                      fill="none"
                                    >
                                      <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </DeleteIcon>
                                  ) : (
                                    <EmptyDeleteIcon title="과제 데이터 없음">
                                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <rect x="2" y="2" width="20" height="20" rx="2" stroke="currentColor" strokeWidth="2" strokeDasharray="2 2" fill="none"/>
                                      </svg>
                                    </EmptyDeleteIcon>
                                    )}
                                  </DateBlock>
                                ));
                              })()}
                            </DateBlocksGrid>
                          </DateBlocksSection>
                        </RoutineDetailSection>
                      )}
                    </React.Fragment>
                  );
                  });
              })()}
              
              {(() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const filteredCount = routineFilterType === 'incomplete' 
                  ? routineAssignments.filter(r => {
                      const dueDate = new Date(r.dueDate);
                      dueDate.setHours(0, 0, 0, 0);
                      return dueDate >= today;
                    }).length
                  : routineAssignments.length;
                
                if (filteredCount === 0 && !selectedRoutineForDetail) {
                  return (
                    <EmptyState>
                      <EmptyStateTitle>
                        {routineFilterType === 'incomplete' ? '미완료 루틴 과제가 없습니다' : '루틴 과제가 없습니다'}
                      </EmptyStateTitle>
                      <EmptyStateText>
                        {routineFilterType === 'incomplete' 
                          ? '마감일이 지나지 않은 루틴 과제가 없습니다.'
                          : '아직 생성된 루틴 과제가 없습니다.\n첫 번째 루틴 과제를 생성해보세요!'}
                      </EmptyStateText>
                    </EmptyState>
                  );
                }
                return null;
              })()}
            </BlocksContainer>
            
            
            {/* 루틴 과제용 플로팅 액션 버튼 */}
            <FloatingActionButton onClick={handleCreateRoutineAssignment}>
              <PlusIcon>+</PlusIcon>
            </FloatingActionButton>

          </>
        )}

        {/* 프로젝트 과제 탭 내용 */}
        {activeTab === 'project' && (
          <>
            <TopBar>
              <Title>프로젝트 과제 관리</Title>
              <TopBarRightActions>
                <CircleToggleButton
                  type="button"
                  onClick={toggleProjectSelectionMode}
                  title={isProjectSelectionMode ? '선택모드 취소' : '선택모드'}
                  aria-pressed={isProjectSelectionMode}
                  $active={isProjectSelectionMode}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </CircleToggleButton>
                <IconActionButton
                  type="button"
                  onClick={() => exportSelectedProjectAssignments()}
                  disabled={!isProjectSelectionMode || selectedProjectAssignmentIds.size === 0 || isExportingProjectAssignments}
                  title="프린트"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" stroke="currentColor" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
                  </svg>
                </IconActionButton>
              </TopBarRightActions>
            </TopBar>
            
            {/* 2행: 프로젝트 과제 필터 */}
            <FilterRow>
                <FilterSelectContainer>
                  <FilterSelectButton 
                  onClick={() => setIsProjectFilterDropdownOpen(!isProjectFilterDropdownOpen)}
                    type="button"
                  >
                    <FilterOption>
                      <ColorDot color="var(--color-border)" />
                      <span>
                      {projectFilterType === 'all' ? '전체 과제' : '미완료 과제'}
                      {projectSortBy === 'createdAt' ? ' · 생성일' : 
                       projectSortBy === 'dueDate' ? ' · 마감일' : 
                       projectSortBy === 'title' ? ' · 제목' : ''}
                      </span>
                    </FilterOption>
                  </FilterSelectButton>
                  
                {isProjectFilterDropdownOpen && (
                    <>
                    <FilterDropdownOverlay onClick={() => setIsProjectFilterDropdownOpen(false)} />
                      <FilterDropdown>
                        <FilterOption
                        onClick={(e) => {
                          e.stopPropagation();
                          setProjectFilterType('all');
                          setIsProjectFilterDropdownOpen(false);
                        }}
                      >
                        <span>전체 과제</span>
                      </FilterOption>
                      <FilterOption 
                        onClick={(e) => {
                          e.stopPropagation();
                          setProjectFilterType('incomplete');
                          setIsProjectFilterDropdownOpen(false);
                        }}
                      >
                        <span>미완료 과제</span>
                      </FilterOption>
                      <FilterDivider />
                      <FilterOption
                        onClick={(e) => {
                          e.stopPropagation();
                            setProjectSortBy('createdAt');
                          setIsProjectFilterDropdownOpen(false);
                          }}
                        >
                          <ColorDot color="var(--color-border)" />
                          <span>생성일</span>
                        </FilterOption>
                        <FilterOption
                        onClick={(e) => {
                          e.stopPropagation();
                            setProjectSortBy('dueDate');
                          setIsProjectFilterDropdownOpen(false);
                          }}
                        >
                          <ColorDot color="var(--color-border)" />
                          <span>마감일</span>
                        </FilterOption>
                        <FilterOption
                        onClick={(e) => {
                          e.stopPropagation();
                            setProjectSortBy('title');
                          setIsProjectFilterDropdownOpen(false);
                          }}
                        >
                          <ColorDot color="var(--color-border)" />
                          <span>제목</span>
                        </FilterOption>
                      </FilterDropdown>
                    </>
                  )}
                </FilterSelectContainer>
            </FilterRow>

            {projectAssignments.length === 0 ? (
              <EmptyState>
                <EmptyStateTitle>프로젝트 과제가 없습니다</EmptyStateTitle>
                <EmptyStateText>
                  아직 생성된 프로젝트 과제가 없습니다.<br />
                  첫 번째 프로젝트 과제를 생성해보세요!
                </EmptyStateText>
              </EmptyState>
            ) : (
              <BlocksContainer>
                {/* 🎯 페이지네이션 버튼 (첫번째 블럭 위) */}
                {(() => {
                  const { totalPages } = getProjectAssignmentPagination();
                  return totalPages > 1 && (
                    <PaginationContainer>
                      <PaginationButton 
                        onClick={() => handleProjectAssignmentPageChange(projectAssignmentCurrentPage - 1)} 
                        disabled={projectAssignmentCurrentPage === 1}
                      >
                        &lt;
                      </PaginationButton>
                      <PageInfo>
                        {projectAssignmentCurrentPage}/{totalPages}
                      </PageInfo>
                      <PaginationButton 
                        onClick={() => handleProjectAssignmentPageChange(projectAssignmentCurrentPage + 1)} 
                        disabled={projectAssignmentCurrentPage === totalPages}
                      >
                        &gt;
                      </PaginationButton>
                    </PaginationContainer>
                  );
                })()}

                {/* 모든 프로젝트 과제 표시 */}
                {(() => {
                  const { currentPageProjects } = getProjectAssignmentPagination();
                  
                  return currentPageProjects.map(project => {
                    const isSelected = selectedProjectAssignmentIds.has(project.id);
                    return (
                    <React.Fragment key={project.id}>
                      <ProjectAssignmentBlock
                        project={project}
                        onDelete={handleProjectDelete}
                        onEdit={handleProjectEdit}
                        onCheck={handleProjectCheck}
                        onCopy={handleProjectCopy}
                        onMenuToggle={(projectId) => {
                          if (openProjectMenuId === projectId) {
                            setOpenProjectMenuId(null);
                          } else {
                            // 햄버거 버튼의 위치를 계산하여 메뉴 위치 설정
                            const button = document.querySelector(`[data-project-id="${projectId}"] .hamburger-button`) as HTMLElement;
                            if (button) {
                              const rect = button.getBoundingClientRect();
                              setProjectMenuPosition({
                                top: rect.bottom + 4, // viewport 기준
                                left: rect.right - 200 // 프로젝트 메뉴 너비(200px)만큼 왼쪽으로 이동
                              });
                            }
                            setOpenProjectMenuId(projectId);
                          }
                        }}
                        isMenuOpen={openProjectMenuId === project.id}
                        members={members}
                        currentClassId={currentClass?.id || ''}
                        color={project.color}
                          selectionMode={isProjectSelectionMode}
                          isSelected={isSelected}
                          onSelectToggle={() => toggleProjectAssignmentSelected(project.id)}
                      />
                      {/* 선택된 프로젝트 과제의 하위 섹션 */}
                      {selectedProjectForDetail && selectedProjectForDetail.id === project.id && (
                        <ProjectDetailSection>
                          {/* 뒤로가기 버튼 - 상단 중앙 */}
                          <BackButtonContainer>
                            <BackButton onClick={handleProjectDetailBack}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7 14L12 9L17 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              뒤로가기
                            </BackButton>
                          </BackButtonContainer>
                          
                          <MonitoringHeader>
                            <MonitoringTitle>프로젝트 과제 도달 모니터링</MonitoringTitle>
                            <MonitoringButton onClick={handleOpenProjectMonitoring} disabled={projectMonitoringLoading}>
                              {projectMonitoringLoading ? '도달률 계산 중...' : '멤버별 도달률 확인하기'}
                            </MonitoringButton>
                          </MonitoringHeader>

                          <DateBlocksSection>
                            <SectionTitle>하위과제 현황</SectionTitle>
                            
                            {/* 프로젝트 하위과제 정렬 필터 */}
                            <FilterRow>
                              <FilterContainer>
                                <FilterDropdownItem>
                                  <FilterDropdownButton 
                                    onClick={() => setProjectSubTaskSortOrder(projectSubTaskSortOrder === 'desc' ? 'asc' : 'desc')}
                                    $active={false}
                                  >
                                    <FilterDot />
                                    {projectSubTaskSortOrder === 'desc' ? '내림차순 순서' : '오름차순 순서'}
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </FilterDropdownButton>
                                </FilterDropdownItem>
                              </FilterContainer>
                            </FilterRow>

                            {/* 하위과제 블럭들 */}
                            <DateBlocksGrid>
                              {projectSubTaskSummaries.length > 0 ? (
                                getSortedProjectSubTasks(projectSubTaskSummaries).map((subTaskSummary) => (
                                  <ProjectSubTaskBlock
                                    key={subTaskSummary.subTask.id}
                                    onClick={() => {
                                      // 하위과제 체크 모달 열기
                                      handleProjectSubTaskCheck(subTaskSummary);
                                    }}
                                    $isCompleted={subTaskSummary.pendingStudents === 0 && subTaskSummary.totalStudents > 0}
                                  >
                                    <ProjectSubTaskInfo>
                                      <DateText $isCompleted={subTaskSummary.pendingStudents === 0 && subTaskSummary.totalStudents > 0}>
                                        {subTaskSummary.subTask.order}. {subTaskSummary.subTask.title}
                                      </DateText>
                                      <DesktopDayText $isCompleted={subTaskSummary.pendingStudents === 0 && subTaskSummary.totalStudents > 0}>
                                        포인트: {subTaskSummary.subTask.points}
                                      </DesktopDayText>
                                      <MobileInfoRow>
                                        <DayText $isCompleted={subTaskSummary.pendingStudents === 0 && subTaskSummary.totalStudents > 0}>
                                          포인트: {subTaskSummary.subTask.points}
                                        </DayText>
                                        <StatusText $isCompleted={subTaskSummary.pendingStudents === 0 && subTaskSummary.totalStudents > 0}>
                                          {subTaskSummary.submittedStudents > 0 && (
                                            <span style={{
                                              background: '#000000',
                                              color: '#ffffff',
                                              padding: '2px 8px',
                                              borderRadius: '20px',
                                              marginRight: '8px',
                                              fontSize: '0.75rem',
                                              display: 'inline-block'
                                            }}>
                                              제출 확인: {subTaskSummary.submittedStudents}명
                                            </span>
                                          )}
                                          {subTaskSummary.totalStudents > 0 ? 
                                            (subTaskSummary.pendingStudents > 0 ? 
                                              `미확인: ${subTaskSummary.pendingStudents}/${subTaskSummary.totalStudents}명` : 
                                              '과제 완료'
                                            ) : 
                                            '기록 없음'
                                          }
                                        </StatusText>
                                      </MobileInfoRow>
                                    </ProjectSubTaskInfo>
                                    <DesktopStatusText $isCompleted={subTaskSummary.pendingStudents === 0 && subTaskSummary.totalStudents > 0}>
                                      {subTaskSummary.submittedStudents > 0 && (
                                        <span style={{
                                          background: '#000000',
                                          color: '#ffffff',
                                          padding: '2px 8px',
                                          borderRadius: '20px',
                                          marginRight: '12px',
                                          fontSize: '0.8rem',
                                          display: 'inline-block',
                                          lineHeight: '1.2'
                                        }}>
                                          제출 확인: {subTaskSummary.submittedStudents}명
                                        </span>
                                      )}
                                      {subTaskSummary.totalStudents > 0 ? 
                                        (subTaskSummary.pendingStudents > 0 ? 
                                          `미확인: ${subTaskSummary.pendingStudents}/${subTaskSummary.totalStudents}명` : 
                                          '과제 완료'
                                        ) : 
                                        '기록 없음'
                                      }
                                    </DesktopStatusText>
                                  </ProjectSubTaskBlock>
                                ))
                              ) : (
                                <EmptyState>
                                  <EmptyStateTitle>하위과제가 없습니다</EmptyStateTitle>
                                  <EmptyStateText>
                                    아직 하위과제가 없습니다.<br />
                                    프로젝트 과제 설정에서 하위과제를 추가해보세요!
                                  </EmptyStateText>
                                </EmptyState>
                              )}
                            </DateBlocksGrid>
                          </DateBlocksSection>
                        </ProjectDetailSection>
                      )}
                    </React.Fragment>
                    );
                  });
                })()}
              </BlocksContainer>
            )}

            
            {/* 프로젝트 과제용 플로팅 액션 버튼 */}
            <FloatingActionButton onClick={() => {
              setEditingProject(null); // 새 과제 생성 모드
              setIsProjectSettingsModalOpen(true);
            }}>
              <PlusIcon>+</PlusIcon>
            </FloatingActionButton>

          </>
        )}

        {/* 하단 EDUKODI 로고 워터마크 (배경보다 아주 조금 진한 회색) */}
        <AssignmentsPageBottomLogo>
          <img src="/EDUKODI_LOGO2.svg?v=2" alt="" aria-hidden />
        </AssignmentsPageBottomLogo>
      </Content>

      {/* 루틴 과제 메뉴 드롭다운 */}
      {openRoutineMenuId && (
        <>
          <MenuDropdownOverlay onClick={() => setOpenRoutineMenuId(null)} />
          <MenuDropdown style={{
            top: routineMenuPosition.top,
            left: routineMenuPosition.left
          }}>
            <MenuDropdownOption onClick={() => {
              const routine = routineAssignments.find(r => r.id === openRoutineMenuId);
              if (routine) {
                handleRoutineEdit(routine);
                setOpenRoutineMenuId(null);
              }
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09A1.65 1.65 0 0 0 19.4 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              루틴 과제 설정
            </MenuDropdownOption>
            <MenuDropdownOption onClick={() => {
              const routine = routineAssignments.find(r => r.id === openRoutineMenuId);
              if (routine) {
                handleCopyRoutineAssignment(routine);
                setOpenRoutineMenuId(null);
              }
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 4v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7.242a2 2 0 0 0-.602-1.43L16.083 2.57A2 2 0 0 0 14.685 2H10a2 2 0 0 0-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 18v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              루틴 과제 복제
            </MenuDropdownOption>
            <MenuDropdownOption onClick={() => {
              const routine = routineAssignments.find(r => r.id === openRoutineMenuId);
              if (routine) {
                handleRoutineDelete(routine);
                setOpenRoutineMenuId(null);
              }
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              루틴 과제 삭제
            </MenuDropdownOption>
          </MenuDropdown>
        </>
      )}

      {/* 프로젝트 과제 메뉴 드롭다운 */}
      {openProjectMenuId && (
        <>
          <MenuDropdownOverlay onClick={() => setOpenProjectMenuId(null)} />
          <ProjectMenuDropdown style={{
            top: projectMenuPosition.top,
            left: projectMenuPosition.left
          }}>
            <MenuDropdownOption onClick={() => {
              const project = projectAssignments.find(p => p.id === openProjectMenuId);
              if (project) {
                handleProjectEdit(project);
                setOpenProjectMenuId(null);
              }
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09A1.65 1.65 0 0 0 19.4 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              프로젝트 과제 설정
            </MenuDropdownOption>
            <MenuDropdownOption onClick={() => {
              const project = projectAssignments.find(p => p.id === openProjectMenuId);
              if (project) {
                handleProjectCopy(project);
                setOpenProjectMenuId(null);
              }
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 4v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7.242a2 2 0 0 0-.602-1.43L16.083 2.57A2 2 0 0 0 14.685 2H10a2 2 0 0 0-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 18v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              프로젝트 과제 복제
            </MenuDropdownOption>
            <MenuDropdownOption onClick={() => {
              const project = projectAssignments.find(p => p.id === openProjectMenuId);
              if (project) {
                handleProjectDelete(project);
                setOpenProjectMenuId(null);
              }
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              프로젝트 과제 삭제
            </MenuDropdownOption>
          </ProjectMenuDropdown>
        </>
      )}

      <NewAssignmentModal
        isOpen={isNewModalOpen}
        onCancel={() => setIsNewModalOpen(false)}
        onSave={handleCreateAssignment}
        members={members}
        memberGroups={memberGroups}
        memberGroupOrder={currentClass?.memberGroupOrder}
        currentMemberGroup={currentMemberGroup}
        memberGroupNames={currentClass?.memberGroupNames}
        mode={modalMode}
        initialAssignment={initialAssignment}
        initialSelectedIds={initialSelectedIds}
        categories={categories}
        defaultPoints={defaultPoints}
        userId={user.id}
        classId={currentClass?.id || ''}
        user={user}
        onLicenseClick={onLicenseClick}
        onOpenItemCardModal={(currentIds, onConfirm) => {
          setAssignmentRewardCardIds(currentIds);
          setAssignmentRewardCardCallback(() => onConfirm);
          setIsAssignmentRewardCardModalOpen(true);
        }}
      />

      <AssignmentSettingsModal
        isOpen={isCatModalOpen}
        onClose={() => setIsCatModalOpen(false)}
        user={user}
        currentClass={currentClass}
        onDefaultPointsChange={setDefaultPoints}
        currentDefaultPoints={defaultPoints}
        onStatusDefaultPointsChange={setStatusDefaultPoints}
        currentStatusDefaultPoints={statusDefaultPoints}
        onCategoriesChange={(newCategories) => {
          setCategories(newCategories);
        }}
        onAssignmentSettingsChange={handleAssignmentSettingsChange}
      />

      {/* 루틴 과제 설정 모달 */}
      <RoutineAssignmentSettingsModal
        isOpen={isRoutineSettingsModalOpen}
        onClose={() => {
          setIsRoutineSettingsModalOpen(false);
          setInitialRoutineAssignment(undefined);
          setInitialSelectedIds(undefined);
          setModalMode('new');
        }}
        onSave={handleRoutineAssignmentSave}
        members={members}
        memberGroups={memberGroups}
        memberGroupOrder={currentClass?.memberGroupOrder}
        currentMemberGroup={currentMemberGroup}
        memberGroupNames={currentClass?.memberGroupNames}
        currentClassId={currentClass?.id || ''}
        userId={user.id}
        user={user}
        defaultPoints={defaultPoints}
        mode={modalMode}
        initialData={initialRoutineAssignment}
        initialSelectedIds={initialSelectedIds}
        onLicenseClick={onLicenseClick}
      />

      {isRoutineMonitoringModalOpen && (
        <MonitoringModalOverlay onClick={() => setIsRoutineMonitoringModalOpen(false)}>
          <MonitoringModalContent onClick={(e) => e.stopPropagation()}>
            <MonitoringModalTitle>멤버별 도달률 확인하기</MonitoringModalTitle>
            {selectedRoutineForDetail?.rewardEnabled && (
              <ProjectRewardButtonContainer>
                <ProjectRewardButton onClick={handleRoutineRewardGrant} disabled={isRoutineRewardProcessing}>
                  {isRoutineRewardProcessing ? '보상 처리 중...' : '루틴 과제 보상 지급'}
                </ProjectRewardButton>
              </ProjectRewardButtonContainer>
            )}
            <MonitoringSortRow>
              <MonitoringSortButtons>
                <MonitoringSortButton
                  type="button"
                  $active={routineMonitoringSortOrder === 'number'}
                  onClick={() => setRoutineMonitoringSortOrder('number')}
                >
                  번호순
                </MonitoringSortButton>
                <MonitoringSortButton
                  type="button"
                  $active={routineMonitoringSortOrder === 'rate'}
                  onClick={() => setRoutineMonitoringSortOrder('rate')}
                >
                  도달률순
                </MonitoringSortButton>
              </MonitoringSortButtons>
            </MonitoringSortRow>
            {routineMonitoringLoading ? (
              <MonitoringLoading>도달률 계산 중...</MonitoringLoading>
            ) : (
              <MonitoringList>
                {[...routineMonitoringData]
                  .sort((a, b) => routineMonitoringSortOrder === 'number'
                    ? a.studentNumber - b.studentNumber
                    : b.completionRate - a.completionRate || a.studentNumber - b.studentNumber)
                  .map((item) => (
                  <MonitoringCard key={item.studentId}>
                    <MonitoringCardHeader>
                      <span>{item.studentNumber}. {item.studentName}</span>
                      <MonitoringPercent>{item.completionRate}%</MonitoringPercent>
                    </MonitoringCardHeader>
                    <MonitoringBar>
                      <MonitoringBarToday style={{ width: `${Math.min((item.todayCount / Math.max(item.totalDates, 1)) * 100, 100)}%` }} />
                      <MonitoringBarFill style={{ width: `${Math.min(item.completionRate, 100)}%` }} />
                      {selectedRoutineForDetail?.rewardCompletionRate !== undefined && (
                        <MonitoringThresholdLine style={{
                          left: `${Math.min(Math.max(selectedRoutineForDetail.rewardCompletionRate, 0), 100)}%`
                        }} />
                      )}
                    </MonitoringBar>
                    <MonitoringMeta>
                      확인 완료 {item.confirmedCount} / 전체 {item.totalDates}회 (오늘까지 {item.todayCount}회)
                    </MonitoringMeta>
                  </MonitoringCard>
                ))}
              </MonitoringList>
            )}
          </MonitoringModalContent>
        </MonitoringModalOverlay>
      )}

      {isProjectMonitoringModalOpen && (
        <MonitoringModalOverlay onClick={() => setIsProjectMonitoringModalOpen(false)}>
          <MonitoringModalContent onClick={(e) => e.stopPropagation()}>
            <MonitoringModalTitle>멤버별 도달률 확인하기</MonitoringModalTitle>
            {selectedProjectForDetail?.rewardEnabled && (
              <ProjectRewardButtonContainer>
                <ProjectRewardButton onClick={() => handleProjectRewardGrant(selectedProjectForDetail.id)}>
                  프로젝트 과제 보상 지급
                </ProjectRewardButton>
              </ProjectRewardButtonContainer>
            )}
            <MonitoringSortRow>
              <MonitoringSortButtons>
                <MonitoringSortButton
                  type="button"
                  $active={projectMonitoringSortOrder === 'number'}
                  onClick={() => setProjectMonitoringSortOrder('number')}
                >
                  번호순
                </MonitoringSortButton>
                <MonitoringSortButton
                  type="button"
                  $active={projectMonitoringSortOrder === 'rate'}
                  onClick={() => setProjectMonitoringSortOrder('rate')}
                >
                  도달률순
                </MonitoringSortButton>
              </MonitoringSortButtons>
            </MonitoringSortRow>
            {projectMonitoringLoading ? (
              <MonitoringLoading>도달률 계산 중...</MonitoringLoading>
            ) : (
              <MonitoringList>
                {[...projectMonitoringData]
                  .sort((a, b) => projectMonitoringSortOrder === 'number'
                    ? a.studentNumber - b.studentNumber
                    : b.completionRate - a.completionRate || a.studentNumber - b.studentNumber)
                  .map((item) => (
                  <MonitoringCard key={item.studentId}>
                    <MonitoringCardHeader>
                      <span>{item.studentNumber}. {item.studentName}</span>
                      <MonitoringPercent>{item.completionRate}%</MonitoringPercent>
                    </MonitoringCardHeader>
                    {item.totalSubTasks > 0 ? (
                      <ProjectMonitoringBar $columns={item.totalSubTasks}>
                        {item.segmentStates.map((isDone, index) => (
                          <ProjectMonitoringSegment key={`${item.studentId}-${index}`} $isDone={isDone} />
                        ))}
                      </ProjectMonitoringBar>
                    ) : (
                      <MonitoringMeta>하위과제가 없습니다.</MonitoringMeta>
                    )}
                    {item.totalSubTasks > 0 && (
                      <MonitoringMeta>
                        확인 완료 {item.completedCount} / 전체 {item.totalSubTasks}개
                      </MonitoringMeta>
                    )}
                  </MonitoringCard>
                ))}
              </MonitoringList>
            )}
          </MonitoringModalContent>
        </MonitoringModalOverlay>
      )}

      {/* 루틴 과제 체크 모달 - selectedRoutineAssignment용 */}
      {selectedRoutineAssignment && (
        <AssignmentCheckModal
          isOpen={isRoutineCheckModalOpen}
          onClose={() => {
            setIsRoutineCheckModalOpen(false);
            setSelectedRoutineAssignment(null);
            // 🎯 모달이 닫힐 때 커스텀 날짜 상태 초기화
            setCustomDateForRoutine(null);
            console.log('🎯 모달 닫기 시 customDateForRoutine 초기화됨');
          }}
          userId={user?.id}
          classId={currentClass?.id}
          userType={user?.userType}
          memberGroup={currentMemberGroup}
          mode="routine"
          routine={routineAssignments.find(r => r.id === selectedRoutineAssignment.routineAssignmentId)}
          members={members}
          defaultPoints={defaultPoints}
          statusDefaultPoints={statusDefaultPoints} // 🎯 누락된 props 추가
          selectedDate={selectedRoutineAssignment.date} // 🎯 선택된 날짜 전달
          customDate={customDateForRoutine || undefined} // 🎯 커스텀 날짜 전달
          onSave={async (studentStatuses, selectedDate) => {
            try {
              // 루틴 과제 체크 결과를 데이터베이스에 저장
              if (user?.id && currentClass?.id && selectedRoutineAssignment) {
                // onSave의 두 번째 파라미터로 전달된 날짜 사용 (날짜 네비게이션 후 현재 날짜)
                // selectedDate가 없으면 fallback으로 selectedRoutineAssignment.date 사용
                const targetDate = selectedDate || selectedRoutineAssignment.date;
                // 한국 시간대를 고려한 날짜 ID 생성
                const dateId = targetDate.getFullYear() + '-' + 
                  String(targetDate.getMonth() + 1).padStart(2, '0') + '-' + 
                  String(targetDate.getDate()).padStart(2, '0');
                
                console.log('📅 루틴 과제 저장 - onSave에서 전달된 날짜:', selectedDate);
                console.log('📅 루틴 과제 저장 - 사용할 날짜:', targetDate);
                console.log('📅 루틴 과제 저장 - dateId:', dateId);
                
                // 해당 루틴 과제 정보 가져오기
                const routineAssignment = routineAssignments.find(r => r.id === selectedRoutineAssignment.routineAssignmentId);
                console.log('🔍 전체 루틴 과제 목록:', routineAssignments);
                console.log('🔍 찾고 있는 루틴 과제 ID:', selectedRoutineAssignment.routineAssignmentId);
                console.log('🔍 찾은 루틴 과제:', routineAssignment);
                
                if (!routineAssignment) {
                  console.error('루틴 과제 정보를 찾을 수 없습니다.');
                  return;
                }
                
                // 과제를 부여받은 멤버 수 확인 (로깅용)
                const assignedStudentCount = routineAssignment.assignedStudentIds?.length || 0;
                console.log('📅 과제 부여 멤버 수:', assignedStudentCount);
                console.log('📅 전체 클래스 멤버:', members);
                
                // 데이터 입력이 있는 멤버가 있는지 확인
                const hasDataInput = studentStatuses.length > 0;
                console.log('🔍 데이터 입력 멤버 수:', studentStatuses.length);
                console.log('🔍 모든 멤버 문서 생성 여부:', hasDataInput ? '예' : '아니오');
                
                if (hasDataInput) {
                  // 데이터 입력이 있는 경우: 변경된 멤버만 저장 (기존 데이터 보존)
                  console.log('🔄 변경된 멤버만 저장 시작...');
                  console.log('📝 저장할 멤버 수:', studentStatuses.length);
                  console.log('📝 studentStatuses:', studentStatuses);
                  
                  await routineAssignmentService.batchUpdateRoutineAssignmentStatusOptimized(
                    user.id,
                    currentClass.id,
                    selectedRoutineAssignment.routineAssignmentId,
                    dateId,
                    studentStatuses.map(s => ({
                      studentId: s.studentId,
                      status: s.status,
                      earnedPoints: s.earnedPoints,
                      memo: s.memo || ''
                    })),
                    currentMemberGroup,
                    {
                      fillMissingAssignedAsPending: true,
                      assignedStudentIds: routineAssignment.assignedStudentIds || []
                    }
                  );
                  console.log(`🎉 총 ${studentStatuses.length}명의 멤버 상태 일괄 저장 완료`);
                } else {
                  // 데이터 입력이 없는 경우: 저장하지 않음
                  console.log('ℹ️ 데이터 입력이 없어 저장을 건너뜁니다.');
                }
                
                // 저장 성공 후 데이터 새로고침
                await loadData();
                invalidateRoutineMonitoringCache(selectedRoutineAssignment.routineAssignmentId);
                
                // 현재 선택된 루틴의 날짜별 현황도 다시 로드
                if (selectedRoutineForDetail) {
                  try {
                    const consolidatedService = new RoutineAssignmentConsolidatedService(user.id, currentClass.id, currentMemberGroup);
                    const result = await consolidatedService.loadRoutineAssignmentWithStatuses(selectedRoutineForDetail.id);
                    
                    // RoutineAssignmentDateStatus 형식으로 변환
                    const dateStatuses = result.dateStatuses.map(dateAssignment => {
                      const students: Record<string, RoutineAssignmentStudentStatus> = {};
                      let totalStudents = 0;
                      let completedStudents = 0;
                      let pendingStudents = 0;
                      
                      const studentStatuses = dateAssignment.studentStatuses || [];
                      studentStatuses.forEach((studentStatus: any) => {
                        students[studentStatus.studentId] = {
                          id: studentStatus.studentId,
                          studentId: studentStatus.studentId,
                          assignmentId: selectedRoutineForDetail.id,
                          status: studentStatus.status,
                          earnedPoints: studentStatus.points,
                          memo: studentStatus.memo,
                          updatedAt: studentStatus.updatedAt
                        };
                        
                        totalStudents++;
                        if (studentStatus.status === 'confirmed') {
                          completedStudents++;
                        } else if (studentStatus.status === 'pending') {
                          pendingStudents++;
                        }
                      });
                      
                      return {
                        dateId: dateAssignment.date,
                        routineAssignmentId: selectedRoutineForDetail.id,
                        date: new Date(dateAssignment.date),
                        isCustomDate: dateAssignment.isCustomDate,
                        students,
                        totalStudents,
                        completedStudents,
                        pendingStudents,
                        updatedAt: new Date()
                      };
                    });
                    
                    const summaries = processRoutineDateStatuses(dateStatuses, selectedRoutineForDetail);
                    setRoutineDateSummaries(summaries);
                    console.log('🔄 날짜별 과제 현황 리로드 완료');
                  } catch (error) {
                    console.error('날짜별 과제 현황 리로드 실패:', error);
                  }
                }
                
                console.log('📅 루틴 과제 체크 결과 저장 완료 - 날짜:', dateId);
              }
            } catch (error) {
              console.error('루틴 과제 체크 결과 저장 실패:', error);
              alert('체크 결과 저장에 실패했습니다.');
            }
          }}
        />
      )}

      {/* 과제 체크 모달 추가 */}
      {selectedAssignment && (
        <AssignmentCheckModal
          isOpen={isCheckModalOpen}
          onClose={() => {
            setIsCheckModalOpen(false);
            // studentStatuses는 유지 - 제출 확인 표시를 위해 초기화하지 않음
          }}
          onSave={handleSaveCheckResults}
          mode="general"
          assignment={selectedAssignment}
          members={members}
          category={categories.find(c => c.id === selectedAssignment.categoryId)}
          existingStatuses={studentStatuses}
          defaultPoints={defaultPoints}
          statusDefaultPoints={statusDefaultPoints}
          userId={user.id}
          classId={currentClass?.id}
          userType={user.userType}
          onPendingCountUpdate={(pendingCount, submittedCount) => {
            if (!selectedAssignment) return;
            setAssignments(prev => {
              const prevAss = prev.find(a => a.id === selectedAssignment.id);
              const currentPendingCount = pendingCountUpdateRef.current[selectedAssignment.id];
              const prevSubmitted = prevAss?.submittedCount;
              const submittedChanged =
                submittedCount !== undefined && submittedCount !== prevSubmitted;
              if (currentPendingCount === pendingCount && !submittedChanged) {
                return prev;
              }
              pendingCountUpdateRef.current[selectedAssignment.id] = pendingCount;
              return prev.map(ass =>
                ass.id === selectedAssignment.id
                  ? {
                      ...ass,
                      pendingCount,
                      ...(submittedCount !== undefined
                        ? { submittedCount, lastSubmittedUpdate: new Date() }
                        : {}),
                      lastPendingUpdate: new Date(),
                    }
                  : ass
              );
            });
          }}
        />
      )}

      {/* 처리 중 토스트 메시지 */}
      {processingToast && (
        <ToastOverlay>
          <ToastMessage>
            <ToastSpinner />
            {processingToast}
          </ToastMessage>
        </ToastOverlay>
      )}

      {/* 멤버별 미완료 과제 모달 */}
      {isIncompleteByStudentModalOpen && (
        <IncompleteModalOverlay onClick={() => setIsIncompleteByStudentModalOpen(false)}>
          <IncompleteModalContent onClick={(e) => e.stopPropagation()}>
            <IncompleteModalHeader>
              <IncompleteModalTitle>멤버별 미완료 과제</IncompleteModalTitle>
              <IncompleteModalActions>
                <IncompleteActionButton onClick={() => handleSaveAsPdf('byStudent')} title="PDF 저장">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                </IncompleteActionButton>
                <IncompleteActionButton onClick={handlePrint} title="프린트">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
                  </svg>
                </IncompleteActionButton>
                <IncompleteCloseButton onClick={() => setIsIncompleteByStudentModalOpen(false)}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </IncompleteCloseButton>
              </IncompleteModalActions>
            </IncompleteModalHeader>
            <IncompleteModalBody ref={incompleteModalContentRef}>
              {modalOpenTime && (
                <ModalDateHeader data-pdf-element="header">{formatModalOpenTime(modalOpenTime)}</ModalDateHeader>
              )}
              {isLoadingIncompleteData ? (
                <LoadingMessage>데이터를 불러오는 중...</LoadingMessage>
              ) : incompleteByStudentData.length === 0 ? (
                <EmptyMessage>미완료 과제가 없습니다.</EmptyMessage>
              ) : (
                incompleteByStudentData.map(({ student, incompleteAssignments }) => (
                  <StudentIncompleteRow key={student.id} data-pdf-element="row">
                    <StudentInfoColumn>
                      <StudentNumber>{student.number}번</StudentNumber>
                      <StudentName>{student.name}</StudentName>
                    </StudentInfoColumn>
                    <IncompleteAssignmentList>
                      {incompleteAssignments.map(({ assignment, type, routineDateId, routineDate }) => (
                        <CompactAssignmentBlock 
                          key={type === 'routine' ? `${assignment.id}-${routineDateId}` : assignment.id}
                          onClick={() => handleOpenCheckFromIncomplete(assignment, type, routineDateId, routineDate)}
                        >
                          <AssignmentBlockRow1>
                            <AssignmentTypeBadge $type={type}>
                              {type === 'general' ? '일반' : type === 'project' ? '프로' : '루틴'}
                            </AssignmentTypeBadge>
                            <CompactDueDate>{formatDueDate(type === 'routine' ? routineDate : assignment.dueDate)}</CompactDueDate>
                          </AssignmentBlockRow1>
                          <AssignmentBlockRow2>
                            <CompactAssignmentName>{assignment.title}</CompactAssignmentName>
                          </AssignmentBlockRow2>
                        </CompactAssignmentBlock>
                      ))}
                    </IncompleteAssignmentList>
                  </StudentIncompleteRow>
                ))
              )}
            </IncompleteModalBody>
          </IncompleteModalContent>
        </IncompleteModalOverlay>
      )}

      {/* 과제별 미완료 멤버 모달 */}
      {isIncompleteByAssignmentModalOpen && (
        <IncompleteModalOverlay onClick={() => setIsIncompleteByAssignmentModalOpen(false)}>
          <IncompleteModalContent onClick={(e) => e.stopPropagation()}>
            <IncompleteModalHeader>
              <IncompleteModalTitle>과제별 미완료 멤버</IncompleteModalTitle>
              <IncompleteModalActions>
                <IncompleteActionButton onClick={() => handleSaveAsPdf('byAssignment')} title="PDF 저장">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                </IncompleteActionButton>
                <IncompleteActionButton onClick={handlePrint} title="프린트">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
                  </svg>
                </IncompleteActionButton>
                <IncompleteCloseButton onClick={() => setIsIncompleteByAssignmentModalOpen(false)}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </IncompleteCloseButton>
              </IncompleteModalActions>
            </IncompleteModalHeader>
            <IncompleteModalBody ref={incompleteModalContentRef}>
              {modalOpenTime && (
                <ModalDateHeader data-pdf-element="header">{formatModalOpenTime(modalOpenTime)}</ModalDateHeader>
              )}
              {isLoadingIncompleteData ? (
                <LoadingMessage>데이터를 불러오는 중...</LoadingMessage>
              ) : incompleteByAssignmentData.length === 0 ? (
                <EmptyMessage>미완료 과제가 없습니다.</EmptyMessage>
              ) : (
                incompleteByAssignmentData.map(({ assignment, type, routineDateId, routineDate, incompleteStudents }) => (
                  <AssignmentIncompleteRow key={type === 'routine' ? `${assignment.id}-${routineDateId}` : assignment.id} data-pdf-element="row">
                    <AssignmentTitleBlock onClick={() => handleOpenCheckFromIncomplete(assignment, type, routineDateId, routineDate)}>
                      <AssignmentBlockRow1>
                        <AssignmentTypeBadge $type={type}>
                          {type === 'general' ? '일반' : type === 'project' ? '프로' : '루틴'}
                        </AssignmentTypeBadge>
                        <CompactDueDate>{formatDueDate(type === 'routine' ? routineDate : assignment.dueDate)}</CompactDueDate>
                      </AssignmentBlockRow1>
                      <AssignmentBlockRow2>
                        <CompactAssignmentName>{assignment.title}</CompactAssignmentName>
                      </AssignmentBlockRow2>
                    </AssignmentTitleBlock>
                    <IncompleteStudentList>
                      {incompleteStudents.map((student) => (
                        <IncompleteStudentBlock key={student.id}>
                          <IncompleteStudentNumber>{student.number}번</IncompleteStudentNumber>
                          <IncompleteStudentName>{student.name}</IncompleteStudentName>
                        </IncompleteStudentBlock>
                      ))}
                    </IncompleteStudentList>
                  </AssignmentIncompleteRow>
                ))
              )}
            </IncompleteModalBody>
          </IncompleteModalContent>
        </IncompleteModalOverlay>
      )}

      {isStudentOverviewOpen && (
        <StudentOverviewModalOverlay onClick={handleCloseStudentOverview}>
          <StudentOverviewModalContent onClick={(e) => e.stopPropagation()}>
            <StudentOverviewModalHeader>
              <StudentOverviewModalHeaderTopRow>
                <StudentOverviewModalTitle>멤버별 과제 현황</StudentOverviewModalTitle>
                <StudentOverviewHeaderActions>
                  <IncompleteCloseButton onClick={handleCloseStudentOverview}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </IncompleteCloseButton>
                </StudentOverviewHeaderActions>
              </StudentOverviewModalHeaderTopRow>
              <StudentOverviewModalToggleRow>
              <StudentOverviewModeToggleCenter
                onClick={() => setStudentOverviewMode(prev => prev === 'number' ? 'seat' : 'number')}
                role="button"
                tabIndex={0}
                $isSeatMode={studentOverviewMode === 'seat'}
              >
                <StudentOverviewModeOption $isActive={studentOverviewMode === 'number'}>번호</StudentOverviewModeOption>
                <StudentOverviewModeOption $isActive={studentOverviewMode === 'seat'}>자리</StudentOverviewModeOption>
              </StudentOverviewModeToggleCenter>
              </StudentOverviewModalToggleRow>
            </StudentOverviewModalHeader>
            <StudentOverviewModalBody>
              {studentOverviewLoading ? (
                <LoadingMessage>데이터를 불러오는 중...</LoadingMessage>
              ) : sortedStudentOverviewData.length === 0 ? (
                <EmptyMessage>표시할 학생 데이터가 없습니다.</EmptyMessage>
              ) : (
                studentOverviewMode === 'number' ? (
                  <>
                    <StudentOverviewSeatTableOuter>
                      <StudentOverviewNumberModeStudentsGrid $alignment={studentOverviewGridAlignment}>
                    {sortedStudentOverviewData.map((student) => (
                          <StudentOverviewCard
                            key={student.studentId}
                            $clickable={student.pendingCount > 0}
                            onClick={student.pendingCount > 0 ? () => handleOpenStudentIncompleteModal(student) : undefined}
                          >
                        <StudentOverviewProfileWrapper>
                          <StudentOverviewProfileImage
                            imageId={resolveOverviewProfileByStudentId(student.studentId, student.profileImage)}
                            alt={`${student.studentName} 프로필`}
                          />
                        </StudentOverviewProfileWrapper>
                        <StudentOverviewMain>
                          <StudentOverviewTopRow>
                            <StudentOverviewStudentInfoGroup>
                              <StudentOverviewNumber>{String(student.studentNumber).padStart(2, '0')}</StudentOverviewNumber>
                              <StudentOverviewName>{student.studentName}</StudentOverviewName>
                            </StudentOverviewStudentInfoGroup>
                          </StudentOverviewTopRow>
                              <StudentOverviewCountsColumn $pushToBottom={student.pendingCount > 0}>
                                {student.pendingCount > 0 ? (
                                  <>
                                    <StudentOverviewStatusBadge $variant="incomplete">
                                      <StudentOverviewStatusBadgeLabel>미완료 과제</StudentOverviewStatusBadgeLabel>
                                    </StudentOverviewStatusBadge>
                                    <StudentOverviewPendingCountText>
                                      {student.pendingCount}개
                                    </StudentOverviewPendingCountText>
                                  </>
                                ) : (
                                  <StudentOverviewStatusBadge $variant="complete">
                                    <StudentOverviewStatusBadgeLabel>모든 과제 완료</StudentOverviewStatusBadgeLabel>
                                  </StudentOverviewStatusBadge>
                                )}
                          </StudentOverviewCountsColumn>
                        </StudentOverviewMain>
                      </StudentOverviewCard>
                    ))}
                      </StudentOverviewNumberModeStudentsGrid>
                    </StudentOverviewSeatTableOuter>
                    <StudentOverviewGridAlignmentContainer>
                      <StudentOverviewGridAlignmentToggle
                        type="button"
                        className="touch-compact"
                        onClick={() =>
                          setStudentOverviewGridAlignment((prev) => (prev === 'left' ? 'center' : 'left'))
                        }
                        tabIndex={-1}
                        title={
                          studentOverviewGridAlignment === 'left'
                            ? '중앙 정렬로 변경'
                            : '왼쪽 정렬로 변경'
                        }
                      >
                        <StudentOverviewGridAlignmentOption $isActive={studentOverviewGridAlignment === 'left'}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 6h18M3 12h12M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                        </StudentOverviewGridAlignmentOption>
                        <StudentOverviewGridAlignmentOption $isActive={studentOverviewGridAlignment === 'center'}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 6h18M6 12h12M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                        </StudentOverviewGridAlignmentOption>
                      </StudentOverviewGridAlignmentToggle>
                    </StudentOverviewGridAlignmentContainer>
                  </>
                ) : (
                  <StudentOverviewSeatArea>
                    <StudentOverviewSeatTableOuter>
                      <StudentOverviewSeatGridScroll>
                      <StudentOverviewSeatGrid
                        $gridTemplateColumns={studentOverviewSeatLayout.colTemplate}
                        $gridTemplateRows={studentOverviewSeatLayout.rowTemplate}
                      >
                        {studentOverviewSeatLayout.assigned.map(({ student, seatRow, seatCol }) => {
                          const gridRow = seatRow - studentOverviewSeatLayout.minRow + 1;
                          const gridCol = seatCol - studentOverviewSeatLayout.minCol + 1;
                          return (
                          <StudentOverviewSeatCell
                            key={student.studentId}
                            style={{
                              gridColumn: `${gridCol} / span 2`,
                              gridRow: `${gridRow} / span 2`
                            }}
                          >
                            <StudentOverviewCard
                              $seat
                              $clickable={student.pendingCount > 0}
                              onClick={student.pendingCount > 0 ? () => handleOpenStudentIncompleteModal(student) : undefined}
                            >
                              <StudentOverviewProfileWrapper $seat>
                                <StudentOverviewProfileImage
                                  imageId={resolveOverviewProfileByStudentId(student.studentId, student.profileImage)}
                                  alt={`${student.studentName} 프로필`}
                                />
                              </StudentOverviewProfileWrapper>
                              <StudentOverviewMain $seat>
                                <StudentOverviewTopRow $seat>
                                  <StudentOverviewStudentInfoGroup>
                                    <StudentOverviewNumber>{String(student.studentNumber).padStart(2, '0')}</StudentOverviewNumber>
                                    <StudentOverviewName>{student.studentName}</StudentOverviewName>
                                  </StudentOverviewStudentInfoGroup>
                                </StudentOverviewTopRow>
                                <StudentOverviewCountsColumn $seat>
                                {student.pendingCount > 0 ? (
                                  <>
                                    <StudentOverviewStatusBadge $variant="incomplete" $seat>
                                      <StudentOverviewStatusBadgeLabel $seat>미완료 과제</StudentOverviewStatusBadgeLabel>
                                    </StudentOverviewStatusBadge>
                                    <StudentOverviewPendingCountText $seat>
                                      {student.pendingCount}개
                                    </StudentOverviewPendingCountText>
                                  </>
                                ) : (
                                  <StudentOverviewStatusBadge $variant="complete" $seat>
                                    <StudentOverviewStatusBadgeLabel $seat>모든 과제 완료</StudentOverviewStatusBadgeLabel>
                                  </StudentOverviewStatusBadge>
                                )}
                                </StudentOverviewCountsColumn>
                              </StudentOverviewMain>
                            </StudentOverviewCard>
                          </StudentOverviewSeatCell>
                          );
                        })}
                      </StudentOverviewSeatGrid>
                      </StudentOverviewSeatGridScroll>
                    </StudentOverviewSeatTableOuter>
                    <StudentOverviewRotateButtonContainer>
                      <StudentOverviewRotateButton onClick={() => setIsStudentOverviewSeatRotated(prev => !prev)} title="180도 회전">
                        <StudentOverviewRotateIconWrapper>
                          <StudentOverviewRotateArrowIcon>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 4v16M12 4l-4 4M12 4l4 4M12 20l-4-4M12 20l4-4" />
                            </svg>
                          </StudentOverviewRotateArrowIcon>
                          <StudentOverviewRotateNumberIcon>180</StudentOverviewRotateNumberIcon>
                        </StudentOverviewRotateIconWrapper>
                      </StudentOverviewRotateButton>
                    </StudentOverviewRotateButtonContainer>
                    {seatUnassignedStudents.length > 0 && (
                      <>
                        <StudentOverviewUnassignedTitle>자리 미지정 학생</StudentOverviewUnassignedTitle>
                        <StudentOverviewGrid>
                          {seatUnassignedStudents.map((student) => (
                            <StudentOverviewCard
                              key={student.studentId}
                              $seat
                              $clickable={student.pendingCount > 0}
                              onClick={student.pendingCount > 0 ? () => handleOpenStudentIncompleteModal(student) : undefined}
                            >
                              <StudentOverviewProfileWrapper $seat>
                                <StudentOverviewProfileImage
                                  imageId={resolveOverviewProfileByStudentId(student.studentId, student.profileImage)}
                                  alt={`${student.studentName} 프로필`}
                                />
                              </StudentOverviewProfileWrapper>
                              <StudentOverviewMain $seat>
                                <StudentOverviewTopRow $seat>
                                  <StudentOverviewStudentInfoGroup>
                                    <StudentOverviewNumber>{String(student.studentNumber).padStart(2, '0')}</StudentOverviewNumber>
                                    <StudentOverviewName>{student.studentName}</StudentOverviewName>
                                  </StudentOverviewStudentInfoGroup>
                                </StudentOverviewTopRow>
                                <StudentOverviewCountsColumn $seat>
                                {student.pendingCount > 0 ? (
                                  <>
                                    <StudentOverviewStatusBadge $variant="incomplete" $seat>
                                      <StudentOverviewStatusBadgeLabel $seat>미완료 과제</StudentOverviewStatusBadgeLabel>
                                    </StudentOverviewStatusBadge>
                                    <StudentOverviewPendingCountText $seat>
                                      {student.pendingCount}개
                                    </StudentOverviewPendingCountText>
                                  </>
                                ) : (
                                  <StudentOverviewStatusBadge $variant="complete" $seat>
                                    <StudentOverviewStatusBadgeLabel $seat>모든 과제 완료</StudentOverviewStatusBadgeLabel>
                                  </StudentOverviewStatusBadge>
                                )}
                                </StudentOverviewCountsColumn>
                              </StudentOverviewMain>
                            </StudentOverviewCard>
                          ))}
                        </StudentOverviewGrid>
                      </>
                    )}
                  </StudentOverviewSeatArea>
                )
              )}
            </StudentOverviewModalBody>
          </StudentOverviewModalContent>
        </StudentOverviewModalOverlay>
      )}

      {isStudentIncompleteModalOpen && (
        <StudentIncompleteModalOverlay onClick={() => setIsStudentIncompleteModalOpen(false)}>
          <StudentIncompleteModalContent onClick={(e) => e.stopPropagation()}>
            <IncompleteModalHeader>
              <IncompleteModalTitle>
                {selectedOverviewStudent ? `${selectedOverviewStudent.studentNumber}번 ${selectedOverviewStudent.studentName} 미확인 과제` : '미확인 과제'}
              </IncompleteModalTitle>
              <IncompleteModalActions>
                <IncompleteCloseButton onClick={() => setIsStudentIncompleteModalOpen(false)}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </IncompleteCloseButton>
              </IncompleteModalActions>
            </IncompleteModalHeader>
            <IncompleteModalBody>
              {isLoadingIncompleteData ? (
                <LoadingMessage>데이터를 불러오는 중...</LoadingMessage>
              ) : selectedStudentIncompleteAssignments.length === 0 ? (
                <EmptyMessage>미확인 과제가 없습니다.</EmptyMessage>
              ) : (
                <StudentIncompleteSectionBox>
                  <StudentIncompleteSectionTitleBox>
                    <StudentIncompleteSectionTitleText>미확인 과제</StudentIncompleteSectionTitleText>
                  </StudentIncompleteSectionTitleBox>
                  <StudentIncompleteAssignmentList>
                    {selectedStudentIncompleteAssignments.map(({ assignment, type, routineDateId, routineDate }, index) => (
                      <StudentIncompleteAssignmentBlock
                        key={type === 'routine' ? `${assignment.id}-${routineDateId}` : `${assignment.id}-${index}`}
                        onClick={() => handleOpenCheckFromStudentIncomplete(assignment, type, routineDateId, routineDate)}
                      >
                        <StudentIncompleteAssignmentHeader>
                          <StudentIncompleteTypeBadge
                            $type={type}
                            $projectColor={type === 'project' ? (assignment as any).color : undefined}
                          >
                            {type === 'general' ? '일반과제' : type === 'project' ? '프로젝트' : '루틴과제'}
                          </StudentIncompleteTypeBadge>
                          <StudentIncompleteAssignmentTitle>{assignment.title}</StudentIncompleteAssignmentTitle>
                        </StudentIncompleteAssignmentHeader>
                        <StudentIncompleteAssignmentInfo>
                          마감: {formatDueDate(type === 'routine' ? routineDate : assignment.dueDate)} • 획득포인트: {(assignment as any).basePoints ?? 0}점
                        </StudentIncompleteAssignmentInfo>
                      </StudentIncompleteAssignmentBlock>
                    ))}
                  </StudentIncompleteAssignmentList>
                </StudentIncompleteSectionBox>
              )}
            </IncompleteModalBody>
          </StudentIncompleteModalContent>
        </StudentIncompleteModalOverlay>
      )}

      <ProfileEditModal
        isOpen={isProfileModalOpen}
        user={user}
        onSave={onUpdateUser}
        onCancel={() => setIsProfileModalOpen(false)}
        onLogout={onLogout}
      />

      {/* 과제 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={isDeleteConfirmModalOpen}
        onClose={() => {
          setIsDeleteConfirmModalOpen(false);
          setAssignmentToDelete(null);
        }}
        onConfirm={executeDeleteAssignment}
        title={[
          "이 과제를 삭제할까요?",
          "과제를 삭제하면 과제에 포함된",
          "멤버 정보도 함께 삭제됩니다."
        ]}
        confirmText="삭제"
        cancelText="취소"
        icon="!"
        iconBackground="var(--status-exempted)"
      />

      {/* 루틴 과제 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={isRoutineDeleteConfirmModalOpen}
        onClose={() => {
          setIsRoutineDeleteConfirmModalOpen(false);
          setRoutineToDelete(null);
        }}
        onConfirm={executeDeleteRoutineAssignment}
        title={[
          "이 루틴 과제를 삭제할까요?",
          "루틴 과제를 삭제하면 관련된",
          "모든 기록도 함께 삭제됩니다."
        ]}
        confirmText="삭제"
        cancelText="취소"
        icon="!"
        iconBackground="var(--status-exempted)"
      />

      {/* 프로젝트 과제 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={isProjectDeleteConfirmModalOpen}
        onClose={() => {
          setIsProjectDeleteConfirmModalOpen(false);
          setProjectToDelete(null);
        }}
        onConfirm={executeDeleteProjectAssignment}
        title={[
          "이 프로젝트 과제를 삭제할까요?",
          "프로젝트 과제를 삭제하면 과제에 포함된",
          "하위 과제 및 멤버 정보도 함께 삭제됩니다."
        ]}
        confirmText="삭제"
        cancelText="취소"
        icon="!"
        iconBackground="var(--status-exempted)"
      />
      
      {/* 🎯 DateInputModal */}
      <DateInputModal
        isOpen={isDateInputModalOpen}
        onClose={() => setIsDateInputModalOpen(false)}
        onConfirm={handleDateInputConfirm}
      />

      {/* 🎯 날짜 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={isDateDeleteConfirmModalOpen}
        onClose={() => {
          setIsDateDeleteConfirmModalOpen(false);
          setDateToDelete(null);
        }}
        onConfirm={executeDateDelete}
        title={[
          '날짜 삭제 확인',
          '',
          dateToDelete 
            ? `${dateToDelete.date.toLocaleDateString('ko-KR')} 날짜의 과제 데이터를 완전히 삭제하시겠습니까?`
            : '',
          '',
          '이 작업은 되돌릴 수 없으며, 해당 날짜의 모든 멤버 상태 데이터가 영구적으로 삭제됩니다.'
        ]}
        confirmText="삭제"
        cancelText="취소"
        icon="!"
        iconBackground="var(--status-exempted)"
      />

      {/* 프로젝트 과제 설정 모달 */}
      <ProjectAssignmentSettingsModal
        isOpen={isProjectSettingsModalOpen}
        onClose={() => {
          setIsProjectSettingsModalOpen(false);
          setEditingProject(null); // 편집 상태 초기화
        }}
        onSave={async () => {
          setIsProjectSettingsModalOpen(false);
          // 프로젝트 과제 목록 새로고침 (데이터 갱신을 먼저 수행)
          await loadData();
          setEditingProject(null); // 편집 상태 초기화 (데이터 로드 후)
        }}
        members={members}
        memberGroups={memberGroups}
        memberGroupOrder={currentClass?.memberGroupOrder}
        currentMemberGroup={currentMemberGroup}
        memberGroupNames={currentClass?.memberGroupNames}
        currentClassId={currentClass?.id || ''}
        userId={user?.id || ''}
        user={user}
        editingProject={editingProject} // 편집할 프로젝트 정보 전달
        defaultPoints={defaultPoints}
        onLicenseClick={onLicenseClick}
      />

      {/* 프로젝트 과제 체크 모달 */}
      {selectedProjectAssignment && (
        <AssignmentCheckModal
          isOpen={isProjectCheckModalOpen}
          onClose={() => {
            setIsProjectCheckModalOpen(false);
            setSelectedProjectAssignment(null);
            setSelectedSubTaskId(null);
            setCurrentSubTaskIndex(0);
          }}
          userId={user?.id}
          classId={currentClass?.id}
          userType={user?.userType}
          memberGroup={currentMemberGroup}
          statusDefaultPoints={statusDefaultPoints}
          defaultPoints={defaultPoints}
          onSave={async (studentStatuses) => {
            try {
              console.log('🔍 프로젝트 과제 체크 모달 저장 시작:', studentStatuses);
              
              if (user?.id && currentClass?.id && selectedProjectAssignment && selectedSubTaskId) {
                // 배치로 한 번에 저장 (성능 최적화)
                await projectAssignmentService.batchUpdateSubTaskStudentStatuses(
                  user.id,
                  currentClass.id,
                  selectedProjectAssignment.id,
                  selectedSubTaskId,
                  studentStatuses.map(status => {
                    const update: any = {
                      studentId: status.studentId,
                      status: status.status,
                      earnedPoints: status.earnedPoints,
                      memo: status.memo || ''
                    };
                    
                    // 제출 확인 데이터 포함 (있는 경우)
                    if (status.hasOwnProperty('submissionLink')) {
                      update.submissionLink = status.submissionLink;
                    }
                    if (status.hasOwnProperty('submissionMessage')) {
                      update.submissionMessage = status.submissionMessage;
                    }
                    if (status.hasOwnProperty('submittedAt')) {
                      update.submittedAt = status.submittedAt;
                    }
                    if (status.hasOwnProperty('submissionImagePath')) {
                      update.submissionImagePath = (status as any).submissionImagePath;
                    }
                    if (status.hasOwnProperty('submissionImageUpdatedAt')) {
                      update.submissionImageUpdatedAt = (status as any).submissionImageUpdatedAt;
                    }
                    if (status.hasOwnProperty('submissionImageSizeBytes')) {
                      update.submissionImageSizeBytes = (status as any).submissionImageSizeBytes;
                    }
                    
                    return update;
                  }),
                  currentMemberGroup
                );
                
                console.log('✅ 프로젝트 과제 상태 배치 저장 완료');
                
                // 저장 완료 후 프로젝트 하위과제 상태 새로고침
                if (selectedProjectForDetail) {
                  try {
                    const result = await projectAssignmentService.loadProjectAssignmentWithStatuses(
                      user.id,
                      currentClass.id,
                      selectedProjectForDetail.id,
                      currentMemberGroup
                    );
                    setProjectSubTaskSummaries(result.subTaskSummaries);
                    
                    // 메인 프로젝트 블럭의 제출 확인 합계도 업데이트
                    const totalSubmittedCount = result.subTaskSummaries.reduce(
                      (sum, summary) => sum + (summary.submittedStudents || 0),
                      0
                    );
                    
                    setProjectAssignments(prev => prev.map(proj => 
                      proj.id === selectedProjectForDetail.id
                        ? { ...proj, submittedCount: totalSubmittedCount }
                        : proj
                    ));
                  } catch (error) {
                    console.error('프로젝트 하위과제 데이터 새로고침 실패:', error);
                  }
                }
              }
            } catch (error) {
              console.error('프로젝트 과제 상태 저장 실패:', error);
              throw error; // 에러를 다시 throw하여 AssignmentCheckModal에서 처리하도록 함
            }
          }}
          mode="project"
          assignment={selectedProjectAssignment}
          projectAssignment={selectedProjectAssignment}
          projectSubTasks={projectSubTaskSummaries}
          selectedSubTaskId={selectedSubTaskId}
          currentSubTaskIndex={currentSubTaskIndex}
          onSubTaskChange={async (newIndex: number) => {
            setCurrentSubTaskIndex(newIndex);
            if (projectSubTaskSummaries[newIndex]) {
              const newSubTaskId = projectSubTaskSummaries[newIndex].subTask.id;
              setSelectedSubTaskId(newSubTaskId);
              
              // 새로운 하위 과제의 멤버 상태 로드
              if (user?.id && currentClass?.id && selectedProjectForDetail) {
                try {
                  console.log('🔍 하위 과제 변경 - 멤버 상태 로드 시작:', {
                    projectId: selectedProjectForDetail.id,
                    subTaskId: newSubTaskId
                  });
                  
                  const existingStatuses = await projectAssignmentService.loadProjectAssignmentStudentStatuses(
                    user.id,
                    currentClass.id,
                    selectedProjectForDetail.id,
                    newSubTaskId,
                    currentMemberGroup
                  );
                  
                  console.log('✅ 하위 과제 변경 - 멤버 상태 로드 완료:', existingStatuses);
                  setProjectStudentStatuses(existingStatuses);
                } catch (error) {
                  console.error('하위 과제 변경 - 멤버 상태 로드 실패:', error);
                  setProjectStudentStatuses([]);
                }
              }
            }
          }}
          members={members}
          existingStatuses={projectStudentStatuses}
        />
      )}

      {/* 일반 과제 보상 아이템 카드 선택 모달 */}
      <ItemCardSelectionModal
        isOpen={isAssignmentRewardCardModalOpen}
        onClose={() => {
          setIsAssignmentRewardCardModalOpen(false);
          setAssignmentRewardCardCallback(null);
        }}
        onConfirm={(selectedItemCardIds) => {
          if (assignmentRewardCardCallback) {
            assignmentRewardCardCallback(selectedItemCardIds);
          }
          setIsAssignmentRewardCardModalOpen(false);
          setAssignmentRewardCardCallback(null);
        }}
        userId={user.id}
        classId={currentClass?.id || ''}
        initialSelectedIds={assignmentRewardCardIds}
        categoryFilter={['assignment']}
      />
      
      {/* 토스트 메시지 */}
      {showToast && (
        <Toast
          message={toastMessage}
          onClose={() => setShowToast(false)}
          confirmOnly={toastRequiresConfirm}
        />
      )}
    </PageContainer>
  );
};

export default AssignmentsPage;

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--color-bg);
  display: flex;
  flex-direction: column;
  padding-top: 36px; /* 상단 헤더 공간 확보 */
  padding-bottom: 32px; /* 하단 네비바 공간 확보 */
`;

const Content = styled.div`
  position: relative;
  padding-top: 12px;
  padding-left: 16px;
  padding-right: 16px;
  padding-bottom: 40px;
  max-width: 600px;
  width: 100%;
  margin: 0 auto;

  @media (max-width: 480px) {
    padding-top: 8px;
  }

  @media (max-width: 360px) {
    padding-top: 4px;
  }
`;

const AssignmentsPageBottomLogo = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 100px;
  padding-bottom: 48px;
  padding-left: 20px;
  padding-right: 20px;
  margin-top: 0;

  img {
    height: 48px;
    width: auto;
    object-fit: contain;
    opacity: 0.12;
    filter: brightness(0) saturate(0);
  }

  :root:not([data-theme='light']) & img {
    opacity: 0.1;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 8px 0 0 0;
  flex-wrap: nowrap;
  position: relative;
  
  @media (max-width: 480px) {
    margin: 4px 0 0 0;
    gap: 6px;
  }
  
  @media (max-width: 380px) {
    margin: 2px 0 0 0;
    gap: 4px;
  }
`;

const TopBarRightActions = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CircleToggleButton = styled.button<{ $active: boolean }>`
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  border-radius: 999px;
  border: 1px solid var(--color-border);
  background: ${props => props.$active ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${props => props.$active ? '#ffffff' : 'var(--color-text-primary)'};
  display: flex;
  align-items: center;
  
  /* 다크 모드에서 선택된 원형 v 토글 버튼: 흰색 바탕에 진한 회색 v */
  :root:not([data-theme='light']) & {
    background: ${props => props.$active ? '#ffffff' : 'var(--color-surface)'};
    color: ${props => props.$active ? '#333333' : 'var(--color-text-primary)'};
    border-color: ${props => props.$active ? '#ffffff' : 'var(--color-border)'};
  }
  justify-content: center;
  padding: 0;
  cursor: pointer;

  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const IconActionButton = styled.button`
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;

  &:hover { background: var(--color-surface-highlight); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.3rem;
  color: var(--color-text-primary);
  white-space: nowrap;
  flex-shrink: 0;
  text-align: center;
`;

/* 원형 버튼 행: 왼쪽(테이블/카드) | 오른쪽(미완료) */
const TopButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  margin-bottom: 16px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 600px) {
    flex-wrap: wrap;
  }
`;

/* 과제 관리 테이블/카드 — 왼쪽 정렬 */
const TableButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
  flex: 1;
  box-sizing: border-box;
  
  @media (max-width: 600px) {
    gap: 16px;
  }
`;

const CenterButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`;

/* 원형 버튼 호버 시 타이틀 표시 (보드 페이지와 동일 스타일) */
const AssignmentsTooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &[data-tooltip]::after,
  &[data-tooltip]::before {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.1s ease;
  }

  &[data-tooltip]::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + 4px);
    left: 50%;
    transform: translateX(-50%);
    background: rgba(var(--color-accent-rgb), 0.92);
    color: #fff;
    font-size: 11px;
    font-weight: 500;
    padding: 4px 8px;
    border-radius: 6px;
    white-space: nowrap;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 10;
  }

  &[data-tooltip]::before {
    content: '';
    position: absolute;
    bottom: calc(100% + 3px);
    left: 50%;
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid rgba(var(--color-accent-rgb), 0.92);
    z-index: 9;
  }

  &:hover::after,
  &:hover::before {
    opacity: 1;
  }
`;

/* 원형 아이콘 버튼 (과제 관리 테이블/카드) — 전체 크기 80% */
const TableCardMainButton = styled.button`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    border-color: var(--color-text-muted);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  img {
    width: 43px;
    height: 43px;
    filter: invert(18%) sepia(25%) saturate(1386%) hue-rotate(129deg) brightness(88%) contrast(98%);
  }

  :root:not([data-theme='light']) & img {
    filter: brightness(0) invert(1);
  }

  @media (max-width: 600px) {
    width: 58px;
    height: 58px;

    img {
      width: 38px;
      height: 38px;
    }
  }
`;

/* 미완료 과제 확인 — 오른쪽 정렬 */
const IncompleteButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 20px;
  flex: 1;
  box-sizing: border-box;
  
  @media (max-width: 600px) {
    gap: 16px;
  }
`;

const StudentOverviewModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1050;
  padding: calc(80px / var(--app-page-zoom, 1)) 20px;
  overflow-y: auto;
`;

const StudentOverviewModalContent = styled.div`
  width: min(1560px, calc(100vw - 24px));
  max-height: calc((100vh - 160px) / var(--app-page-zoom, 1));
  background: var(--color-bg);
  border: var(--modal-shell-border);
  border-radius: 16px;
  box-shadow: var(--modal-shadow-xlarge);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const StudentOverviewModalHeader = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border);

  @media (min-width: 520px) {
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    min-height: 52px;
    gap: 0;
  }
`;

const StudentOverviewModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
  flex: 1;
  min-width: 0;
  text-align: left;
`;

const StudentOverviewModeToggleCenter = styled.div.attrs({
  className: 'touch-compact',
})<{ $isSeatMode: boolean }>`
  position: relative;
  display: flex;
  background: var(--color-accent-strong);
  border-radius: 13px;
  padding: 2px;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 26px;
  min-width: 120px;
  width: auto;
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$isSeatMode ? '50%' : '2px'};
    width: calc(50% - 2px);
    height: calc(100% - 4px);
    background: var(--color-surface);
    border-radius: 11px;
    transition: left 0.3s ease;
    z-index: 1;
  }
`;

const StudentOverviewModeOption = styled.div<{ $isActive: boolean }>`
  flex: 1;
  padding: 2px 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.$isActive ? 'var(--color-toggle-active)' : 'var(--color-toggle-inactive)'};
  text-align: center;
  position: relative;
  z-index: 2;
  transition: color 0.3s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`;

const StudentOverviewHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  justify-content: flex-end;
`;

const StudentOverviewRotateButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 8px;
`;

const StudentOverviewRotateButton = styled.button`
  width: 60px;
  height: 26px;
  min-height: 26px;
  max-height: 26px;
  min-width: 60px;
  box-sizing: border-box;
  line-height: 1;
  background: var(--color-grid-alignment-container);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 2px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &:hover {
    border-color: var(--color-border);
    background: var(--color-grid-alignment-container);
  }
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  &:active {
    transform: none;
  }
`;

const StudentOverviewRotateIconWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: var(--color-grid-alignment-active-bg);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
`;

const StudentOverviewRotateArrowIcon = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  transform: scale(1);
  transition: opacity 0.3s ease, transform 0.3s ease;

  svg {
    width: 22px;
    height: 22px;
  }
  :root[data-theme='light'] & svg {
    stroke: #717785;
  }
  :root:not([data-theme='light']) & svg {
    stroke: rgba(255, 255, 255, 0.85);
  }
  ${StudentOverviewRotateButton}:hover & {
    opacity: 0;
    transform: scale(0.8);
  }
`;

const StudentOverviewRotateNumberIcon = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: currentColor;
  opacity: 0;
  transform: scale(0.8);
  transition: opacity 0.3s ease, transform 0.3s ease;
  ${StudentOverviewRotateButton}:hover & {
    opacity: 1;
    transform: scale(1);
  }
`;

const StudentOverviewModalBody = styled.div`
  background: var(--color-bg);
  padding: 18px 20px 20px;
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const StudentOverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 1400px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 800px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

/** AssignmentCardPage StudentsGrid 와 동일 — 번호 모드 학생 블럭 정렬 */
const StudentOverviewNumberModeStudentsGrid = styled.div<{ $alignment?: 'left' | 'center' }>`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px;
  justify-content: ${props => (props.$alignment === 'center' ? 'center' : 'flex-start')};

  & > * {
    flex: 0 0 auto;
    width: calc((100% - 64px) / 5);
    min-width: 180px;
    max-width: calc((100% - 64px) / 5);
  }

  @media (max-width: 1400px) {
    & > * {
      width: calc((100% - 48px) / 4);
      max-width: calc((100% - 48px) / 4);
    }
  }

  @media (max-width: 1100px) {
    & > * {
      width: calc((100% - 32px) / 3);
      max-width: calc((100% - 32px) / 3);
    }
  }

  @media (max-width: 800px) {
    & > * {
      width: calc((100% - 16px) / 2);
      max-width: calc((100% - 16px) / 2);
    }
  }

  @media (max-width: 500px) {
    & > * {
      width: 100%;
      max-width: none;
    }
  }
`;

/** PointsTablePage 그리드 정렬 토글과 동일 */
const StudentOverviewGridAlignmentContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px;
`;

const StudentOverviewGridAlignmentToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 2px;
  height: 26px;
  min-height: 26px;
  max-height: 26px;
  min-width: 60px;
  box-sizing: border-box;
  line-height: 1;
  background: var(--color-grid-alignment-container);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &:hover {
    border-color: var(--color-border);
    background: var(--color-grid-alignment-container);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const StudentOverviewGridAlignmentOption = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 20px;
  border-radius: 4px;
  background: ${props => (props.$isActive ? 'var(--color-grid-alignment-active-bg)' : 'transparent')};
  color: ${props =>
    props.$isActive ? 'var(--color-grid-alignment-active-icon)' : 'var(--color-grid-alignment-icon)'};
  box-shadow: ${props => (props.$isActive ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none')};
  transition: all 0.2s ease;
`;

const StudentOverviewCard = styled.div<{ $seat?: boolean; $clickable?: boolean }>`
  display: grid;
  grid-template-columns: ${props => (props.$seat ? 'auto 1fr' : 'minmax(0, 104px) 1fr')};
  align-items: stretch;
  gap: ${props => props.$seat ? '4px' : '8px'};
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  border-radius: ${props => props.$seat ? '10px' : '8px'};
  padding: ${props => props.$seat ? '4px 4px 6px 4px' : '8px'};
  height: ${props => props.$seat ? '100%' : '120px'};
  width: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  transition: all 0.2s;
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};

  :root[data-theme='light'] & {
    background: rgba(255, 255, 255, 0.6);
  }

  ${props => props.$clickable && `
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-color: rgba(var(--color-accent-rgb), 0.5);
  }

  :root:not([data-theme='light']) &:hover {
    border-color: rgba(255, 255, 255, 0.6);
    box-shadow: 0 4px 12px rgba(255, 255, 255, 0.15);
  }
  `}
`;

const StudentOverviewProfileWrapper = styled.div<{ $seat?: boolean }>`
  ${props =>
    props.$seat
      ? `
    width: 80px;
    min-width: 80px;
    max-width: 80px;
    height: 80px;
    min-height: 80px;
    max-height: 80px;
    border-radius: 10px;
  `
      : `
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    max-width: 100%;
    max-height: 100%;
    aspect-ratio: 1;
    align-self: center;
    justify-self: stretch;
    border-radius: 10px;
  `}
  overflow: hidden;
  border: 2px solid #000000;
  background: transparent;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const StudentOverviewProfileImage = styled(CachedProfileImage)`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  max-width: 100%;
`;

const StudentOverviewMain = styled.div<{ $seat?: boolean }>`
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  align-self: stretch;
  height: ${props => (props.$seat ? 'auto' : '100%')};
  justify-content: flex-start;
`;

const StudentOverviewTopRow = styled.div<{ $seat?: boolean }>`
  display: flex;
  align-items: ${props => (props.$seat ? 'center' : 'baseline')};
  justify-content: flex-start;
  margin-bottom: ${props => (props.$seat ? '1px' : '4px')};
`;

const StudentOverviewStudentInfoGroup = styled.div`
  display: flex;
  gap: 4px;
  align-items: baseline;
  flex: 1;
`;

const StudentOverviewNumber = styled.span`
  font-size: 16px;
  color: var(--color-text-gray);
  font-weight: 700;
  flex-shrink: 0;
`;

const StudentOverviewName = styled.span`
  color: var(--color-text-primary);
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const StudentOverviewCountsColumn = styled.div<{ $seat?: boolean; $pushToBottom?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: ${props => {
    if (props.$seat) return '2px';
    if (props.$pushToBottom) return 'auto';
    return '0';
  }};
  align-items: flex-start;
  flex-shrink: 0;
`;

const StudentOverviewStatusBadge = styled.div<{ $variant: 'incomplete' | 'complete'; $seat?: boolean }>`
  background: ${props =>
    props.$variant === 'incomplete' ? 'var(--status-pending)' : 'var(--color-accent-strong)'};
  color: #fff;
  border-radius: 8px;
  padding: ${props => (props.$seat ? '2px 7px' : '4px 10px')};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: fit-content;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 500px) {
    ${props =>
      !props.$seat &&
      `
      padding: 3px 8px;
      border-radius: 6px;
    `}
  }
`;

const StudentOverviewStatusBadgeLabel = styled.div<{ $seat?: boolean }>`
  font-size: ${props => (props.$seat ? '11px' : '13px')};
  font-weight: 800;
  letter-spacing: -0.2px;
  line-height: 1.25;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
  word-break: keep-all;
  overflow-wrap: break-word;
  max-width: 100%;

  @media (max-width: 500px) {
    ${props => !props.$seat && `font-size: 11px;`}
  }
`;

const StudentOverviewPendingCountText = styled.div<{ $seat?: boolean }>`
  font-size: ${props => (props.$seat ? '22px' : '30px')};
  font-weight: 900;
  line-height: 1.1;
  margin-top: 0px;
  color: var(--status-pending); /* 미확인(미완료) 컬러 */
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;

  @media (max-width: 500px) {
    ${props => !props.$seat && `font-size: 22px;`}
  }
`;

const StudentOverviewSeatArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

/** AssignmentCardPage SeatTableContainer 와 동일 */
const StudentOverviewSeatTableOuter = styled.div`
  background: transparent;
  border: 2px dashed var(--color-border);
  border-radius: 8px;
  overflow: hidden;
  overflow-x: auto;
  box-shadow: none;
`;

/** AssignmentCardPage SeatGridScroll 와 동일 */
const StudentOverviewSeatGridScroll = styled.div`
  width: 100%;
  overflow: auto;
  padding: 12px;
`;

const StudentOverviewSeatGrid = styled.div<{ $gridTemplateColumns: string; $gridTemplateRows: string }>`
  display: grid;
  grid-template-columns: ${props => props.$gridTemplateColumns};
  grid-template-rows: ${props => props.$gridTemplateRows};
  gap: 4px;
  width: 100%;
  min-width: max-content;
  align-content: start;
  justify-content: center;
`;

const StudentOverviewSeatCell = styled.div`
  width: 100%;
  min-height: 84px;
  min-width: 144px;
  display: flex;
  align-items: stretch;
  justify-content: center;
`;

const StudentOverviewUnassignedTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: var(--color-text-secondary);
  padding: 0 2px;
`;

/* 원형 아이콘 버튼 (멤버별/과제별 미완료 확인) — 전체 크기 80% */
const IncompleteCircularButton = styled.button`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    border-color: var(--color-text-muted);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  img {
    width: 43px;
    height: 43px;
    filter: invert(18%) sepia(25%) saturate(1386%) hue-rotate(129deg) brightness(88%) contrast(98%);
  }

  :root:not([data-theme='light']) & img {
    filter: brightness(0) invert(1);
  }

  @media (max-width: 600px) {
    width: 58px;
    height: 58px;

    img {
      width: 38px;
      height: 38px;
    }
  }
`;

// 기존 PlusIcon은 제거 (새로운 PlusIcon으로 대체)

const FloatingActionButton = styled.button`
  position: fixed;
  bottom: calc(env(safe-area-inset-bottom, 0px) + 60px);
  right: 24px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: var(--color-accent-strong);
  color: white;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(var(--color-accent-rgb), 0.3);
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: var(--color-accent);
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(var(--color-accent-rgb), 0.4);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    /* 가로가 짧아져도 저장 호버 버튼처럼 고정된 높이로 유지 */
    bottom: calc(env(safe-area-inset-bottom, 0px) + 60px);
    right: 20px;
    font-size: 18px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: var(--color-text-primary);
  padding: 20px 20px;
  background: var(--color-panel);
  border-radius: 12px;
  margin-top: 20px;
  border: 1px solid var(--color-border);
`;

const EmptyStateTitle = styled.h3`
  margin-bottom: 10px;
  font-size: 1.2rem;
  color: var(--color-text-primary);
`;

const EmptyStateText = styled.p`
  margin: 0;
  color: var(--color-text-gray);
`;

const FilterSelectContainer = styled.div`
  position: relative;
  width: auto;
  min-width: fit-content;
`;

const FilterSelectButton = styled.button`
  width: 100%;
  padding: 8px 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--color-text-secondary);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  box-shadow: none;
  transition: all 0.2s ease;

  &:hover {
    background: transparent;
    color: var(--color-text-secondary);
  }

  &:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

const FilterOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--color-border);
  }
`;

const ColorDot = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.color};
`;

const FilterDropdownOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
`;

// 기존 FilterDropdown은 제거 (새로운 FilterDropdown으로 대체)

// 기존 FilterRow는 제거 (새로운 FilterRow로 대체)

const BlocksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;

  @media (max-width: 480px) {
    margin-top: 4px;
    gap: 8px;
  }

  @media (max-width: 360px) {
    margin-top: 2px;
    gap: 6px;
  }
`;

const MenuDropdownOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999; /* 햄버거 메뉴보다 아래에 위치 */
`;

const MenuDropdown = styled.div`
  position: fixed;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1101;
  overflow: hidden;
  width: 160px; /* 일반/루틴 과제용 기본 너비 */
`;

const ProjectMenuDropdown = styled.div`
  position: fixed;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1101;
  overflow: hidden;
  width: 200px; /* 프로젝트 과제용 더 넓은 너비 */
`;

const MenuDropdownOption = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  color: var(--color-text-secondary);
  
  &:hover {
    background-color: var(--color-panel);
    transform: scale(1.02);
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
  }
  
  svg {
    margin-right: 8px;
    flex-shrink: 0;
  }
`;

// 루틴 과제 상세 보기 스타일 컴포넌트들
const RoutineDetailSection = styled.div`
  margin-top: 30px;
  padding: 20px;
  background: var(--color-panel);
  border-radius: 12px;
  border: 1px solid var(--color-border);
  position: relative;
`;

// 프로젝트 과제 상세 보기 스타일 컴포넌트들
const ProjectDetailSection = styled.div`
  margin-top: 30px;
  padding: 20px;
  background: var(--color-panel);
  border-radius: 12px;
  border: 1px solid var(--color-border);
  position: relative;
`;

const BackButtonContainer = styled.div`
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
`;

const BackButton = styled.button`
  background: var(--color-surface);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border);
  border-radius: 25px;
  width: 100px;
  height: 36px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  svg {
    width: 16px;
    height: 16px;
    margin-right: 6px;
  }
`;

const MonitoringHeader = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
`;

const MonitoringTitle = styled.h5`
  margin: 0;
  font-size: 1rem;
  color: var(--color-text-primary);
  text-align: center;
`;

const MonitoringButton = styled.button`
  padding: 6px 12px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;



const DateBlocksSection = styled.div`
  margin-top: 20px;
`;

// 프로젝트 보상 지급 버튼 컨테이너
const ProjectRewardButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 8px 0 24px 0;
`;

// 프로젝트 보상 지급 버튼 (모달 내 프로젝트/루틴 과제 보상 지급 버튼)
const ProjectRewardButton = styled.button`
  padding: 6px 16px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 🎯 필터 관련 스타일 컴포넌트들 (기존 드롭다운과 동일한 스타일)
const FilterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0px;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  background: var(--color-panel);
  border-radius: 8px;
  padding: 4px;
`;

// 🎯 드롭다운 스타일 컴포넌트들 (기존 SortControl과 동일한 스타일)
const FilterDropdownItem = styled.div`
  position: relative;
`;

const FilterDropdownButton = styled.button<{ $active: boolean }>`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: var(--color-border);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0,0,0,0.3);
  }

  svg {
    transition: transform 0.2s ease;
  }
`;

const FilterDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 140px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  margin-top: 2px;
`;

const FilterDivider = styled.div`
  height: 1px;
  background: var(--color-border);
  margin: 4px 0;
`;

const FilterDropdownOption = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: var(--color-panel);
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f1f3f4;
  }
`;

// 🎯 작은 회색 원 스타일 컴포넌트
const FilterDot = styled.div`
  width: 6px;
  height: 6px;
  background-color: var(--color-text-muted);
  border-radius: 50%;
  flex-shrink: 0;
`;

const AddButton = styled.button.attrs({ type: 'button', className: 'touch-compact' })`
  background: var(--color-text-muted); /* 🎯 더 연한 순수 회색 */
  color: white;
  border: none;
  border-radius: 50%;
  width: 28px; /* 🎯 더 작게 변경 */
  height: 28px; /* 🎯 더 작게 변경 */
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  
  &:hover {
    background: var(--color-text-muted); /* 🎯 호버 시 약간 더 진한 회색 */
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const PlusIcon = styled.span`
  font-size: 18px;
  font-weight: bold;
  line-height: 1;
`;

const SectionTitle = styled.h5`
  font-size: 16px;
  font-weight: bold;
  margin: 0 0 15px 0;
  color: var(--color-text-primary);
`;

const MonitoringModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 20px;
`;

const MonitoringModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 100%;
  max-width: 728px; /* 기존(520px)의 140% */
  border: var(--modal-shell-border);
  box-shadow: var(--modal-shadow-large);
  display: flex;
  flex-direction: column;
  overflow: hidden; /* list가 modal 바깥으로 튀어나가지 않게 */
  height: min(calc((100vh - 160px) / var(--app-page-zoom, 1)), calc(650px / var(--app-page-zoom, 1))); /* list가 남는 높이를 확실히 채우게 */

  @media (max-width: 768px) {
    max-width: 95vw;
  }
`;

const MonitoringModalTitle = styled.h4`
  margin: 0;
  padding: 20px 20px 10px 20px;
  text-align: center;
  color: var(--color-text-primary);
`;

const MonitoringSortRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 0 20px 12px 20px;
  margin: 0;
  border-bottom: 1px solid var(--color-border);
  box-sizing: border-box;
`;

const MonitoringSortButtons = styled.div`
  display: inline-flex;
  border: 1px solid var(--color-border);
  border-radius: 999px;
  overflow: hidden;
`;

const MonitoringSortButton = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border: none;
  background: ${({ $active }) => $active ? 'var(--color-accent-strong)' : 'transparent'};
  color: ${({ $active }) => $active ? '#ffffff' : 'var(--color-text-secondary)'};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:not(:last-child) {
    border-right: 1px solid var(--color-border);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const MonitoringLoading = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--color-text-muted);
  padding: 20px;
`;

const MonitoringList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1; /* 헤더 아래 영역 전체를 스크롤 */
  padding: 16px 20px 16px 20px; /* 스크롤 카드 좌우 + 마지막 하단 여백 */
  max-height: none;
  overflow-y: auto;
`;

const MonitoringCard = styled.div`
  background: var(--color-panel);
  border: 1px solid var(--color-border);
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  [data-theme='light'] & {
    border-color: var(--color-block-border);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const MonitoringCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 700;
  margin-bottom: 6px;
`;

const MonitoringPercent = styled.span`
  color: var(--color-accent-strong);
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const MonitoringBar = styled.div`
  width: 100%;
  height: 10px;
  border-radius: 6px;
  background: var(--color-border);
  overflow: hidden;
  position: relative;
`;

const MonitoringBarToday = styled.div`
  height: 100%;
  background: #bfc3c9;
  transition: width 0.2s ease;
  position: absolute;
  left: 0;
  top: 0;
  :root:not([data-theme='light']) & {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const MonitoringBarFill = styled.div`
  height: 100%;
  background: var(--color-accent-strong);
  transition: width 0.2s ease;
  position: absolute;
  left: 0;
  top: 0;
  :root:not([data-theme='light']) & {
    background: #ffffff;
  }
`;

const MonitoringThresholdLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #1f2937;
  opacity: 0.6;
  transform: translateX(-1px);
  :root:not([data-theme='light']) & {
    background: #ffffff;
    opacity: 0.7;
  }
`;

const ProjectMonitoringBar = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${props => Math.max(props.$columns, 1)}, 1fr);
  gap: 4px;
  height: 10px;
  margin-top: 2px;
`;

const ProjectMonitoringSegment = styled.div<{ $isDone: boolean }>`
  background: ${props => (props.$isDone ? 'var(--color-accent-strong)' : 'var(--color-border)')};
  border-radius: 4px;
  height: 100%;
  :root:not([data-theme='light']) & {
    background: ${props => (props.$isDone ? '#ffffff' : 'var(--color-border)')};
  }
`;

const MonitoringMeta = styled.div`
  margin-top: 6px;
  font-size: 0.8rem;
  color: var(--color-text-muted);
`;

const DateBlocksGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DateBlock = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 12px 16px;
  padding-right: 40px; /* 오른쪽에 아이콘 공간 확보 */
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative; /* 휴지통 아이콘 위치 기준 */
  
  &:hover {
    border-color: var(--color-accent-text);
    box-shadow: 0 2px 8px rgba(var(--color-accent-rgb), 0.2);
    background-color: var(--color-panel);
  }
`;

const ProjectSubTaskBlock = styled.div<{ $isCompleted?: boolean }>`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 12px 16px;
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  
  &:hover {
    border-color: var(--color-accent-text);
    box-shadow: 0 2px 8px rgba(var(--color-accent-rgb), 0.2);
    background-color: var(--color-panel);
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const DateInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

const ProjectSubTaskInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    width: 100%;
  }
`;

const DateText = styled.div<{ $isCompleted?: boolean }>`
  font-size: 16px;
  font-weight: bold;
  color: ${props => props.$isCompleted ? '#b0b0b0' : 'var(--color-text-primary)'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  opacity: ${props => props.$isCompleted ? 0.7 : 1};
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const DayText = styled.div<{ $isCompleted?: boolean }>`
  font-size: 12px;
  color: ${props => props.$isCompleted ? '#b0b0b0' : 'var(--color-text-gray)'};
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  opacity: ${props => props.$isCompleted ? 0.6 : 1};
  
  @media (max-width: 768px) {
    text-align: left;
  }
`;

const DesktopDayText = styled.div<{ $isCompleted?: boolean }>`
  font-size: 12px;
  color: ${props => props.$isCompleted ? '#b0b0b0' : 'var(--color-text-gray)'};
  opacity: ${props => props.$isCompleted ? 0.6 : 1};
  display: flex;
  align-items: center;
  gap: 4px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const RoutineDayIcon = styled.span`
  font-size: 14px;
`;

const CustomDayIcon = styled.span`
  font-size: 14px;
  margin-left: 4px;
`;

const StatusText = styled.div<{ $isCompleted?: boolean }>`
  font-size: 14px;
  color: ${props => props.$isCompleted !== undefined ? 
    (props.$isCompleted ? 'var(--color-accent-strong)' : 'var(--status-pending)') : 
    'var(--color-text-gray)'}; // 루틴 과제용 기본 색상 (진한 회색)
  font-weight: ${props => props.$isCompleted !== undefined ? 
    (props.$isCompleted ? '500' : '600') : 
    '500'}; // 루틴 과제용 기본 굵기
  flex-shrink: 0;
  opacity: ${props => props.$isCompleted !== undefined ? 
    (props.$isCompleted ? 0.6 : 0.8) : 
    1}; // 루틴 과제용 기본 투명도
`;

const MobileInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 4px;
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const DesktopStatusText = styled.div<{ $isCompleted?: boolean }>`
  font-size: 14px;
  color: ${props => props.$isCompleted ? 'var(--color-accent-strong)' : 'var(--status-pending)'};
  font-weight: ${props => props.$isCompleted ? '500' : '600'};
  opacity: ${props => props.$isCompleted ? 0.6 : 0.8};
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const DeleteIcon = styled.svg`
  width: 20px;
  height: 20px;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  
  &:hover {
    color: var(--color-text-secondary);
    transform: translateY(-50%) scale(1.1);
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`;

const EmptyDeleteIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  color: #6c757d;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

// 페이지네이션 스타일 컴포넌트
const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 8px 0;
  padding: 0;
`;

const PaginationButton = styled.button<{ disabled: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--color-surface);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 18px;
  font-weight: 600;
  flex-shrink: 0;
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  &:hover:not(:disabled) {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &:disabled {
    cursor: not-allowed;
  }
`;

const PageInfo = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px; /* 버튼과 동일한 높이로 조정 */
  box-sizing: border-box;
`;

// 토스트 메시지 스타일
const ToastOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ToastMessage = styled.div`
  background: rgba(0, 0, 0, 0.85);
  color: white;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const ToastSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// 미완료 과제 모달 스타일
const IncompleteModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding: 80px 20px;
  overflow-y: auto;
  min-height: 100vh;
  
  @media (max-width: 600px) {
    padding: 40px 16px;
  }
`;

const StudentIncompleteModalOverlay = styled(IncompleteModalOverlay)`
  z-index: 1200;
`;

const IncompleteModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 20px;
  width: 95%;
  max-width: 900px;
  overflow: hidden;
  border: var(--modal-shell-border);
  box-shadow: var(--modal-shadow-xlarge);
`;

const StudentIncompleteModalContent = styled(IncompleteModalContent)`
  width: min(600px, calc(100vw - 32px));
  max-width: 600px;
  border-radius: 20px;
  overflow: hidden;
`;

const IncompleteModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
  background: #ffffff;
`;

const IncompleteModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #212529;
`;

const IncompleteModalActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IncompleteActionButton = styled.button`
  width: 36px;
  height: 36px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    border-color: #b0b0b0;
  }

  svg {
    width: 20px;
    height: 20px;
    stroke: #6c757d;
  }
`;

const IncompleteCloseButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    color: #6c757d;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const IncompleteModalBody = styled.div`
  padding: 24px;
  max-height: calc((100vh - 240px) / var(--app-page-zoom, 1));
  overflow-y: auto;
  background: #ffffff;
  
  /* 라이트 모드 스크롤바 스타일 */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
    
    &:hover {
      background: #a8a8a8;
    }
  }
  
  /* Firefox 스크롤바 스타일 */
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
`;

const StudentIncompleteSectionBox = styled.div`
  width: 100%;
  border-radius: 12px;
  padding: 32px 0 0;
  margin: 0 auto;
  border: none;
  background: transparent;
  color: inherit;
  box-sizing: border-box;
  position: relative;
`;

const StudentIncompleteSectionTitleBox = styled.div`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 150px;
  height: 34px;
  border-radius: 17px;
  background: var(--color-surface);
  :root[data-theme='light'] & {
    border: 2px solid var(--color-accent-strong);
    color: var(--color-accent-strong);
  }
  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.85);
    color: rgba(255, 255, 255, 0.9);
  }
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
`;

const StudentIncompleteSectionTitleText = styled.span`
  font-size: 14px;
  font-weight: 900;
  white-space: nowrap;
`;

const StudentIncompleteAssignmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 2px;
  width: 100%;
`;

const StudentIncompleteAssignmentBlock = styled.button`
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  border-radius: 12px;
  padding: 10px 10px;
  margin: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  user-select: none;
  text-align: left;
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
`;

const StudentIncompleteAssignmentHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 8px;
  position: relative;
`;

const StudentIncompleteTypeBadge = styled.span<{ $type: string; $projectColor?: string }>`
  position: absolute;
  left: 0;
  top: 0;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 8px;
  background: ${props => {
    if (props.$type === 'general') return '#6c757d';
    if (props.$type === 'project') return (props.$projectColor && String(props.$projectColor).trim()) ? props.$projectColor : '#FC5555';
    return '#4f46e5';
  }};
  color: #ffffff;
`;

const StudentIncompleteAssignmentTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: var(--color-text-primary);
  text-align: center;
  margin-top: 4px;
`;

const StudentIncompleteAssignmentInfo = styled.div`
  font-size: 14px;
  color: var(--color-text-gray);
  font-weight: 500;
  margin-top: 4px;
  text-align: center;
`;

const ModalDateHeader = styled.div`
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: #212529;
  padding: 0 0 16px 0;
  margin-bottom: 16px;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 40px;
  color: #6c757d;
  font-size: 15px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 60px 40px;
  color: #6c757d;
  font-size: 15px;
`;

const StudentIncompleteRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 12px 16px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  margin-bottom: 8px;
  background: #ffffff;
  page-break-inside: avoid;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StudentInfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 60px;
  flex-shrink: 0;
  padding-top: 4px;
`;

const StudentNumber = styled.span`
  font-weight: 500;
  font-size: 13px;
  color: #6c757d;
`;

const StudentName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #212529;
`;

const IncompleteAssignmentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
`;

const CompactAssignmentBlock = styled.button`
  background: #ffffff;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  text-align: left;
  page-break-inside: avoid;
  flex-shrink: 0;
  width: 160px;
  box-sizing: border-box;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-color: #b0b0b0;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const CompactDueDate = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  background: #f1f3f4;
  color: #5f6368;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  box-sizing: border-box;
`;

const CompactAssignmentName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #212529;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  display: block;
  min-width: 0;
`;

const AssignmentBlockRow1 = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const AssignmentBlockRow2 = styled.div`
  min-width: 0;
`;

const AssignmentTypeBadge = styled.span<{ $type: 'general' | 'project' | 'routine' }>`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  color: white;
  background: ${props =>
    props.$type === 'general'
      ? '#6c757d'
      : props.$type === 'project'
        ? 'var(--color-accent-strong)'
        : '#4f46e5'};
  box-sizing: border-box;
`;

const AssignmentIncompleteRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 12px 16px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  margin-bottom: 8px;
  background: #ffffff;
  page-break-inside: avoid;
  min-width: 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const AssignmentTitleBlock = styled.button`
  background: #ffffff;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  text-align: left;
  flex-shrink: 0;
  width: 160px;
  box-sizing: border-box;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border-color: #b0b0b0;
  }
`;

const IncompleteStudentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  align-items: center;
  min-width: 0;
`;

/* 2행 변환: 화면 가로 415px 이하에서 학생 블럭 1열로 줄어들 때만 적용 */
const IncompleteStudentBlock = styled.div`
  background: #f5f5f5;
  color: #6c757d;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 14px;
  font-weight: 500;
  width: 100px;
  min-width: 80px;
  flex-shrink: 1;
  text-align: center;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  gap: 4px;

  @media (max-width: 415px) {
    flex-direction: column;
    gap: 2px;
  }
`;

const IncompleteStudentNumber = styled.span`
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
`;

const IncompleteStudentName = styled.span`
  min-width: 0;

  @media (min-width: 416px) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-width: 415px) {
    word-break: keep-all;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    width: 100%;
  }
`;